import { EditorView, basicSetup } from 'codemirror';
import { keymap } from '@codemirror/view';
import { foldedRanges, unfoldEffect } from '@codemirror/language';
import { EditorState, type Extension } from '@codemirror/state';
import { markdown, markdownLanguage } from '@codemirror/lang-markdown';
import { htmlLanguage } from '@codemirror/lang-html';
import { languages } from '@codemirror/language-data';
import { indentWithTab } from '@codemirror/commands';
import { oneDark } from '@codemirror/theme-one-dark';

// Leedoc editor extensions (embed navigation + marker autocomplete)
import { embedLinks } from './editor/embed-links.js';
import { collapseFold, autoFoldCollapsedBlocks } from './editor/collapse-fold.js';
import { codeBlockEditGesture, findCodeBlockAtFence } from './editor/code-block-edit.js';
import { codeBlockHighlight, codeBlockLineNumbers } from './editor/codeblock-highlight.js';
import { leedocCompletionSource } from './editor/leedoc-completions.js';
import { ScrollSync } from './scroll-sync/index.js';

import { marked } from 'marked';
// Use the lightweight core build and register only the languages relevant to
// server-install / maintenance docs. The full `highlight.js` default export
// bundles ~190 languages (~1MB) — this curated set is a fraction of that while
// still covering every realistic fenced-code language. Unknown languages fall
// back to auto-detection across the registered set (see SyntaxHighlightEnricher).
import hljs from 'highlight.js/lib/core';
import 'highlight.js/styles/atom-one-dark.css';
import { registerHighlightLanguages } from './highlight-languages.js';

registerHighlightLanguages(hljs);

(window as any).hljs = hljs;

// Storage manager and backends
import { storage } from './storage/index.js';
import { BrowserStorageBackend } from './storage/index.js';
import { CloudStorageBackend } from './storage/index.js';
import { cloudAuth } from './auth/index.js';
import { computeHash, sanitizeFilename } from './storage/utils.js';

// Enricher pipeline
import { PreviewEnricher } from './enrichers/index.js';
import { SyntaxHighlightEnricher } from './enrichers/index.js';
import { LineNumberEnricher } from './enrichers/index.js';
import { CopyButtonEnricher } from './enrichers/index.js';
import { CollapseEnricher } from './enrichers/index.js';
import { InlineCodeEnricher } from './enrichers/index.js';
import { VariableEnricher } from './enrichers/index.js';
import { TocEnricher } from './enrichers/index.js';
import { ChecklistEnricher } from './enrichers/index.js';
import { LinkNavigationEnricher } from './enrichers/index.js';
import { VariableSystem } from './variables/index.js';

// Weave/Tangle - client-side processing for instant preview
import { weave, tangleWithSubstitution, substituteVariables, cleanForExport } from '../shared/index.js';
import type { ScriptTemplateMeta } from '../shared/index.js';

// Interactive script generation
import { generateAllScriptModes } from '../shared/script-converters/index.js';
import type { ScriptMode, ScriptInfo, ScriptModeOption } from '../shared/script-converters/index.js';
import {
  INSTALL_ONELINERS,
  getRecommendedInstallOneliner,
  type InstallOnelinerProvider,
} from '../shared/install-oneliners/index.js';

// Export system
import { ExportService } from './export/index.js';
import type { ExportFormat, ExportOptions } from './export/types.js';

// Template system
import { TemplateManager, type Template } from './templates/index.js';

// Tab system
import { TabManager, TabBar } from './tabs/index.js';
import type { TabFileState } from './tabs/index.js';
import { DesktopManager, type PanelState } from './windows/index.js';

// Checklist sync (script-heading mapping)
import { buildScriptHeadingMapping, ChecklistSync } from './checklist/index.js';
import type { ScriptHeadingMapping } from './checklist/index.js';

// TOC sidebar (scroll-spy navigation)
import { TocSidebar } from './toc-sidebar/index.js';

// Unified in-app search (Ctrl/Cmd+F) across source editor and preview
import { PaneSearch, ProjectSearch } from './search/index.js';
import type { ProjectSearchMatch } from './search/index.js';

// File tree
import {
  getNewPath,
  TreeRenderer,
} from './filetree/index.js';
import type { TreeNode } from './filetree/index.js';

// Services
import { modalManager, MODAL_IDS } from './services/index.js';
import { renderProjectList, saveLastProject, getLastProject } from './services/index.js';
import { unsavedBackupKey, writeUnsavedBackup, readUnsavedBackup, clearUnsavedBackup } from './services/index.js';

// Types
interface FileData {
  path: string;
  name: string;
  dir: string;
}

interface ComponentData {
  name: string;  // Full name including path, e.g., "utils/docker-install.md"
  path: string;  // Same as name for components (the path within _components/)
}

interface Variables {
  static?: Record<string, string>;
  dynamic?: Record<string, string>;
}

interface FileResponse {
  content: string;
  variables: Variables;
}

interface ComponentResponse {
  content: string;
  path: string;
}

interface SaveResponse {
  success: boolean;
  hash?: string;
}

interface FilesResponse {
  files?: FileData[];
  components?: ComponentData[];
}

interface DeleteTarget {
  type: 'file' | 'component';
  path: string;
  name?: string;
}

interface RenameTarget {
  type: 'file' | 'component';
  path: string;
  originalName?: string;
  category?: string;
}

interface Snapshot {
  id: string;
  timestamp: number;
  reason: string;
}

type SaveState = 'hidden' | 'synced' | 'modified' | 'saving' | 'error' | 'out-of-sync';
type AuthMode = 'login' | 'register';

// Loading overlay
const appLoading = document.getElementById('app-loading') as HTMLDivElement;
const URL_PROJECT_PARAM = 'project';

// Project-root README acts as the home page — always present as a pinned tab.
const README_PATH = 'README.src.md';
const README_STUB = '# Welcome\n\n';

// State
let currentFile: string | null = null;
let tangledScripts: Record<string, string> = {};
let scriptTemplateMeta: ScriptTemplateMeta[] = [];
let scriptInfoMap: Map<string, ScriptInfo> = new Map();
let scriptMode: ScriptMode = 'standard';
let sourceContent = '';
let wovenContent = '';
let debounceTimer: number | null = null;
let saveDebounceTimer: number | null = null;
let variableDetectTimer: number | null = null;
let activeScriptTab: string | null = null;
let editMode = false;
// When true, the Documentation panel renders the woven markdown source instead
// of the rendered preview. Per-view (ephemeral) — resets on every file/tab switch
// so a different tab always starts in normal preview mode.
let markdownSourceView = false;
let hasUnsavedChanges = false;
let lastSaveTime: number | null = null;
let lastSavedHash: string | null = null;
let saveState: SaveState = 'hidden';
let editorView: EditorView | null = null;
// Set when typing changes the document; after the next preview render the
// scroll sync realigns the preview to the cursor line (see processFile)
let pendingCursorAlign = false;

const DEFAULT_SCRIPT_MODE_OPTIONS: ScriptModeOption[] = [
  { value: 'standard', label: 'Standard' },
];

// Variable system (centralized state for variables and block overrides)
const variableSystem = new VariableSystem();

// Checklist state
let checklistEnabled = false;
let checklistSlugs: string[] = [];
let checklistSaveTimer: number | null = null;
let scriptHeadingMapping: ScriptHeadingMapping | null = null;

// Checklist sync (bidirectional script-heading sync)
const checklistSync = new ChecklistSync({
  getCheckedSlugs: () => checklistSlugs,
  setCheckedSlugs: (slugs) => {
    checklistSlugs = slugs;
    tocSidebar.syncChecklist(checklistSlugs);
    debounceSaveChecklist();
    updatePageTitle();
  },
  onScriptStateChange: () => updateScriptTabsUI(),
});

// Legacy accessors for backward compatibility with buildVariablesForm etc.
let staticVariables: Record<string, string> = {};
let dynamicVariables: Record<string, string> = {};
let userVariables: Record<string, string> = {};

// Tab system
const tabManager = new TabManager();
let tabBar: TabBar | null = null;
let isRestoringTabState = false; // Guard: ignore editor dispatches during tab restore
// Experimental windowed mode (every tab becomes a floating window; see src/client/windows/)
let desktop: DesktopManager | null = null;

// Preview enricher pipeline
const previewEnricher = new PreviewEnricher();
previewEnricher.register(new SyntaxHighlightEnricher({ priority: 100 }));
previewEnricher.register(new LineNumberEnricher({ priority: 98 }));
previewEnricher.register(new TocEnricher({ priority: 95 }));
previewEnricher.register(new VariableEnricher({ priority: 90, variableSystem }));
previewEnricher.register(new CollapseEnricher({ priority: 85 }));
previewEnricher.register(new CopyButtonEnricher({ priority: 80 }));
previewEnricher.register(new InlineCodeEnricher({ priority: 70 }));

// Inter-page link navigation (runs after TocEnricher so in-page anchors stay separate)
previewEnricher.register(new LinkNavigationEnricher({
  priority: 92,
  getCurrentFile: () => currentFile,
  getAllPaths: () => currentFilesData
    .map(f => f.path)
    .filter(p => p.endsWith('.src.md') && !p.startsWith('_components/')),
  onNavigate: (path, { newTab, anchor }) => {
    void handlePageLinkNavigation(path, { newTab, anchor });
  },
  onBrokenLink: (href) => {
    openCreatePageForBrokenLink(href);
  },
}));

// Checklist enricher (lowest priority - runs last)
const checklistEnricher = new ChecklistEnricher({
  priority: 10,
  getCheckedSlugs: () => checklistSlugs,
  onToggle: (slug: string, checked: boolean) => {
    if (checked) {
      if (!checklistSlugs.includes(slug)) checklistSlugs.push(slug);
    } else {
      checklistSlugs = checklistSlugs.filter(s => s !== slug);
    }
    // Notify checklistSync so it can update script checkbox states
    checklistSync.onHeadingToggle();
    // Sync TOC sidebar checked state
    tocSidebar.syncChecklist(checklistSlugs);
    debounceSaveChecklist();
    updatePageTitle();
  },
});
previewEnricher.register(checklistEnricher);

// Export service
const exportService = new ExportService({
  getAuthToken: () => cloudAuth.getToken(),
});

// Template manager
const templateManager = new TemplateManager({
  getAuthToken: () => cloudAuth.getToken(),
  onTemplatesChanged: () => {
    renderTemplateList();
    updateDocxExportTemplateSelect();
  },
});

// Tree renderers (initialized in init)
let fileTreeRenderer: TreeRenderer | null = null;
let componentTreeRenderer: TreeRenderer | null = null;

// Data for re-rendering trees
let currentFilesData: FileData[] = [];
let currentComponentsData: ComponentData[] = [];

// DOM Elements
const variablesPanel = document.getElementById('variables-panel') as HTMLDivElement;
const variablesForm = document.getElementById('variables-form') as HTMLFormElement;
const markdownOutput = document.getElementById('markdown-output') as HTMLDivElement;
const scriptsOutput = document.getElementById('scripts-output') as HTMLTextAreaElement;
const scriptsTabs = document.getElementById('scripts-tabs') as HTMLDivElement;
const statusEl = document.getElementById('status') as HTMLDivElement;
const btnToggleEdit = document.getElementById('btn-toggle-edit') as HTMLButtonElement;
const saveStatusBtn = document.getElementById('btn-save-status') as HTMLButtonElement;
const editorPanel = document.getElementById('editor-panel') as HTMLDivElement;
const editorResize = document.getElementById('editor-resize') as HTMLDivElement;
const btnCopy = document.getElementById('btn-copy') as HTMLButtonElement;
const btnCopyInstall = document.getElementById('btn-copy-install') as HTMLButtonElement;
const btnInstallMenuToggle = document.getElementById('btn-install-menu-toggle') as HTMLButtonElement;
const installMenu = document.getElementById('install-menu') as HTMLDivElement;
const btnScriptVars = document.getElementById('script-vars-btn') as HTMLButtonElement;
const scriptModeSelect = document.getElementById('script-mode') as HTMLSelectElement;
const codemirrorContainer = document.getElementById('codemirror-container') as HTMLDivElement;
const toggleVariables = document.getElementById('toggle-variables') as HTMLInputElement;
const btnExportDocx = document.getElementById('btn-export-docx') as HTMLButtonElement;
const btnChecklist = document.getElementById('btn-checklist') as HTMLButtonElement;
const btnTocSidebar = document.getElementById('btn-toc-sidebar') as HTMLButtonElement;
const btnViewMarkdown = document.getElementById('btn-view-markdown') as HTMLButtonElement;
const toggleDocs = document.getElementById('toggle-docs') as HTMLInputElement;
const toggleScripts = document.getElementById('toggle-scripts') as HTMLInputElement;
const markdownPanel = document.querySelector('.markdown-panel') as HTMLDivElement;
const scriptsPanel = document.querySelector('.scripts-panel') as HTMLDivElement;
const panelsResizeHandle = document.querySelector('.resize-handle[data-resize="panels"]') as HTMLDivElement;
const btnRefresh = document.getElementById('btn-refresh') as HTMLButtonElement;
const btnProjectSearch = document.getElementById('btn-project-search') as HTMLButtonElement;
const btnComponentUsages = document.getElementById('btn-component-usages') as HTMLButtonElement;
const saveErrorMessage = document.getElementById('save-error-message') as HTMLParagraphElement;
const saveErrorFile = document.getElementById('save-error-file') as HTMLElement;
const saveErrorDetail = document.getElementById('save-error-detail') as HTMLElement;
const saveErrorBackupNote = document.getElementById('save-error-backup-note') as HTMLParagraphElement;
const btnSaveErrorCopy = document.getElementById('btn-save-error-copy') as HTMLButtonElement;
const btnSaveErrorDownload = document.getElementById('btn-save-error-download') as HTMLButtonElement;
const btnSaveErrorRetry = document.getElementById('btn-save-error-retry') as HTMLButtonElement;
const btnSaveErrorReload = document.getElementById('btn-save-error-reload') as HTMLButtonElement;

// Unified in-app search (Ctrl/Cmd+F). Replaces the browser's native find,
// which can't see CodeMirror's virtualized document or scope to a panel.
const paneSearch = new PaneSearch({
  getEditorView: () => editorView,
  sourcePanel: editorPanel,
  previewPanel: markdownPanel,
  previewContainer: markdownOutput,
});

// Bidirectional editor <-> preview scroll sync. The anchor map is rebuilt
// after every preview render (see processFile) and invalidated on file
// switches so stale anchors never sync against the wrong document.
const scrollSync = new ScrollSync({
  preview: markdownOutput,
  getEditorView: () => editorView,
  isEnabled: () => editMode && !markdownSourceView,
  resolveVariables: (text) => substituteVariables(text, userVariables).result,
});

// CRUD modal elements
const btnNewFile = document.getElementById('btn-new-file') as HTMLButtonElement;
const btnNewComponent = document.getElementById('btn-new-component') as HTMLButtonElement;
const createFileModal = document.getElementById('create-file-modal') as HTMLDivElement;
const createComponentModal = document.getElementById('create-component-modal') as HTMLDivElement;
const deleteModal = document.getElementById('delete-modal') as HTMLDivElement;
const newFileCategory = document.getElementById('new-file-category') as HTMLSelectElement;
const newCategoryGroup = document.getElementById('new-category-group') as HTMLDivElement;
const newCategoryName = document.getElementById('new-category-name') as HTMLInputElement;
const newFileName = document.getElementById('new-file-name') as HTMLInputElement;
const newFilePreview = document.getElementById('new-file-preview') as HTMLDivElement;
const btnCreateFile = document.getElementById('btn-create-file') as HTMLButtonElement;
const newComponentName = document.getElementById('new-component-name') as HTMLInputElement;
const newComponentPreview = document.getElementById('new-component-preview') as HTMLDivElement;
const btnCreateComponent = document.getElementById('btn-create-component') as HTMLButtonElement;
const deleteTargetPath = document.getElementById('delete-target-path') as HTMLSpanElement;
const btnConfirmDelete = document.getElementById('btn-confirm-delete') as HTMLButtonElement;

// Help modal elements
const helpModal = document.getElementById('help-modal') as HTMLDivElement;
const btnCloseHelp = document.getElementById('btn-close-help') as HTMLButtonElement;

// Loading modal elements (reusable loading indicator)
const loadingModal = document.getElementById('loading-modal') as HTMLDivElement | null;
const loadingModalMessage = document.getElementById('loading-modal-message') as HTMLSpanElement | null;

// History modal elements
const historyModal = document.getElementById('history-modal') as HTMLDivElement;
const historyList = document.getElementById('history-list') as HTMLDivElement;
const btnCreateSnapshot = document.getElementById('btn-create-snapshot') as HTMLButtonElement;
const btnRestoreSnapshot = document.getElementById('btn-restore-snapshot') as HTMLButtonElement;
let selectedSnapshotId: string | null = null;

// Media library modal elements
const mediaModal = document.getElementById('media-modal') as HTMLDivElement;
const mediaGrid = document.getElementById('media-grid') as HTMLDivElement;
const mediaCount = document.getElementById('media-count') as HTMLSpanElement;
const mediaSize = document.getElementById('media-size') as HTMLSpanElement;
const btnMediaUpload = document.getElementById('btn-media-upload') as HTMLButtonElement;
const btnMediaDeleteUnused = document.getElementById('btn-media-delete-unused') as HTMLButtonElement;
const btnMediaInsert = document.getElementById('btn-media-insert') as HTMLButtonElement;
const mediaUploadInput = document.getElementById('media-upload-input') as HTMLInputElement;
let selectedMediaFilename: string | null = null;

// Template modal elements
const templateModal = document.getElementById('template-modal') as HTMLDivElement;
const templateList = document.getElementById('template-list') as HTMLDivElement;
const btnTemplateUpload = document.getElementById('btn-template-upload') as HTMLButtonElement;
const btnTemplateDownloadDefault = document.getElementById('btn-template-download-default') as HTMLButtonElement;
const templateUploadInput = document.getElementById('template-upload-input') as HTMLInputElement;
const btnMenuTemplates = document.getElementById('btn-menu-templates') as HTMLButtonElement | null;

// DOCX export modal elements
const docxExportModal = document.getElementById('docx-export-modal') as HTMLDivElement;
const docxExportTitle = document.getElementById('docx-export-title') as HTMLInputElement;
const docxExportTemplate = document.getElementById('docx-export-template') as HTMLSelectElement;
const docxExportToc = document.getElementById('docx-export-toc') as HTMLInputElement;
const docxExportFrontpage = document.getElementById('docx-export-frontpage') as HTMLInputElement;
const frontpageOptions = document.getElementById('frontpage-options') as HTMLDivElement;
const docxExportSubtitle = document.getElementById('docx-export-subtitle') as HTMLInputElement;
const docxExportCompany = document.getElementById('docx-export-company') as HTMLInputElement;
const btnDocxExportConfirm = document.getElementById('btn-docx-export-confirm') as HTMLButtonElement;
const btnManageTemplates = document.getElementById('btn-manage-templates') as HTMLButtonElement;

// CRUD state
let deleteTarget: DeleteTarget | null = null;
let renameTarget: RenameTarget | null = null;

// Rename modal elements
const renameModal = document.getElementById('rename-modal') as HTMLDivElement;
const renameModalTitle = document.getElementById('rename-modal-title') as HTMLHeadingElement;
const renameInput = document.getElementById('rename-input') as HTMLInputElement;
const renameHint = document.getElementById('rename-hint') as HTMLParagraphElement;
const renamePreview = document.getElementById('rename-preview') as HTMLDivElement;
const btnConfirmRename = document.getElementById('btn-confirm-rename') as HTMLButtonElement;

// Storage-related DOM elements
const importFileInput = document.getElementById('import-file-input') as HTMLInputElement | null;
// Topbar project pill + project switcher modal elements
const topbarProjectPill = document.getElementById('topbar-project-pill') as HTMLButtonElement | null;
const topbarProjectName = document.getElementById('topbar-project-name') as HTMLSpanElement | null;
const topbarStorageMode = document.getElementById('topbar-storage-mode') as HTMLSpanElement | null;
const psSearchInput = document.getElementById('ps-search-input') as HTMLInputElement | null;
const psProjectList = document.getElementById('ps-project-list') as HTMLDivElement | null;
const psFileList = document.getElementById('ps-file-list') as HTMLDivElement | null;
const psComponentList = document.getElementById('ps-component-list') as HTMLDivElement | null;
const psFileTree = document.getElementById('ps-file-tree') as HTMLDivElement | null;
const psComponentTree = document.getElementById('ps-component-tree') as HTMLDivElement | null;
const psViewToggle = document.getElementById('ps-view-toggle') as HTMLDivElement | null;
const psViewButtons = document.querySelectorAll<HTMLButtonElement>('.ps-view-btn');
const btnPsNewFile = document.getElementById('btn-ps-new-file') as HTMLButtonElement | null;
const btnPsNewFolder = document.getElementById('btn-ps-new-folder') as HTMLButtonElement | null;
const btnPsNewComponent = document.getElementById('btn-ps-new-component') as HTMLButtonElement | null;
const btnPsNewComponentFolder = document.getElementById('btn-ps-new-component-folder') as HTMLButtonElement | null;
const psEmpty = document.getElementById('ps-empty') as HTMLDivElement | null;
const psEmptyQuery = document.getElementById('ps-empty-query') as HTMLSpanElement | null;
const psEmptyFiles = document.getElementById('ps-empty-files') as HTMLDivElement | null;
const psEmptyFilesQuery = document.getElementById('ps-empty-files-query') as HTMLSpanElement | null;
const psEmptyComponents = document.getElementById('ps-empty-components') as HTMLDivElement | null;
const psEmptyComponentsQuery = document.getElementById('ps-empty-components-query') as HTMLSpanElement | null;
const psFooterProjects = document.getElementById('ps-footer-projects') as HTMLDivElement | null;
const psFooterFiles = document.getElementById('ps-footer-files') as HTMLDivElement | null;
const psFooterComponents = document.getElementById('ps-footer-components') as HTMLDivElement | null;
const psTabs = document.querySelectorAll<HTMLButtonElement>('.ps-tab');
const btnPsCreateFromQuery = document.getElementById('btn-ps-create-from-query') as HTMLButtonElement | null;
const btnPsNewProject = document.getElementById('btn-ps-new-project') as HTMLButtonElement | null;
const btnPsImportProject = document.getElementById('btn-ps-import-project') as HTMLButtonElement | null;
const btnPsExportProject = document.getElementById('btn-ps-export-project') as HTMLButtonElement | null;
const appBrandTrigger = document.getElementById('app-brand-trigger') as HTMLDivElement | null;
const topbarEl = document.querySelector('.topbar') as HTMLDivElement | null;
// User menu elements (avatar dropdown)
const userMenu = document.getElementById('user-menu') as HTMLDivElement | null;
const btnUserAvatar = document.getElementById('btn-user-avatar') as HTMLButtonElement | null;
const userAvatarInitials = document.getElementById('user-avatar-initials') as HTMLSpanElement | null;
const userMenuDropdown = document.getElementById('user-menu-dropdown') as HTMLDivElement | null;
const userMenuEmail = document.getElementById('user-menu-email') as HTMLDivElement | null;
const btnMenuHistory = document.getElementById('btn-menu-history') as HTMLButtonElement | null;
const btnMenuMedia = document.getElementById('btn-menu-media') as HTMLButtonElement | null;
const btnMenuHelp = document.getElementById('btn-menu-help') as HTMLButtonElement | null;
const btnMenuWindowed = document.getElementById('btn-menu-windowed') as HTMLButtonElement | null;
const windowedModeState = document.getElementById('windowed-mode-state') as HTMLSpanElement | null;
const workspaceEl = document.getElementById('workspace') as HTMLDivElement;
const contentContainerEl = document.querySelector('.content-container') as HTMLDivElement;
const btnMenuLogout = document.getElementById('btn-menu-logout') as HTMLButtonElement | null;
const btnMenuLogin = document.getElementById('btn-menu-login') as HTMLButtonElement | null;
const authModal = document.getElementById('auth-modal') as HTMLDivElement | null;
const authModalTitle = document.getElementById('auth-modal-title') as HTMLHeadingElement | null;
const authModalSubtitle = document.getElementById('auth-modal-subtitle') as HTMLParagraphElement | null;
const authForm = document.getElementById('auth-form') as HTMLFormElement | null;
const authEmail = document.getElementById('auth-email') as HTMLInputElement | null;
const authPassword = document.getElementById('auth-password') as HTMLInputElement | null;
const authError = document.getElementById('auth-error') as HTMLDivElement | null;
const authSubmitBtn = document.getElementById('auth-submit-btn') as HTMLButtonElement | null;
const authBtnText = authSubmitBtn?.querySelector('.auth-btn-text') as HTMLSpanElement | null;
const authSwitchBtn = document.getElementById('auth-switch-btn') as HTMLButtonElement | null;
const authSwitchText = document.getElementById('auth-switch-text') as HTMLSpanElement | null;
const authSkipBtn = document.getElementById('auth-skip-btn') as HTMLButtonElement | null;


// TOC sidebar (scroll-spy navigation)
const tocSidebar = new TocSidebar({
  scrollContainer: markdownOutput,
  panel: markdownPanel,
});
if (localStorage.getItem('leedoc_toc_sidebar') === 'true') {
  tocSidebar.show();
  btnTocSidebar.classList.add('active');
}

// Initialize storage backends
let browserBackend: BrowserStorageBackend | null = null;
let cloudBackend: CloudStorageBackend | null = null;
let authMode: AuthMode = 'login'; // 'login' or 'register'
let allowRegistration: boolean = true;

// Initialize storage backends
async function initStorageBackends(): Promise<void> {
  // Initialize browser storage backend
  browserBackend = new BrowserStorageBackend();
  await browserBackend.init();
  storage.registerBackend('browser', browserBackend);

  // Initialize cloud storage backend
  cloudBackend = new CloudStorageBackend();
  storage.registerBackend('cloud', cloudBackend);

  // If we have a cloud token, set it up
  if (cloudAuth.isAuthenticated()) {
    const token = cloudAuth.getToken();
    if (token) {
      cloudBackend.setToken(token);
    }
  }

  // Listen for auth changes
  cloudAuth.onAuthChange(async (event, data) => {
    if (event === 'login') {
      if (!data.token) return;
      cloudBackend!.setToken(data.token);
      await switchStorageMode('cloud');
      closeAuthModal();
    } else if (event === 'logout') {
      cloudBackend!.logout();
      updateCloudUI();
      // Switch back to browser mode if we were on cloud
      if (storage.currentBackendName === 'cloud') {
        await switchStorageMode('browser');
      }
    }
  });
}

function getUrlProjectId(): string | null {
  const value = new URLSearchParams(window.location.search).get(URL_PROJECT_PARAM);
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

function setUrlProjectId(projectId: string | number | null | undefined): void {
  const url = new URL(window.location.href);
  const value = projectId === null || projectId === undefined ? '' : String(projectId).trim();
  if (value) {
    url.searchParams.set(URL_PROJECT_PARAM, value);
  } else {
    url.searchParams.delete(URL_PROJECT_PARAM);
  }
  window.history.replaceState({}, '', url.toString());
}

async function syncUrlProjectId(mode: 'cloud' | 'browser' = storage.currentBackendName): Promise<void> {
  if (mode === 'cloud') {
    const currentProject = cloudBackend?.getCurrentProject();
    setUrlProjectId(currentProject?.id ?? null);
    return;
  }

  const currentProject = await browserBackend?.getCurrentProject();
  setUrlProjectId(currentProject?.id ?? null);
}

// Initialize tree renderers for files and components — both mount inside the
// project switcher modal (the unified file browser).
function initTreeRenderers(): void {
  if (!psFileTree || !psComponentTree) return;

  // File tree renderer
  fileTreeRenderer = new TreeRenderer({
    container: psFileTree,
    fileExtension: '.src.md',
    treeType: 'file',
    onSelect: async (path) => {
      modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);
      await loadFile(path);
      fileTreeRenderer?.refreshSelection();
    },
    onDrop: handleFileDrop,
    onContextAction: handleContextMenuAction,
    onInlineCreate: handleFileInlineCreate,
    onInlineRename: handleFileInlineRename,
    getCurrentFile: () => currentFile,
  });

  // Component tree renderer
  componentTreeRenderer = new TreeRenderer({
    container: psComponentTree,
    fileExtension: '.md',
    treeType: 'component',
    onSelect: async (path) => {
      modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);
      await loadComponent(path);
      componentTreeRenderer?.refreshSelection();
    },
    onDrop: handleComponentDrop,
    onContextAction: handleComponentContextMenuAction,
    onInlineCreate: handleComponentInlineCreate,
    onInlineRename: handleComponentInlineRename,
    getCurrentFile: () => currentFile,
    pathPrefix: '_components/',
  });
}

// Initialize modals with ModalManager
function initModals(): void {
  // Register all modals with their configurations
  modalManager.register({ id: MODAL_IDS.CREATE_FILE });
  modalManager.register({ id: MODAL_IDS.CREATE_COMPONENT });
  modalManager.register({ id: MODAL_IDS.DELETE, onHide: () => { deleteTarget = null; } });
  modalManager.register({ id: MODAL_IDS.RENAME, onHide: () => { renameTarget = null; } });
  modalManager.register({ id: MODAL_IDS.HISTORY, onHide: () => { selectedSnapshotId = null; } });
  modalManager.register({ id: MODAL_IDS.MEDIA, onHide: () => { selectedMediaFilename = null; } });
  modalManager.register({ id: MODAL_IDS.TEMPLATE });
  modalManager.register({ id: MODAL_IDS.DOCX_EXPORT });
  modalManager.register({ id: MODAL_IDS.HELP });
  modalManager.register({ id: MODAL_IDS.PROJECT_SWITCHER });
  modalManager.register({ id: MODAL_IDS.PROJECT_SEARCH });
  modalManager.register({
    id: MODAL_IDS.SAVE_ERROR,
    closeOnBackdrop: false, // an explicit choice is required
    onHide: () => { saveErrorDismissedAt = Date.now(); },
  });
  modalManager.register({ id: MODAL_IDS.AUTH, closeOnBackdrop: false }); // Auth modal shouldn't close on backdrop
  modalManager.register({ id: MODAL_IDS.LOADING, closeOnBackdrop: false }); // Loading modal shouldn't close on backdrop
}

// Initialize
async function init(): Promise<void> {
  try {
    await initStorageBackends();
    initTreeRenderers();
    initModals();
    setupEventListeners();
    setupStorageEventListeners();
    setupProjectSwitcherTreeListeners();

    // Load public auth config (e.g. whether registration is enabled)
    cloudAuth.fetchConfig().then(cfg => {
      allowRegistration = cfg.allowRegistration;
      applyAuthConfig();
    }).catch(() => { /* keep default (allow) */ });

    // Initialize tab system
    const tabBarContainer = document.createElement('div');
    tabBarContainer.className = 'tab-bar';
    const mainEl = document.querySelector('.main')!;
    const topbar = mainEl.querySelector('.topbar') as HTMLDivElement | null;
    if (topbar) {
      topbar.after(tabBarContainer);
    } else {
      mainEl.prepend(tabBarContainer);
    }

    tabBar = new TabBar(tabBarContainer, tabManager);
    tabBar.onTabClick = async (path: string) => {
      if (path !== currentFile) flushPendingSave();
      // Snapshot current state before switching (while _activeTab still points to old file)
      const snapshot = snapshotCurrentFileState();
      if (snapshot) tabManager.updateActiveState(snapshot);

      // Peek at the target tab without changing _activeTab yet
      const tabState = tabManager.getTab(path);
      if (tabState && tabState.sourceContent) {
        // Tab has real content - activate and restore from cache
        tabManager.activateTab(path);
        restoreFileState(tabState);
        tabBar!.render();
        refreshTreeSelection();
      } else {
        // Placeholder tab (e.g. after page reload) - load from server
        // loadFile/loadComponent will handle activation and tab registration
        if (path.startsWith('_components/')) {
          await loadComponent(path);
        } else {
          await loadFile(path);
        }
        tabBar!.render();
        refreshTreeSelection();
      }
      persistTabs();
    };
    tabBar.onTabClose = async (path: string) => {
      await handleTabClose(path);
    };
    tabBar.onTabCloseAllExcept = async (keepPath: string) => {
      await handleTabCloseAllExcept(keepPath);
    };
    tabBar.onOpenFileChooser = () => {
      void openProjectSwitcher('files');
    };
    tabBar.getDisplayNameOverride = (tab) => {
      if (tab.path === README_PATH) {
        return topbarProjectName?.textContent?.trim() || 'README';
      }
      return null;
    };
    tabBar.updateVisibility();

    // Experimental windowed mode: the tab bar doubles as its taskbar
    desktop = new DesktopManager({
      workspace: workspaceEl,
      preview: markdownOutput,
      tabManager,
      getCurrentFile: () => currentFile,
      activate: (path) => tabBar!.onTabClick!(path),
      close: (path) => handleTabClose(path),
      getDisplayName: (path) => tabBar!.displayNameForPath(path),
      getPanelState,
      applyPanelState,
      setTaskbarActions: (el) => {
        tabBar!.extraActions = el;
        tabBar!.render();
      },
      onModeChange: (enabled) => {
        if (windowedModeState) windowedModeState.textContent = enabled ? 'On' : 'Off';
        btnMenuWindowed?.classList.toggle('active', enabled);
        tabBar?.render();
      },
      renderSnapshot: (path) => renderDetachedPreview(path),
    });
    tabBar.isTabMinimized = (path) => desktop?.isMinimized(path) ?? false;
    tabBar.onActiveTabClick = () => desktop?.focusActive();
    tabBar.onRender = () => desktop?.refreshChrome();

    setTopbarVisible(localStorage.getItem('leedoc_topbar_hidden') !== 'true');

    // Set initial UI state for user menu (before auth check)
    await updateCloudUI();

    // Capture file and heading hash from URL BEFORE switchStorageMode (which calls clearEditor and clears URL)
    const params = new URLSearchParams(window.location.search);
    const fileParam = params.get('file');
    const initialHash = window.location.hash.slice(1); // capture heading to scroll to after load

    // Storage mode is strictly tied to auth state:
    // - Logged in = cloud mode
    // - Not logged in = browser mode (or show auth modal for first-time users)
    if (cloudAuth.isAuthenticated()) {
      // User is logged in - always use cloud mode
      await switchStorageMode('cloud');
    } else {
      // Not logged in - show auth modal to let user login or skip to browser mode
      showAuthModal();
    }

    // Restore persisted tabs
    try {
      const raw = localStorage.getItem('leedoc_tabs');
      if (raw) {
        const data = JSON.parse(raw);
        if (data.tabs?.length > 0) {
          tabManager.restoreFromPaths(data.tabs, data.active);
          tabBar?.render();
        }
      }
    } catch {
      // ignore invalid stored data
    }

    // Set up onChange AFTER tab restoration to avoid persisting incomplete state during init
    tabManager.onChange = () => {
      desktop?.sync();
      tabBar?.render();
      persistTabs();
    };

    // Re-enter windowed mode if it was on last time (tabs are placeholders
    // at this point; windows fill in as files load)
    if (DesktopManager.isPersistedOn()) desktop?.enable();

    // Load file from URL if present (after storage is initialized)
    if (fileParam) {
      if (fileParam.startsWith('_components/')) {
        const componentName = fileParam.replace('_components/', '');
        await loadComponent(componentName);
      } else {
        await loadFile(fileParam);
      }

      // Scroll to heading from URL hash after content is rendered
      if (initialHash) {
        requestAnimationFrame(() => {
          document.getElementById(initialHash)?.scrollIntoView({ block: 'start' });
        });
      }
    } else if (tabManager.activeTab) {
      // No URL file, but we have persisted tabs - load the active one
      const activePath = tabManager.activeTab;
      if (activePath.startsWith('_components/')) {
        await loadComponent(activePath);
      } else {
        await loadFile(activePath);
      }
    } else if (modalManager.isOpen(MODAL_IDS.AUTH)) {
      // Auth modal is showing — don't open another modal behind it. The user
      // will pick a flow (login or skip), which triggers the right file load.
    } else if (currentFilesData.some(f => f.path === README_PATH)) {
      // README always exists after ensureReadmePinned; load it as the home page.
      await loadFile(README_PATH);
    } else {
      // Storage isn't ready (e.g. cloud login pending) — fall back to the switcher.
      void openProjectSwitcher('files');
    }
  } finally {
    // Hide loading overlay
    appLoading?.classList.add('hidden');
  }
}

/**
 * Ensure a `README.src.md` file exists at the project root and is shown as a
 * pinned (non-closable) tab. Called after every file-list refresh so project
 * switches / imports / fresh installs always have a home page.
 */
async function ensureReadmePinned(): Promise<void> {
  const hasReadme = currentFilesData.some(f => f.path === README_PATH);
  if (!hasReadme) {
    try {
      await storage.createFile(README_PATH, README_STUB);
      // Keep currentFilesData in sync without a recursive list call
      currentFilesData.push({ path: README_PATH, name: 'README', dir: '' });
      fileTreeRenderer?.render(currentFilesData);
    } catch (err) {
      console.warn('Failed to auto-create README:', err);
      return;
    }
  }

  if (tabManager.isOpen(README_PATH)) {
    tabManager.pinPath(README_PATH);
  } else {
    tabManager.addPinnedPlaceholder(README_PATH);
  }
  tabBar?.render();
}

// Load both files and components (single API call for efficiency)
async function loadFilesAndComponents(): Promise<void> {
  try {
    const data = await storage.listFiles() as FilesResponse;
    currentFilesData = data.files || [];
    currentComponentsData = data.components || [];
    fileTreeRenderer?.render(currentFilesData);
    componentTreeRenderer?.render(currentComponentsData.map(c => ({ path: c.name })));
    refreshFlatListIfVisible();
    await ensureReadmePinned();
  } catch (err) {
    const error = err as Error;
    showTreeLoadError('file', error.message);
  }
}

// Load source files only (for refresh after create/delete)
async function loadFiles(): Promise<void> {
  try {
    const data = await storage.listFiles() as FilesResponse;
    currentFilesData = data.files || [];
    fileTreeRenderer?.render(currentFilesData);
    refreshFlatListIfVisible();
  } catch (err) {
    const error = err as Error;
    showTreeLoadError('file', error.message);
  }
}

// Load components only (for refresh after create/delete)
async function loadComponents(): Promise<void> {
  try {
    const data = await storage.listFiles() as FilesResponse;
    currentComponentsData = data.components || [];
    componentTreeRenderer?.render(currentComponentsData.map(c => ({ path: c.name })));
    refreshFlatListIfVisible();
  } catch (err) {
    const error = err as Error;
    showTreeLoadError('component', error.message);
  }
}

function showTreeLoadError(kind: 'file' | 'component', message: string): void {
  const target = kind === 'file' ? psFileTree : psComponentTree;
  if (target) {
    target.innerHTML = `<div class="tree-file" style="color: var(--text-muted)">Error: ${message}</div>`;
  }
}

// Load all components content (for non-local backends to send to weave)
async function loadAllComponents(): Promise<Record<string, string>> {
  try {
    const data = await storage.listFiles() as FilesResponse;
    const components: Record<string, string> = {};

    for (const c of data.components || []) {
      try {
        const componentData = await storage.readComponent(c.name) as ComponentResponse;
        components[c.name] = componentData.content;
      } catch (err) {
        console.warn(`Failed to load component ${c.name}:`, err);
      }
    }

    return components;
  } catch (err) {
    console.error('Failed to load components:', err);
    return {};
  }
}

// --- File Tree Handler Functions ---

// Handle inline creation for files (callback for TreeRenderer)
async function handleFileInlineCreate(name: string, type: 'file' | 'folder', parentPath: string): Promise<void> {
  const sanitizedName = sanitizeFilename(name);
  if (!sanitizedName) return;

  try {
    if (type === 'file') {
      const path = parentPath
        ? `${parentPath}/${sanitizedName}.src.md`
        : `${sanitizedName}.src.md`;

      await storage.createFile(path);
      await loadFiles();
      await loadFile(path);
    } else {
      // Creating a folder - add to pending folders
      const folderPath = parentPath
        ? `${parentPath}/${sanitizedName}`
        : sanitizedName;

      fileTreeRenderer?.pending.add(folderPath);
      fileTreeRenderer?.expanded.add(folderPath);
      fileTreeRenderer?.render(currentFilesData);
    }
  } catch (err) {
    console.error('Failed to create:', err);
    alert(`Failed to create: ${(err as Error).message}`);
  }
}

// Handle inline rename for files (callback for TreeRenderer)
async function handleFileInlineRename(node: TreeNode, newName: string): Promise<void> {
  const sanitizedName = sanitizeFilename(newName);
  if (!sanitizedName || sanitizedName === node.name) return;

  try {
    if (node.type === 'file') {
      const parts = node.path.split('/');
      parts.pop();
      const newPath = parts.length > 0
        ? `${parts.join('/')}/${sanitizedName}.src.md`
        : `${sanitizedName}.src.md`;

      await storage.renameFile(node.path, newPath);
      tabManager.renameTab(node.path, newPath);

      if (currentFile === node.path) {
        currentFile = newPath;
        updateUrlWithFile(newPath);
      }
    } else {
      // Rename folder = rename all files inside
      const filesToRename = fileTreeRenderer?.getFilesUnderFolder(node.path) || [];
      if (filesToRename.length === 0) return;

      const parts = node.path.split('/');
      parts.pop();
      const newFolderPath = parts.length > 0
        ? `${parts.join('/')}/${sanitizedName}`
        : sanitizedName;

      for (const filePath of filesToRename) {
        const relativePath = filePath.substring(node.path.length + 1);
        const newFilePath = `${newFolderPath}/${relativePath}`;
        await storage.renameFile(filePath, newFilePath);
        tabManager.renameTab(filePath, newFilePath);

        if (currentFile === filePath) {
          currentFile = newFilePath;
          updateUrlWithFile(newFilePath);
        }
      }

      // Update expanded folders
      if (fileTreeRenderer?.expanded.has(node.path)) {
        fileTreeRenderer.expanded.delete(node.path);
        fileTreeRenderer.expanded.add(newFolderPath);
      }
    }

    await loadFiles();
  } catch (err) {
    console.error('Failed to rename:', err);
    alert(`Failed to rename: ${(err as Error).message}`);
  }
}

// --- Component Tree Handler Functions ---

// Handle inline creation for components (callback for TreeRenderer)
async function handleComponentInlineCreate(name: string, type: 'file' | 'folder', parentPath: string): Promise<void> {
  const sanitizedName = sanitizeFilename(name);
  if (!sanitizedName) return;

  try {
    if (type === 'file') {
      const path = parentPath
        ? `${parentPath}/${sanitizedName}.md`
        : `${sanitizedName}.md`;

      await storage.createComponent(path);
      await loadComponents();
      await loadComponent(path);
    } else {
      // Creating a folder - add to pending folders
      const folderPath = parentPath
        ? `${parentPath}/${sanitizedName}`
        : sanitizedName;

      componentTreeRenderer?.pending.add(folderPath);
      componentTreeRenderer?.expanded.add(folderPath);
      componentTreeRenderer?.render(currentComponentsData.map(c => ({ path: c.name })));
    }
  } catch (err) {
    console.error('Failed to create component:', err);
    alert(`Failed to create: ${(err as Error).message}`);
  }
}

// Handle inline rename for components (callback for TreeRenderer)
async function handleComponentInlineRename(node: TreeNode, newName: string): Promise<void> {
  const sanitizedName = sanitizeFilename(newName);
  if (!sanitizedName || sanitizedName === node.name) return;

  try {
    if (node.type === 'file') {
      const parts = node.path.split('/');
      parts.pop();
      const newPath = parts.length > 0
        ? `${parts.join('/')}/${sanitizedName}.md`
        : `${sanitizedName}.md`;

      await storage.renameComponent(node.path, newPath);
      tabManager.renameTab(`_components/${node.path}`, `_components/${newPath}`);

      if (currentFile === `_components/${node.path}`) {
        currentFile = `_components/${newPath}`;
        updateUrlWithFile(currentFile);
      }
    } else {
      // Rename folder = rename all components inside
      const componentsToRename = componentTreeRenderer?.getFilesUnderFolder(node.path) || [];
      if (componentsToRename.length === 0) return;

      const parts = node.path.split('/');
      parts.pop();
      const newFolderPath = parts.length > 0
        ? `${parts.join('/')}/${sanitizedName}`
        : sanitizedName;

      for (const componentPath of componentsToRename) {
        const relativePath = componentPath.substring(node.path.length + 1);
        const newComponentPath = `${newFolderPath}/${relativePath}`;
        await storage.renameComponent(componentPath, newComponentPath);
        tabManager.renameTab(`_components/${componentPath}`, `_components/${newComponentPath}`);

        if (currentFile === `_components/${componentPath}`) {
          currentFile = `_components/${newComponentPath}`;
          updateUrlWithFile(currentFile);
        }
      }

      // Update expanded folders
      if (componentTreeRenderer?.expanded.has(node.path)) {
        componentTreeRenderer.expanded.delete(node.path);
        componentTreeRenderer.expanded.add(newFolderPath);
      }
    }

    await loadComponents();
  } catch (err) {
    console.error('Failed to rename component:', err);
    alert(`Failed to rename: ${(err as Error).message}`);
  }
}

// Setup event listeners
function setupEventListeners(): void {
  // Toggle edit mode
  btnToggleEdit.addEventListener('click', () => {
    toggleEditMode();
  });

  const handleRefresh = async (): Promise<void> => {
    if (!currentFile) return;

    if (hasUnsavedChanges) {
      // Just reprocess locally if there are unsaved changes
      processFile();
    } else {
      // No pending changes - full resync from server
      setStatus('Resyncing...', 'loading');
      const fileToReload = currentFile;

      try {
        // Reload entire project from server (for cloud backend)
        const backend = storage.backend;
        if (backend && (backend as any).currentProjectId && typeof (backend as any).loadProject === 'function') {
          await (backend as any).loadProject((backend as any).currentProjectId);
          // Also refresh file list in case files were added/removed
          await loadFilesAndComponents();
        }

        // Reload the current file
        if (fileToReload.startsWith('_components/')) {
          const name = fileToReload.replace('_components/', '');
          await loadComponent(name);
        } else {
          await loadFile(fileToReload);
        }
      } catch (err) {
        console.error('Resync failed:', err);
        setStatus('Resync failed', 'error');
      }
    }
  };

  // Refresh buttons - topbar and documentation panel
  btnRefresh.addEventListener('click', () => {
    void handleRefresh();
  });

  // Save status button - manual save trigger
  saveStatusBtn.addEventListener('click', () => {
    if (currentFile) {
      saveFile(true); // Force save - allows manual sync verification
    }
  });

  // Periodically update tooltip to keep relative time fresh
  setInterval(() => {
    if (saveState === 'synced' && lastSaveTime) {
      saveStatusBtn.title = `Saved ${formatRelativeTime(lastSaveTime)}`;
    }
  }, 30000); // Update every 30 seconds

  // Panel toggle checkboxes
  toggleVariables.addEventListener('change', () => {
    updatePanelVisibility();
  });

  toggleDocs.addEventListener('change', () => {
    updatePanelVisibility();
  });

  toggleScripts.addEventListener('change', () => {
    updatePanelVisibility();
  });

  // Keyboard shortcuts
  document.addEventListener('keydown', (e: KeyboardEvent) => {
    // Ctrl/Cmd+F: unified in-app search. Skipped when a modal is open
    // (modals have their own filter inputs) or when CodeMirror's own
    // Mod-f binding already handled it (defaultPrevented).
    if ((e.ctrlKey || e.metaKey) && !e.altKey && !e.shiftKey && e.code === 'KeyF') {
      if (!e.defaultPrevented && !document.querySelector('.modal:not(.hidden)')) {
        e.preventDefault();
        paneSearch.open();
      }
    }
    // Ctrl/Cmd+Shift+F: search the content of every file in the project
    if ((e.ctrlKey || e.metaKey) && !e.altKey && e.shiftKey && e.code === 'KeyF') {
      e.preventDefault();
      if (modalManager.isOpen(MODAL_IDS.PROJECT_SEARCH)) {
        modalManager.hide(MODAL_IDS.PROJECT_SEARCH);
      } else {
        openProjectSearch();
      }
    }
    // Alt/Option+U: show usages of the open component
    if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === 'KeyU') {
      e.preventDefault();
      if (currentFile?.startsWith('_components/')) openComponentUsages(currentFile);
    }
    // F3 / Ctrl+G / Cmd+G: next/previous match while search is open
    if (paneSearch.isOpen() && !e.defaultPrevented &&
        (e.code === 'F3' || ((e.ctrlKey || e.metaKey) && e.code === 'KeyG'))) {
      e.preventDefault();
      if (e.shiftKey) paneSearch.prev(); else paneSearch.next();
    }
    // Ctrl+S / Cmd+S to save
    if ((e.ctrlKey || e.metaKey) && e.code === 'KeyS') {
      e.preventDefault();
      if (editMode && currentFile) {
        saveFile();
      }
    }
    // Alt/Option+E to toggle editor (use e.code for Mac compatibility)
    if (e.altKey && e.code === 'KeyE') {
      e.preventDefault();
      toggleEditMode();
    }
    // Alt/Option+V to toggle variables panel
    if (e.altKey && e.code === 'KeyV') {
      e.preventDefault();
      toggleVariables.checked = !toggleVariables.checked;
      updatePanelVisibility();
    }
    // Alt/Option+D to toggle docs panel
    if (e.altKey && e.code === 'KeyD') {
      e.preventDefault();
      toggleDocs.checked = !toggleDocs.checked;
      updatePanelVisibility();
    }
    // Alt/Option+S to toggle scripts panel
    if (e.altKey && e.code === 'KeyS') {
      e.preventDefault();
      toggleScripts.checked = !toggleScripts.checked;
      updatePanelVisibility();
    }
    // Alt/Option+R to refresh (triggers button click for full resync logic)
    if (e.altKey && e.code === 'KeyR') {
      e.preventDefault();
      void handleRefresh();
    }
    // Alt/Option+B to toggle top bar visibility
    if (e.altKey && e.code === 'KeyB' && !e.shiftKey) {
      e.preventDefault();
      toggleTopbarVisibility();
    }
    // Escape to close any open modal (or the search bar, when focus has
    // moved out of its input — the input handles its own Escape)
    if (e.key === 'Escape') {
      if (paneSearch.isOpen() && !document.querySelector('.modal:not(.hidden)')) {
        paneSearch.close();
        return;
      }
      selectedSnapshotId = null;
      closeAllModals();
    }
    // Alt/Option+Y to show history
    if (e.altKey && e.code === 'KeyY') {
      e.preventDefault();
      openHistoryModal();
    }
    // Alt/Option+M to show media library
    if (e.altKey && e.code === 'KeyM') {
      e.preventDefault();
      openMediaLibrary();
    }
    // Alt/Option+T to toggle TOC sidebar
    if (e.altKey && e.code === 'KeyT' && !e.shiftKey) {
      e.preventDefault();
      btnTocSidebar.click();
    }
    // Alt/Option+? or Alt+H to show help
    if ((e.altKey && e.code === 'Slash' && e.shiftKey) || (e.altKey && e.code === 'KeyH' && !e.shiftKey)) {
      e.preventDefault();
      modalManager.toggle(MODAL_IDS.HELP);
    }
    // Alt/Option+N to create new file
    if (e.altKey && e.code === 'KeyN' && !e.shiftKey) {
      e.preventDefault();
      showCreateFileModal();
    }
    // Alt/Option+Shift+N to create new component
    if (e.altKey && e.code === 'KeyN' && e.shiftKey) {
      e.preventDefault();
      showCreateComponentModal();
    }
    // File/project switcher shortcuts. The same modal serves both:
    //   - Alt/Option+Space: spotlight-style shortcut, great on macOS.
    //   - Alt/Option+Z: cross-platform fallback for Windows (where the OS
    //     captures Alt+Space for the window system menu).
    // Alt+Q used to be supported too, but on macOS a slightly-mistimed Cmd+Q
    // (which quits the app) was easy to confuse with it — so we dropped it.
    const isSwitcherShortcut =
      e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey &&
      (e.code === 'Space' || e.code === 'KeyZ');
    if (isSwitcherShortcut) {
      e.preventDefault();
      const modal = document.getElementById(MODAL_IDS.PROJECT_SWITCHER);
      if (modal && !modal.classList.contains('hidden')) {
        modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);
      } else {
        openProjectSwitcher();
      }
    }
    // Alt+. or Alt+Right to switch to next tab
    if (e.altKey && !e.shiftKey && (e.code === 'Period' || e.code === 'ArrowRight')) {
      e.preventDefault();
      const nextPath = tabManager.nextTab();
      if (nextPath) tabBar?.onTabClick?.(nextPath);
    }
    // Alt+, or Alt+Left to switch to previous tab
    if (e.altKey && !e.shiftKey && (e.code === 'Comma' || e.code === 'ArrowLeft')) {
      e.preventDefault();
      const prevPath = tabManager.prevTab();
      if (prevPath) tabBar?.onTabClick?.(prevPath);
    }
    // Alt+X to maximize/restore the active window (windowed mode only)
    if (e.altKey && e.code === 'KeyX' && !e.shiftKey && desktop?.isEnabled()) {
      e.preventDefault();
      desktop.toggleMaximize();
    }
    // Alt+W to close current tab
    if (e.altKey && e.code === 'KeyW' && !e.shiftKey) {
      e.preventDefault();
      if (tabManager.activeTab) tabBar?.onTabClose?.(tabManager.activeTab);
    }
  });

  // Export button
  btnExportDocx.addEventListener('click', () => showDocxExportModal());

  // TOC sidebar toggle button
  btnTocSidebar.addEventListener('click', () => {
    tocSidebar.toggle();
    btnTocSidebar.classList.toggle('active', tocSidebar.isVisible());
    localStorage.setItem('leedoc_toc_sidebar', String(tocSidebar.isVisible()));
  });

  // Markdown source view toggle button.
  // Ephemeral / per-view: see resetMarkdownSourceView() — every file switch
  // drops back to the rendered preview.
  btnProjectSearch.addEventListener('click', () => openProjectSearch());
  btnComponentUsages.addEventListener('click', () => {
    if (currentFile?.startsWith('_components/')) openComponentUsages(currentFile);
  });
  setupSaveErrorModal();

  btnViewMarkdown.addEventListener('click', () => {
    markdownSourceView = !markdownSourceView;
    btnViewMarkdown.classList.toggle('active', markdownSourceView);
    void processFile();
  });

  // Checklist toggle button
  btnChecklist.addEventListener('click', async () => {
    if (checklistEnabled) {
      // Disabling — confirm and delete all progress
      const confirmed = confirm(
        'Are you sure you wish to disable (and delete) all checkbox progress for this document?'
      );
      if (!confirmed) return;

      if (currentFile) {
        try {
          await storage.backend.deleteChecklist(currentFile);
        } catch (err) {
          console.error('Failed to delete checklist:', err);
        }
      }
      checklistSlugs = [];
      checklistEnabled = false;
      checklistEnricher.setEnabled(false);
      btnChecklist.classList.remove('active');
      markdownOutput.classList.remove('checklist-mode');
      await processFile();
    } else {
      // Enabling — no warning
      checklistEnabled = true;
      checklistSlugs = [];
      checklistEnricher.setEnabled(true);
      btnChecklist.classList.add('active');
      markdownOutput.classList.add('checklist-mode');
      // Save empty checklist to mark "mode on"
      if (currentFile) {
        try {
          await storage.backend.saveChecklist(currentFile, []);
        } catch (err) {
          console.error('Failed to save checklist:', err);
        }
      }
      await processFile();
    }
  });

  // Copy script button
  btnCopy.addEventListener('click', async () => {
    const text = scriptsOutput.value;
    if (!text) return;

    try {
      await navigator.clipboard.writeText(text);
      btnCopy.textContent = 'Copied!';
      btnCopy.classList.add('copied');
      setTimeout(() => {
        btnCopy.textContent = 'Copy';
        btnCopy.classList.remove('copied');
      }, 2000);
    } catch (err) {
      scriptsOutput.select();
      document.execCommand('copy');
      btnCopy.textContent = 'Copied!';
      btnCopy.classList.add('copied');
      setTimeout(() => {
        btnCopy.textContent = 'Copy';
        btnCopy.classList.remove('copied');
      }, 2000);
    }
  });

  // Copy as install one-liner — split button driven by the
  // INSTALL_ONELINERS registry. Menu items are rendered once at init;
  // the ".suggested" class is toggled per-open based on the active tab.
  const setInstallButtonState = (state: 'idle' | 'copied') => {
    if (state === 'copied') {
      btnCopyInstall.textContent = 'Copied!';
      btnCopyInstall.classList.add('copied');
    } else {
      btnCopyInstall.textContent = 'Install';
      btnCopyInstall.classList.remove('copied');
    }
  };

  const closeInstallMenu = () => {
    installMenu.classList.add('hidden');
    btnInstallMenuToggle.setAttribute('aria-expanded', 'false');
  };

  const copyInstallOneliner = async (provider: InstallOnelinerProvider) => {
    const text = scriptsOutput.value;
    if (!text || !activeScriptTab) return;
    const oneliner = provider.build(activeScriptTab, text);

    try {
      await navigator.clipboard.writeText(oneliner);
    } catch {
      const tempTextarea = document.createElement('textarea');
      tempTextarea.value = oneliner;
      document.body.appendChild(tempTextarea);
      tempTextarea.select();
      document.execCommand('copy');
      document.body.removeChild(tempTextarea);
    }

    closeInstallMenu();
    setInstallButtonState('copied');
    setTimeout(() => setInstallButtonState('idle'), 2000);
  };

  // Render menu items once from the registry
  const installMenuButtons: Map<string, HTMLButtonElement> = new Map();
  for (const provider of INSTALL_ONELINERS) {
    const btn = document.createElement('button');
    btn.className = 'install-menu-item';
    btn.setAttribute('role', 'menuitem');
    btn.id = `install-menu-${provider.id}`;

    const textWrap = document.createElement('div');
    textWrap.className = 'install-menu-text';

    const label = document.createElement('div');
    label.className = 'install-menu-label';
    label.textContent = provider.label;
    textWrap.appendChild(label);

    const sub = document.createElement('div');
    sub.className = 'install-menu-sub';
    sub.textContent = provider.sublabel;
    if (provider.sublabelCode) {
      sub.appendChild(document.createTextNode(' — '));
      const code = document.createElement('code');
      code.textContent = provider.sublabelCode;
      sub.appendChild(code);
    }
    textWrap.appendChild(sub);

    btn.appendChild(textWrap);
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      void copyInstallOneliner(provider);
    });

    installMenu.appendChild(btn);
    installMenuButtons.set(provider.id, btn);
  }

  const openInstallMenu = () => {
    if (!activeScriptTab) return;
    const suggested = getRecommendedInstallOneliner(activeScriptTab);
    for (const [id, btn] of installMenuButtons) {
      btn.classList.toggle('suggested', id === suggested.id);
    }
    installMenu.classList.remove('hidden');
    btnInstallMenuToggle.setAttribute('aria-expanded', 'true');
  };

  // Main button: one-click copy of the recommended shell
  btnCopyInstall.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!scriptsOutput.value || !activeScriptTab) return;
    const provider = getRecommendedInstallOneliner(activeScriptTab);
    void copyInstallOneliner(provider);
  });

  // Caret: open/close the override menu
  btnInstallMenuToggle.addEventListener('click', (e) => {
    e.stopPropagation();
    if (!scriptsOutput.value || !activeScriptTab) return;
    if (installMenu.classList.contains('hidden')) {
      openInstallMenu();
    } else {
      closeInstallMenu();
    }
  });

  // Close menu when a press starts outside (mousedown, not click, so
  // selection drags released outside don't dismiss it) or on Escape
  document.addEventListener('mousedown', (e) => {
    if (installMenu.classList.contains('hidden')) return;
    const target = e.target as Node;
    if (
      !installMenu.contains(target) &&
      !btnInstallMenuToggle.contains(target) &&
      !btnCopyInstall.contains(target)
    ) {
      closeInstallMenu();
    }
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && !installMenu.classList.contains('hidden')) {
      closeInstallMenu();
    }
  });

  // Script mode select
  scriptModeSelect.addEventListener('change', () => {
    scriptMode = scriptModeSelect.value as ScriptMode;
    if (activeScriptTab) {
      updateScriptDisplay(activeScriptTab);
    }
  });

  // Script variables button
  btnScriptVars.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (activeScriptTab) {
      showScriptVarsEditor(activeScriptTab);
    }
  });

  // CRUD: New file button (from sidebar)
  btnNewFile?.addEventListener('click', (e: MouseEvent) => {
    e.stopPropagation();
    showCreateFileModal();
  });

  // CRUD: New component button (from sidebar)
  btnNewComponent?.addEventListener('click', (e: MouseEvent) => {
    e.stopPropagation();
    showCreateComponentModal();
  });

  // CRUD: Create file form
  newFileCategory.addEventListener('change', handleCategoryChange);
  newCategoryName.addEventListener('input', updateFilePreview);
  newFileName.addEventListener('input', updateFilePreview);
  btnCreateFile.addEventListener('click', createNewFile);

  // CRUD: Create component form
  newComponentName.addEventListener('input', updateComponentPreview);
  btnCreateComponent.addEventListener('click', createNewComponent);

  // CRUD: Confirm delete
  btnConfirmDelete.addEventListener('click', confirmDelete);

  // CRUD: Rename form
  renameInput.addEventListener('input', updateRenamePreview);
  btnConfirmRename.addEventListener('click', confirmRename);

  // CRUD: Modal close buttons (use event delegation)
  document.querySelectorAll('.modal-close-btn').forEach(btn => {
    btn.addEventListener('click', closeAllModals);
  });

  // CRUD: Close modals on backdrop press. Uses mousedown (not click) so a
  // text-selection drag that starts in an input and releases over the
  // backdrop doesn't close the modal.
  [createFileModal, createComponentModal, deleteModal, renameModal].filter(Boolean).forEach(modal => {
    modal!.addEventListener('mousedown', (e: MouseEvent) => {
      if (e.target === modal) {
        closeAllModals();
      }
    });
  });

  // Note: Help modal close handlers are now managed by ModalManager

  btnCreateSnapshot.addEventListener('click', async () => {
    if (!currentFile) {
      alert('Please select a file first');
      return;
    }
    try {
      // currentFile already has the correct path (including _components/ prefix for components)
      await storage.backend.createSnapshot(currentFile, 'manual');
      await renderHistoryList();
    } catch (err) {
      console.error('Failed to create snapshot:', err);
    }
  });

  btnRestoreSnapshot.addEventListener('click', async () => {
    if (!selectedSnapshotId || !currentFile) return;

    if (!confirm('Are you sure you want to restore this version? Your current work will be saved as a restore point first.')) {
      return;
    }

    try {
      // currentFile already has the correct path
      const result = await storage.backend.restoreSnapshot(currentFile, selectedSnapshotId) as any;
      modalManager.hide(MODAL_IDS.HISTORY);
      selectedSnapshotId = null;

      // Reload the current file with restored content
      if (result.success && result.content) {
        sourceContent = result.content;
        initCodeMirror(sourceContent);
        hasUnsavedChanges = false;
        processFile();
      }
    } catch (err) {
      const error = err as Error;
      console.error('Failed to restore snapshot:', err);
      alert('Failed to restore snapshot: ' + error.message);
    }
  });

  // Note: History modal backdrop click is handled by ModalManager

  btnMediaUpload?.addEventListener('click', () => {
    mediaUploadInput?.click();
  });

  mediaUploadInput?.addEventListener('change', async () => {
    const files = mediaUploadInput.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      const result = await storage.backend.uploadMedia(file);
      if (result.success) {
        setStatus(`Uploaded: ${result.filename}`, 'success');
      } else {
        setStatus(`Upload failed: ${result.error}`, 'error');
      }
    }

    mediaUploadInput.value = '';
    await renderMediaGrid();
  });

  btnMediaDeleteUnused?.addEventListener('click', deleteUnusedMedia);

  btnMediaInsert?.addEventListener('click', insertSelectedMedia);

  // Template modal buttons
  btnTemplateUpload?.addEventListener('click', () => {
    templateUploadInput?.click();
  });

  templateUploadInput?.addEventListener('change', async () => {
    const file = templateUploadInput?.files?.[0];
    if (!file) return;

    const name = prompt('Template name:', file.name.replace('.docx', ''));
    if (!name) {
      templateUploadInput.value = '';
      return;
    }

    const description = prompt('Description (optional):');

    try {
      await templateManager.uploadTemplate(file, name, description || undefined);
      setStatus('Template uploaded successfully', 'success');
    } catch (err) {
      const error = err as Error;
      alert(`Upload failed: ${error.message}`);
    }

    templateUploadInput.value = '';
  });

  btnTemplateDownloadDefault?.addEventListener('click', async () => {
    try {
      await templateManager.downloadDefaultTemplate();
    } catch (err) {
      const error = err as Error;
      alert(`Download failed: ${error.message}\n\nYou can create your own template by exporting a DOCX and customizing it in Word.`);
    }
  });

  // Toggle front page options visibility
  docxExportFrontpage?.addEventListener('change', () => {
    frontpageOptions?.classList.toggle('hidden', !docxExportFrontpage.checked);
  });

  btnDocxExportConfirm?.addEventListener('click', handleDocxExport);

  btnManageTemplates?.addEventListener('click', () => {
    modalManager.hide(MODAL_IDS.DOCX_EXPORT);
    openTemplateLibrary();
  });
}

async function openHistoryModal(): Promise<void> {
  if (!currentFile) {
    alert('Please select a file first to view its history');
    return;
  }

  modalManager.show(MODAL_IDS.HISTORY);
  selectedSnapshotId = null;
  btnRestoreSnapshot.disabled = true;

  // Update modal title with current file name
  const historyModalTitle = document.getElementById('history-modal-title') as HTMLHeadingElement;
  const fileName = currentFile.split('/').pop()!;
  historyModalTitle.textContent = `Version History: ${fileName}`;

  await renderHistoryList();
}

async function renderHistoryList(): Promise<void> {
  // currentFile already has the correct path (including _components/ prefix for components)
  const filePath = currentFile;

  if (!filePath) {
    historyList.innerHTML = '<p class="history-empty">No file selected.</p>';
    return;
  }

  try {
    const snapshots = await storage.backend.listSnapshots(filePath) as Snapshot[];

    if (!snapshots || snapshots.length === 0) {
      historyList.innerHTML = '<p class="history-empty">No version history yet for this file. Start editing to create snapshots.</p>';
      return;
    }

    // Group snapshots by relative date
    const now = Date.now();
    const DAY = 24 * 60 * 60 * 1000;
    const WEEK = 7 * DAY;

    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTodayMs = startOfToday.getTime();
    const startOfYesterday = startOfTodayMs - DAY;

    const groups: Record<string, Snapshot[]> = {
      today: [],
      yesterday: [],
      thisWeek: [],
      thisMonth: [],
      older: []
    };

    for (const snap of snapshots) {
      const age = now - snap.timestamp;

      if (snap.timestamp >= startOfTodayMs) {
        groups.today.push(snap);
      } else if (snap.timestamp >= startOfYesterday) {
        groups.yesterday.push(snap);
      } else if (age < WEEK) {
        groups.thisWeek.push(snap);
      } else if (age < 4 * WEEK) {
        groups.thisMonth.push(snap);
      } else {
        groups.older.push(snap);
      }
    }

    const reasonLabels: Record<string, string> = {
      initial: 'Initial version',
      daily_first: 'Daily backup',
      hourly: 'Hourly backup',
      manual: 'Manual restore point',
      pre_delete: 'Before deletion',
      pre_restore: 'Before restore'
    };

    const renderGroup = (groupName: string, items: Snapshot[]): string => {
      if (items.length === 0) return '';

      const itemsHtml = items.map(snap => {
        const time = new Date(snap.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        const date = new Date(snap.timestamp).toLocaleDateString([], { month: 'short', day: 'numeric' });
        const reason = reasonLabels[snap.reason] || snap.reason;
        const selected = snap.id === selectedSnapshotId ? ' selected' : '';

        return `
          <div class="history-item${selected}" data-snapshot-id="${snap.id}">
            <span class="history-item-time">${groupName === 'Today' || groupName === 'Yesterday' ? time : date + ' ' + time}</span>
            <span class="history-item-reason">${reason}</span>
          </div>
        `;
      }).join('');

      return `
        <div class="history-group">
          <div class="history-group-header">${groupName}</div>
          ${itemsHtml}
        </div>
      `;
    };

    historyList.innerHTML = [
      renderGroup('Today', groups.today),
      renderGroup('Yesterday', groups.yesterday),
      renderGroup('This Week', groups.thisWeek),
      renderGroup('This Month', groups.thisMonth),
      renderGroup('Older', groups.older)
    ].join('');

    // Add click handlers for selection
    historyList.querySelectorAll('.history-item').forEach(item => {
      item.addEventListener('click', () => {
        // Deselect previous
        historyList.querySelectorAll('.history-item.selected').forEach(el => el.classList.remove('selected'));
        // Select current
        item.classList.add('selected');
        selectedSnapshotId = (item as HTMLElement).dataset.snapshotId!;
        btnRestoreSnapshot.disabled = false;
      });
    });

  } catch (err) {
    console.error('Failed to load history:', err);
    historyList.innerHTML = '<p class="history-empty">Failed to load version history.</p>';
  }
}

// Handle media file drop in editor
async function handleMediaDrop(files: File[], view: EditorView, insertPos: number): Promise<void> {
  for (const file of files) {
    // Insert placeholder
    const placeholderText = `![Uploading ${file.name}...]()`;
    view.dispatch({
      changes: { from: insertPos, insert: placeholderText + '\n' }
    });

    try {
      const result = await storage.backend.uploadMedia(file, currentFile ?? undefined);

      if (result.success && result.markdown) {
        // Find and replace placeholder with actual markdown
        const doc = view.state.doc.toString();
        const placeholderIndex = doc.indexOf(placeholderText);

        if (placeholderIndex !== -1) {
          view.dispatch({
            changes: {
              from: placeholderIndex,
              to: placeholderIndex + placeholderText.length,
              insert: result.markdown
            }
          });
        }

        if (result.deduplicated) {
          setStatus('Image already exists, reused existing file', 'info');
        } else {
          setStatus(`Uploaded: ${result.filename}`, 'success');
        }
      } else {
        // Remove placeholder on error
        const doc = view.state.doc.toString();
        const placeholderIndex = doc.indexOf(placeholderText);
        if (placeholderIndex !== -1) {
          view.dispatch({
            changes: {
              from: placeholderIndex,
              to: placeholderIndex + placeholderText.length + 1, // +1 for newline
              insert: ''
            }
          });
        }
        setStatus(`Upload failed: ${result.error || 'Unknown error'}`, 'error');
      }
    } catch (err) {
      // Remove placeholder on error
      const doc = view.state.doc.toString();
      const placeholderIndex = doc.indexOf(placeholderText);
      if (placeholderIndex !== -1) {
        view.dispatch({
          changes: {
            from: placeholderIndex,
            to: placeholderIndex + placeholderText.length + 1,
            insert: ''
          }
        });
      }
      setStatus(`Upload failed: ${err instanceof Error ? err.message : 'Unknown error'}`, 'error');
    }
  }
}

// Handle media paste from clipboard
async function handleMediaPaste(items: DataTransferItem[], view: EditorView): Promise<void> {
  for (const item of items) {
    const file = item.getAsFile();
    if (!file) continue;

    // Generate a name for pasted images
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const ext = file.type.split('/')[1] || 'png';
    const namedFile = new File([file], `pasted-image-${timestamp}.${ext}`, { type: file.type });

    await handleMediaDrop([namedFile], view, view.state.selection.main.head);
  }
}

// Format file size for display
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Rewrite media:// URLs to actual URLs for rendering
// Supports image sizing: ![alt](media://file.png =300x200), =300x (width only), =x200 (height only)
function rewriteMediaUrls(content: string): string {
  return content.replace(
    /!\[([^\]]*)\]\(media:\/\/([^)\s]+)(?:\s*=(\d*)x(\d*))?\)/g,
    (_, alt, filename, width, height) => {
      const url = storage.backend.getMediaUrl(filename);

      // If sizing specified, use HTML img tag
      if (width || height) {
        const attrs: string[] = [`src="${url}"`];
        if (alt) attrs.push(`alt="${alt.replace(/"/g, '&quot;')}"`);
        if (width) attrs.push(`width="${width}"`);
        if (height) attrs.push(`height="${height}"`);
        return `<img ${attrs.join(' ')}>`;
      }

      // Standard markdown image
      return `![${alt}](${url})`;
    }
  );
}

// Open media library modal
async function openMediaLibrary(): Promise<void> {
  modalManager.show(MODAL_IDS.MEDIA);
  selectedMediaFilename = null;
  btnMediaInsert.disabled = true;

  await renderMediaGrid();
}

// Render media grid in library
async function renderMediaGrid(): Promise<void> {
  try {
    const result = await storage.backend.listMedia();

    // Update stats
    mediaCount.textContent = `${result.count} file${result.count !== 1 ? 's' : ''}`;
    mediaSize.textContent = formatFileSize(result.totalSize);

    if (result.media.length === 0) {
      mediaGrid.innerHTML = '<p class="media-empty">No media files yet. Drag and drop images into the editor or use the Upload button.</p>';
      btnMediaDeleteUnused.disabled = true;
      return;
    }

    // Check usage for each media file
    const usagePromises = result.media.map(m => storage.backend.getMediaUsage(m.filename));
    const usages = await Promise.all(usagePromises);

    // Count orphaned files
    const orphanedCount = usages.filter(u => u.isOrphaned).length;
    btnMediaDeleteUnused.disabled = orphanedCount === 0;

    // Render grid
    mediaGrid.innerHTML = result.media.map((item, i) => {
      const isOrphaned = usages[i].isOrphaned;
      const thumbnailUrl = storage.backend.getMediaUrl(item.filename);

      return `
        <div class="media-item ${isOrphaned ? 'orphaned' : ''}" data-filename="${item.filename}">
          <input type="checkbox" class="media-item-checkbox" ${selectedMediaFilename === item.filename ? 'checked' : ''}>
          <img class="media-item-thumb" src="${thumbnailUrl}" alt="${item.originalName}" loading="lazy">
          <button class="media-item-delete" title="Delete" data-filename="${item.filename}">
            <svg xmlns="http://www.w3.org/2000/svg" height="14" viewBox="0 -960 960 960" width="14" fill="currentColor"><path d="M280-120q-33 0-56.5-23.5T200-200v-520h-40v-80h200v-40h240v40h200v80h-40v520q0 33-23.5 56.5T680-120H280Z"/></svg>
          </button>
          <div class="media-item-info">
            <span class="media-item-name" title="${item.originalName}">${item.originalName}</span>
            <span class="media-item-size">${formatFileSize(item.size)}</span>
            ${isOrphaned ? '<span class="media-item-orphaned">Unused</span>' : ''}
          </div>
        </div>
      `;
    }).join('');

    // Add click handlers
    mediaGrid.querySelectorAll('.media-item').forEach(item => {
      const checkbox = item.querySelector('.media-item-checkbox') as HTMLInputElement;
      const filename = (item as HTMLElement).dataset.filename!;

      // Click on item to select
      item.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        if (target.closest('.media-item-delete') || target.classList.contains('media-item-checkbox')) {
          return;
        }

        // Deselect all others
        mediaGrid.querySelectorAll('.media-item.selected').forEach(el => el.classList.remove('selected'));
        mediaGrid.querySelectorAll('.media-item-checkbox').forEach(cb => (cb as HTMLInputElement).checked = false);

        // Select this one
        item.classList.add('selected');
        checkbox.checked = true;
        selectedMediaFilename = filename;
        btnMediaInsert.disabled = false;
      });

      // Checkbox change
      checkbox.addEventListener('change', () => {
        if (checkbox.checked) {
          mediaGrid.querySelectorAll('.media-item.selected').forEach(el => el.classList.remove('selected'));
          mediaGrid.querySelectorAll('.media-item-checkbox').forEach(cb => {
            if (cb !== checkbox) (cb as HTMLInputElement).checked = false;
          });
          item.classList.add('selected');
          selectedMediaFilename = filename;
          btnMediaInsert.disabled = false;
        } else {
          item.classList.remove('selected');
          selectedMediaFilename = null;
          btnMediaInsert.disabled = true;
        }
      });
    });

    // Add delete button handlers
    mediaGrid.querySelectorAll('.media-item-delete').forEach(btn => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const filename = (btn as HTMLElement).dataset.filename!;
        if (confirm(`Delete "${filename}"? This cannot be undone.`)) {
          await storage.backend.deleteMedia(filename);
          await renderMediaGrid();
          setStatus(`Deleted: ${filename}`, 'info');
        }
      });
    });

  } catch (err) {
    console.error('Failed to load media:', err);
    mediaGrid.innerHTML = '<p class="media-empty">Failed to load media library.</p>';
  }
}

// Delete unused media files
async function deleteUnusedMedia(): Promise<void> {
  try {
    const result = await storage.backend.listMedia();
    const usagePromises = result.media.map(m => storage.backend.getMediaUsage(m.filename));
    const usages = await Promise.all(usagePromises);

    const unusedFilenames = result.media
      .filter((_, i) => usages[i].isOrphaned)
      .map(m => m.filename);

    if (unusedFilenames.length === 0) {
      setStatus('No unused media files to delete', 'info');
      return;
    }

    if (!confirm(`Delete ${unusedFilenames.length} unused media file${unusedFilenames.length > 1 ? 's' : ''}? This cannot be undone.`)) {
      return;
    }

    const deleteResult = await storage.backend.bulkDeleteMedia(unusedFilenames);
    await renderMediaGrid();
    setStatus(`Deleted ${deleteResult.deleted} unused file${deleteResult.deleted !== 1 ? 's' : ''}`, 'success');
  } catch (err) {
    console.error('Failed to delete unused media:', err);
    setStatus('Failed to delete unused media', 'error');
  }
}

// Insert selected media into editor
function insertSelectedMedia(): void {
  if (!selectedMediaFilename || !editorView) return;

  const markdown = `![](media://${selectedMediaFilename})`;
  const pos = editorView.state.selection.main.head;

  editorView.dispatch({
    changes: { from: pos, insert: markdown + '\n' }
  });

  editorView.focus();
  modalManager.hide(MODAL_IDS.MEDIA);
  setStatus(`Inserted: ${selectedMediaFilename}`, 'info');
}

// ==================== Template Library ====================

async function openTemplateLibrary(): Promise<void> {
  // Templates are only available in cloud storage mode
  if (storage.currentBackendName !== 'cloud') {
    templateList.innerHTML = '<p class="template-empty">DOCX templates are only available when signed in to cloud storage.</p>';
    modalManager.show(MODAL_IDS.TEMPLATE);
    return;
  }

  modalManager.show(MODAL_IDS.TEMPLATE);
  try {
    await templateManager.loadTemplates();
    renderTemplateList();
  } catch (err) {
    console.error('Failed to load templates:', err);
    templateList.innerHTML = '<p class="template-empty">Failed to load templates. Please try again.</p>';
  }
}

function renderTemplateList(): void {
  const templates = templateManager.getTemplates();

  if (templates.length === 0) {
    templateList.innerHTML = '<p class="template-empty">No custom templates yet. Upload a .docx file to get started.</p>';
    return;
  }

  templateList.innerHTML = templates.map(template => `
    <div class="template-item ${template.isDefault ? 'default' : ''}" data-template-id="${template.id}">
      <div class="template-item-info">
        <div class="template-item-name">
          ${escapeHtml(template.name)}
          ${template.isDefault ? '<span class="template-default-badge">Default</span>' : ''}
        </div>
        ${template.description ? `<div class="template-item-description">${escapeHtml(template.description)}</div>` : ''}
        <div class="template-item-meta">
          ${new Date(template.createdAt).toLocaleDateString()}
        </div>
      </div>
      <div class="template-item-actions">
        <button class="template-action-btn" data-action="download" data-template-id="${template.id}" data-filename="${template.filename}">
          Download
        </button>
        ${!template.isDefault ? `
          <button class="template-action-btn" data-action="set-default" data-template-id="${template.id}">
            Set Default
          </button>
        ` : `
          <button class="template-action-btn" data-action="clear-default" data-template-id="${template.id}">
            Clear Default
          </button>
        `}
        <button class="template-action-btn danger" data-action="delete" data-template-id="${template.id}">
          Delete
        </button>
      </div>
    </div>
  `).join('');

  // Attach event listeners to action buttons
  templateList.querySelectorAll('[data-action]').forEach(btn => {
    btn.addEventListener('click', handleTemplateAction);
  });
}

async function handleTemplateAction(e: Event): Promise<void> {
  const target = e.currentTarget as HTMLButtonElement;
  const action = target.dataset.action;
  const templateId = target.dataset.templateId;
  const filename = target.dataset.filename;

  if (!templateId) return;

  try {
    switch (action) {
      case 'download':
        await templateManager.downloadTemplate(parseInt(templateId, 10), filename || 'template.docx');
        break;
      case 'set-default':
        await templateManager.setDefaultTemplate(parseInt(templateId, 10));
        setStatus('Template set as default', 'success');
        break;
      case 'clear-default':
        await templateManager.clearDefaultTemplate(parseInt(templateId, 10));
        setStatus('Default template cleared', 'success');
        break;
      case 'delete':
        if (confirm('Delete this template?')) {
          await templateManager.deleteTemplate(parseInt(templateId, 10));
          setStatus('Template deleted', 'success');
        }
        break;
    }
  } catch (err) {
    const error = err as Error;
    console.error(`Template action ${action} failed:`, err);
    alert(`Failed: ${error.message}`);
  }
}

function updateDocxExportTemplateSelect(): void {
  const templates = templateManager.getTemplates();
  const defaultTemplate = templateManager.getDefaultTemplate();

  docxExportTemplate.innerHTML = '<option value="">Leedoc Default</option>' +
    templates.map(t => `
      <option value="${t.id}" ${t.isDefault ? 'selected' : ''}>
        ${escapeHtml(t.name)} ${t.isDefault ? '(Your Default)' : ''}
      </option>
    `).join('');
}

// ==================== DOCX Export Modal ====================

function showDocxExportModal(): void {
  if (!currentFile || !wovenContent) {
    alert('No document to export. Please open a file first.');
    return;
  }

  if (!wovenContent.trim()) {
    alert('No content to export. Please ensure the document is loaded.');
    return;
  }

  // Pre-fill title from current file
  const filename = currentFile.replace(/\.src\.md$/, '').replace(/^.*\//, '');
  docxExportTitle.value = filename;

  // Reset form
  docxExportToc.checked = false;

  // Load templates if in cloud mode
  if (storage.currentBackendName === 'cloud') {
    templateManager.loadTemplates().then(() => {
      updateDocxExportTemplateSelect();
    }).catch(err => {
      console.warn('Failed to load templates:', err);
      docxExportTemplate.innerHTML = '<option value="">Leedoc Default</option>';
    });
  } else {
    docxExportTemplate.innerHTML = '<option value="">Leedoc Default</option>';
  }

  modalManager.show(MODAL_IDS.DOCX_EXPORT);
}

async function handleDocxExport(): Promise<void> {
  const title = docxExportTitle.value.trim() || currentFile?.replace(/\.src\.md$/, '').replace(/^.*\//, '') || 'document';
  const templateId = docxExportTemplate.value ? parseInt(docxExportTemplate.value, 10) : undefined;
  const includeToc = docxExportToc.checked;
  const includeFrontPage = docxExportFrontpage.checked;
  const subtitle = docxExportSubtitle.value.trim();
  const company = docxExportCompany.value.trim();

  // Get project ID for media resolution
  const backend = storage.backend as any;
  const project = backend.getCurrentProject?.() || (backend.currentProjectId ? { id: backend.currentProjectId } : null);
  const projectId = project?.id ? parseInt(String(project.id), 10) : undefined;

  modalManager.hide(MODAL_IDS.DOCX_EXPORT);
  setStatus('Exporting...', 'loading');

  try {
    // Clean woven markdown by stripping leedoc-specific markers
    const exportContent = cleanForExport(wovenContent);

    const options: ExportOptions = {
      title,
      filename: title,
      templateId,
      projectId,
      docxOptions: {
        includeToc,
        includeFrontPage,
        subtitle,
        company,
      },
    };

    const success = await exportService.exportAndDownload(exportContent, 'docx', options);

    if (success) {
      setStatus('Exported', '');
      setTimeout(() => setStatus('Ready', ''), 2000);
    } else {
      setStatus('Export failed', 'error');
    }
  } catch (error) {
    console.error('Export error:', error);
    setStatus('Export failed', 'error');
    alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

function indentSelectedLines(view: EditorView, outdent = false): boolean {
  const { state } = view;
  const touchedLines = new Set<number>();

  for (const range of state.selection.ranges) {
    const startLine = state.doc.lineAt(range.from).number;
    const endPos = range.to > range.from ? range.to - 1 : range.to;
    const endLine = state.doc.lineAt(endPos).number;

    // Cursor-only selections should still indent/outdent the current line.
    if (range.empty) {
      touchedLines.add(startLine);
      continue;
    }

    if (startLine === endLine) continue;

    for (let lineNo = startLine; lineNo <= endLine; lineNo += 1) {
      touchedLines.add(lineNo);
    }
  }

  if (touchedLines.size === 0) return false;

  const lineNumbers = Array.from(touchedLines).sort((a, b) => a - b);
  const changes: { from: number; to?: number; insert: string }[] = [];

  for (const lineNo of lineNumbers) {
    const line = state.doc.line(lineNo);

    if (!outdent) {
      changes.push({ from: line.from, insert: '  ' });
      continue;
    }

    if (line.text.startsWith('\t')) {
      changes.push({ from: line.from, to: line.from + 1, insert: '' });
    } else if (line.text.startsWith('  ')) {
      changes.push({ from: line.from, to: line.from + 2, insert: '' });
    } else if (line.text.startsWith(' ')) {
      changes.push({ from: line.from, to: line.from + 1, insert: '' });
    }
  }

  if (changes.length > 0) {
    view.dispatch({ changes });
  }

  return true;
}

// Build the CodeMirror extension list. Called fresh for each EditorState so
// that swapping states across tabs (via setState) gives each file its own
// undo/redo history. Listeners close over module-global state, so reusing
// vs. recreating extension instances is functionally equivalent.
function buildEditorExtensions(): Extension[] {
  const updateListener = EditorView.updateListener.of((update) => {
    if (update.docChanged) {
      // Skip save/process triggers when restoring tab state
      if (isRestoringTabState) return;

      sourceContent = update.state.doc.toString();
      const wasUnsaved = hasUnsavedChanges;
      hasUnsavedChanges = true;
      setSaveStatus('modified');
      // After the re-render, snap the preview to the line being edited
      pendingCursorAlign = true;
      debouncedProcess();
      debouncedSave();
      debouncedVariableDetect();

      // Update tab state (only re-render tab bar on transition to unsaved)
      tabManager.updateActiveState({ sourceContent, hasUnsavedChanges: true });
      if (!wasUnsaved) tabBar?.render();
    } else if (
      // Clicking in the source is an explicit "I'm looking here" — align the
      // preview to that spot (scroll-position sync alone can't know whether
      // the user's attention is at the top or bottom of the viewport,
      // especially with embeds/collapsed blocks stretching the mapping).
      // Pointer selections only: keyboard cursor movement and programmatic
      // selection restores must not yank the preview around.
      update.selectionSet &&
      !isRestoringTabState &&
      update.transactions.some(tr => tr.isUserEvent('select.pointer'))
    ) {
      scrollSync.alignToCursor();
    }
  });

  const saveKeymap = keymap.of([{
    key: 'Mod-s',
    run: () => {
      if (currentFile) {
        saveFile();
      }
      return true;
    }
  }]);

  const scrollListener = EditorView.domEventHandlers({
    scroll: () => {
      if (!isRestoringTabState) scrollSync.onEditorScroll();
      return false;
    }
  });

  const mediaHandler = EditorView.domEventHandlers({
    drop: (event: DragEvent, view: EditorView) => {
      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return false;

      const imageFiles = Array.from(files).filter(f =>
        f.type.startsWith('image/') || f.type === 'application/pdf'
      );

      if (imageFiles.length === 0) return false;

      event.preventDefault();
      event.stopPropagation();

      const pos = view.posAtCoords({ x: event.clientX, y: event.clientY });
      handleMediaDrop(imageFiles, view, pos ?? view.state.selection.main.head);
      return true;
    },
    paste: (event: ClipboardEvent, view: EditorView) => {
      const items = event.clipboardData?.items;
      if (!items) return false;

      const imageItems = Array.from(items).filter(item =>
        item.type.startsWith('image/')
      );

      if (imageItems.length === 0) return false;

      event.preventDefault();
      handleMediaPaste(imageItems, view);
      return true;
    }
  });

  const tabHandler = EditorView.domEventHandlers({
    keydown: (event: KeyboardEvent, view: EditorView) => {
      if (event.key !== 'Tab') return false;

      event.preventDefault();

      if (event.shiftKey) {
        if (indentSelectedLines(view, true)) return true;
        return indentWithTab.run ? indentWithTab.run(view) : false;
      }

      if (indentSelectedLines(view)) return true;
      return indentWithTab.run ? indentWithTab.run(view) : false;
    }
  });

  return [
    basicSetup,
    EditorView.lineWrapping,
    oneDark,
    markdown({ base: markdownLanguage, codeLanguages: languages }),
    // Autocomplete for leedoc markers: component names in <!-- embed: ... -->,
    // page paths in [[wikilinks]], marker snippets on `<`/`[ld`/Ctrl+Space,
    // and a `/` block-inserter on empty lines. (basicSetup already includes
    // the autocompletion UI; these register the completion source.)
    // Registered for BOTH markdown and html: lang-markdown nests the HTML
    // parser inside HTML blocks, so the cursor inside `<!-- embed: ... -->`
    // (or after an unclosed `<!--`) resolves to htmlLanguage, not markdown.
    ...(() => {
      const source = leedocCompletionSource({
        getComponentNames: () => currentComponentsData.map(c => c.name),
        getPagePaths: () => currentFilesData.map(f => f.path),
      });
      return [
        markdownLanguage.data.of({ autocomplete: source }),
        htmlLanguage.data.of({ autocomplete: source }),
      ];
    })(),
    // Double-click (or Cmd/Ctrl+Click) an embed directive's name opens the component
    embedLinks((name) => { void loadComponent(name); }),
    // Labelled fold placeholder for <!-- collapse --> blocks (auto-folded on load
    // via autoFoldCollapsedBlocks); folding itself comes from basicSetup.
    collapseFold(),
    // Darker bordered box on fenced code blocks + per-block line numbers
    // (restarting at 1 for every block), so code stands apart from prose
    // while scrolling busy documents. basicSetup's absolute line-number
    // gutter is hidden for the main editor in styles.css — numbers matter
    // when eyeballing a script, not prose.
    codeBlockHighlight(),
    codeBlockLineNumbers(),
    // Double-click on a code block's opening fence line: opens the block's
    // body in a focused centered modal editor (easy Select-All + bulk edit /
    // review of large blocks).
    codeBlockEditGesture((openLineNo) => openCodeBlockModal(openLineNo)),
    // Unified search: match highlights + Mod-f/Mod-g/F3 bindings (overrides
    // basicSetup's built-in search panel so Ctrl/Cmd+F opens OUR find bar)
    ...paneSearch.editorExtensions(),
    keymap.of([
      { key: 'Tab', run: (view) => indentSelectedLines(view) },
      { key: 'Shift-Tab', run: (view) => indentSelectedLines(view, true) },
      indentWithTab,
    ]),
    saveKeymap,
    updateListener,
    scrollListener,
    tabHandler,
    mediaHandler,
  ];
}

// Build a fresh EditorState (no prior undo history) for the given content.
function createFreshEditorState(content: string): EditorState {
  return EditorState.create({ doc: content, extensions: buildEditorExtensions() });
}

// Replace the editor's state with a brand-new one so undo history is reset.
// Use this when loading a different file from server, or clearing the editor.
function resetEditorContent(content: string): void {
  if (!editorView) return;
  isRestoringTabState = true;
  editorView.setState(createFreshEditorState(content));
  autoFoldCollapsedBlocks(editorView);
  isRestoringTabState = false;
  // setState replaces the whole state (no update event), so recompute
  // search highlights if the find bar is open on the source scope
  paneSearch.refresh();
}

// Initialize CodeMirror editor
function initCodeMirror(content = ''): void {
  if (editorView) {
    editorView.destroy();
  }

  editorView = new EditorView({
    state: createFreshEditorState(content),
    parent: codemirrorContainer,
  });
  autoFoldCollapsedBlocks(editorView);
}

// Guards against opening a second code-block-edit modal on top of another.
let codeBlockModalOpen = false;

/**
 * Open the body of a fenced code block in a focused, centered modal code editor.
 * Makes big embedded scripts easy to bulk-edit: Ctrl/Cmd+A selects the whole
 * block, and the isolated view makes issues (e.g. an unbound var) easy to spot.
 * Edits are written back to the source on Apply / click-outside, and discarded on
 * Cancel / Esc. Triggered by double-clicking the block's opening fence line.
 */
function openCodeBlockModal(openLineNo: number): void {
  if (!editorView || codeBlockModalOpen) return;
  const block = findCodeBlockAtFence(editorView.state, openLineNo);
  if (!block) return;

  codeBlockModalOpen = true;
  const original = block.hasBody ? editorView.state.sliceDoc(block.bodyFrom, block.bodyTo) : '';

  const overlay = document.createElement('div');
  overlay.className = 'modal code-block-edit-modal';

  const content = document.createElement('div');
  content.className = 'modal-content';
  overlay.appendChild(content);

  const header = document.createElement('div');
  header.className = 'modal-header';
  const title = document.createElement('span');
  title.textContent = `Edit code block${block.lang ? ` · ${block.lang}` : ''}`;
  const hint = document.createElement('span');
  hint.className = 'code-block-edit-hint';
  hint.textContent = `${navigator.platform.includes('Mac') ? '⌘' : 'Ctrl'}+A to select all · Esc to cancel`;
  header.append(title, hint);
  content.appendChild(header);

  const editorHost = document.createElement('div');
  editorHost.className = 'code-block-edit-body';
  content.appendChild(editorHost);

  const footer = document.createElement('div');
  footer.className = 'modal-footer';
  const cancelBtn = document.createElement('button');
  cancelBtn.textContent = 'Cancel';
  const applyBtn = document.createElement('button');
  applyBtn.className = 'btn-primary';
  applyBtn.textContent = 'Apply';
  footer.append(cancelBtn, applyBtn);
  content.appendChild(footer);

  document.body.appendChild(overlay);

  // Nested, isolated editor holding just the block body (its own undo history).
  const modalEditor = new EditorView({
    state: EditorState.create({
      doc: original,
      extensions: [basicSetup, oneDark, EditorView.lineWrapping],
    }),
    parent: editorHost,
  });
  modalEditor.focus();

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKeydown, true);
    modalEditor.destroy();
    overlay.remove();
    codeBlockModalOpen = false;
    editorView?.focus();
  };

  const apply = (): void => {
    if (!editorView) { close(); return; }
    const newText = modalEditor.state.doc.toString();
    // Re-resolve geometry: the source doc is untouched while the modal is open,
    // but reading fresh keeps this correct if that ever changes.
    const fresh = findCodeBlockAtFence(editorView.state, openLineNo) ?? block;
    if (newText !== original) {
      const insert = fresh.hasBody ? newText : (newText ? `\n${newText}` : '');
      editorView.dispatch({ changes: { from: fresh.bodyFrom, to: fresh.bodyTo, insert } });
      autoFoldCollapsedBlocks(editorView);
      paneSearch.refresh();
    }
    close();
  };

  // Capture-phase Escape so it closes THIS modal (and stops the global handler's
  // closeAllModals, which doesn't know about this dynamically-created overlay).
  const onKeydown = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      close();
    }
  };
  document.addEventListener('keydown', onKeydown, true);

  // Click-outside applies (per the "click out → it updates" request). Uses
  // mousedown per the popup-dismissal convention so a text-drag ending outside
  // the panel doesn't fire.
  overlay.addEventListener('mousedown', (e) => {
    if (e.target === overlay) apply();
  });
  cancelBtn.addEventListener('click', close);
  applyBtn.addEventListener('click', apply);
}

// ========================================
// TOPBAR / SWITCHER HELPERS
// ========================================

function setTopbarVisible(visible: boolean): void {
  topbarEl?.classList.toggle('hidden', !visible);
  if (visible) {
    localStorage.removeItem('leedoc_topbar_hidden');
  } else {
    localStorage.setItem('leedoc_topbar_hidden', 'true');
  }
}

function toggleTopbarVisibility(): void {
  const isVisible = !(topbarEl?.classList.contains('hidden'));
  setTopbarVisible(!isVisible);
}

// Refresh active-file highlight in both tree renderers (mounted in the switcher).
function refreshTreeSelection(): void {
  fileTreeRenderer?.refreshSelection();
  componentTreeRenderer?.refreshSelection();
}

// ========================================
// RECENT FILES (tracks the order files / components have been opened)
// ========================================

const RECENT_FILES_KEY = 'leedoc_recent_files';
const RECENT_FILES_MAX = 30;

function loadRecentPaths(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_FILES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((p): p is string => typeof p === 'string') : [];
  } catch {
    return [];
  }
}

function trackRecentPath(path: string): void {
  if (!path) return;
  const recent = loadRecentPaths().filter(p => p !== path);
  recent.unshift(path);
  while (recent.length > RECENT_FILES_MAX) recent.pop();
  try {
    localStorage.setItem(RECENT_FILES_KEY, JSON.stringify(recent));
  } catch {
    // ignore storage errors (quota exceeded, private mode, etc.)
  }
}

// Order `items` so files in `recent` come first (in MRU order), then everything
// else alphabetically by path. Items not present anywhere are appended at the
// end. Used by the modal's "List" view when search is empty.
function sortByRecent(items: PsListItem[]): PsListItem[] {
  const recent = loadRecentPaths();
  const recentIndex = new Map<string, number>();
  recent.forEach((p, i) => recentIndex.set(p, i));

  const withRecent: PsListItem[] = [];
  const rest: PsListItem[] = [];
  for (const item of items) {
    if (recentIndex.has(item.path)) withRecent.push(item);
    else rest.push(item);
  }
  withRecent.sort((a, b) => (recentIndex.get(a.path)! - recentIndex.get(b.path)!));
  rest.sort((a, b) => a.path.localeCompare(b.path));
  return [...withRecent, ...rest];
}

// ========================================
// VIEW MODE (tree vs flat list) — per tab, persisted
// ========================================

type PsViewMode = 'tree' | 'list';
const VIEW_MODE_KEY_FILES = 'leedoc_ps_view_files';
const VIEW_MODE_KEY_COMPONENTS = 'leedoc_ps_view_components';

function getViewMode(tab: 'files' | 'components'): PsViewMode {
  const key = tab === 'files' ? VIEW_MODE_KEY_FILES : VIEW_MODE_KEY_COMPONENTS;
  return (localStorage.getItem(key) as PsViewMode) === 'list' ? 'list' : 'tree';
}

function setViewMode(tab: 'files' | 'components', mode: PsViewMode): void {
  const key = tab === 'files' ? VIEW_MODE_KEY_FILES : VIEW_MODE_KEY_COMPONENTS;
  localStorage.setItem(key, mode);
}

// Re-render the flat list for whichever tab is active, but only if the modal
// is open (cheap no-op the rest of the time). Called after file mutations so
// recent-files / search ranking stay in sync with the data.
function refreshFlatListIfVisible(): void {
  const modal = document.getElementById(MODAL_IDS.PROJECT_SWITCHER);
  if (!modal || modal.classList.contains('hidden')) return;
  if (psActiveTab === 'files' || psActiveTab === 'components') {
    renderProjectSwitcherList(psSearchInput?.value ?? '');
  }
}

// Handle file/folder drop for move operations
async function handleFileDrop(sourcePath: string, sourceType: 'file' | 'folder', targetFolder: string): Promise<void> {
  try {
    if (sourceType === 'file') {
      // Move single file
      const newPath = getNewPath(sourcePath, targetFolder);
      if (newPath === sourcePath) return;

      await storage.renameFile(sourcePath, newPath);

      // Update tab manager with new path
      tabManager.renameTab(sourcePath, newPath);

      // Update current file reference if moved
      if (currentFile === sourcePath) {
        currentFile = newPath;
        updateUrlWithFile(newPath);
      }
    } else {
      // Move folder = move all files inside
      const filesToMove = fileTreeRenderer?.getFilesUnderFolder(sourcePath) || [];
      if (filesToMove.length === 0) return;

      const folderName = sourcePath.split('/').pop()!;
      const newFolderPath = targetFolder ? `${targetFolder}/${folderName}` : folderName;

      for (const filePath of filesToMove) {
        const relativePath = filePath.substring(sourcePath.length + 1);
        const newFilePath = `${newFolderPath}/${relativePath}`;
        await storage.renameFile(filePath, newFilePath);

        // Update tab manager with new path
        tabManager.renameTab(filePath, newFilePath);

        if (currentFile === filePath) {
          currentFile = newFilePath;
          updateUrlWithFile(newFilePath);
        }
      }

      // Update expanded folders
      fileTreeRenderer?.expanded.delete(sourcePath);
      fileTreeRenderer?.expanded.add(newFolderPath);
    }

    await loadFiles();
  } catch (err) {
    console.error('Failed to move:', err);
    alert(`Failed to move: ${(err as Error).message}`);
  }
}

// Update URL with current file
function updateUrlWithFile(path: string): void {
  const url = new URL(window.location.href);
  url.searchParams.set('file', path);
  url.hash = '';
  window.history.pushState({}, '', url.toString());
}

function getDuplicatePath(originalPath: string, extension: string): string | null {
  const parts = originalPath.split('/');
  const filename = parts.pop();
  if (!filename || !filename.endsWith(extension)) return null;

  const baseName = filename.slice(0, -extension.length);
  const defaultName = `${baseName}-copy`;
  const input = prompt('Duplicate as:', defaultName);
  if (input === null) return null;

  const sanitizedName = sanitizeFilename(input.trim());
  if (!sanitizedName) {
    alert('Invalid name');
    return null;
  }

  const parentPath = parts.join('/');
  return parentPath ? `${parentPath}/${sanitizedName}${extension}` : `${sanitizedName}${extension}`;
}

async function duplicateSourceFile(path: string): Promise<void> {
  try {
    const newPath = getDuplicatePath(path, '.src.md');
    if (!newPath) return;

    const data = await storage.readFile(path) as FileResponse;
    await storage.createFile(newPath, data.content);
    await loadFiles();
    await loadFile(newPath);
  } catch (err) {
    console.error('Failed to duplicate file:', err);
    alert(`Failed to duplicate file: ${(err as Error).message}`);
  }
}

async function duplicateComponentFile(path: string): Promise<void> {
  try {
    const newPath = getDuplicatePath(path, '.md');
    if (!newPath) return;

    const data = await storage.readComponent(path) as ComponentResponse;
    await storage.createComponent(newPath, data.content);
    await loadComponents();
    await loadComponent(newPath);
  } catch (err) {
    console.error('Failed to duplicate component:', err);
    alert(`Failed to duplicate component: ${(err as Error).message}`);
  }
}

// Handle context menu action
function handleContextMenuAction(action: string, targetPath: string | null, targetType: 'file' | 'folder' | 'empty'): void {
  switch (action) {
    case 'new-file':
      fileTreeRenderer?.startInlineCreate('file', targetType === 'folder' ? targetPath || '' : '');
      break;
    case 'new-folder':
      fileTreeRenderer?.startInlineCreate('folder', targetType === 'folder' ? targetPath || '' : '');
      break;
    case 'rename':
      if (targetPath) {
        fileTreeRenderer?.startInlineRename(targetPath, targetType as 'file' | 'folder');
      }
      break;
    case 'duplicate':
      if (targetPath && targetType === 'file') {
        void duplicateSourceFile(targetPath);
      }
      break;
    case 'delete':
      if (targetPath) {
        if (targetType === 'file') {
          showDeleteModal('file', targetPath);
        } else if (targetType === 'folder') {
          deleteFolderWithConfirm(targetPath);
        }
      }
      break;
  }
}

// Delete folder with confirmation
async function deleteFolderWithConfirm(folderPath: string): Promise<void> {
  const files = fileTreeRenderer?.getFilesUnderFolder(folderPath) || [];

  if (files.length === 0) {
    // Empty folder exists only in the tree's `pending` set (no files persist it).
    // Drop it from pending and re-render so it disappears immediately.
    if (fileTreeRenderer?.pending.has(folderPath)) {
      fileTreeRenderer.pending.delete(folderPath);
    }
    fileTreeRenderer?.expanded.delete(folderPath);
    fileTreeRenderer?.render(currentFilesData);
    return;
  }

  // Confirm deletion
  const confirmed = confirm(
    `Delete folder "${folderPath}" and all ${files.length} file(s) inside?\n\nThis action cannot be undone.`
  );

  if (!confirmed) return;

  try {
    // Delete all files in the folder
    for (const filePath of files) {
      // Create pre-delete snapshot
      await storage.backend.createSnapshot(filePath, 'pre_delete');
      await storage.deleteFile(filePath);

      if (currentFile === filePath) {
        clearEditor();
      }
    }

    // Remove from expanded folders
    fileTreeRenderer?.expanded.delete(folderPath);

    await loadFiles();
  } catch (err) {
    console.error('Failed to delete folder:', err);
    alert(`Failed to delete folder: ${(err as Error).message}`);
  }
}

// Handle component drop for move operations
async function handleComponentDrop(sourcePath: string, sourceType: 'file' | 'folder', targetFolder: string): Promise<void> {
  try {
    if (sourceType === 'file') {
      const newPath = getNewPath(sourcePath, targetFolder);
      if (newPath === sourcePath) return;

      await storage.renameComponent(sourcePath, newPath);

      // Update tab manager with new path
      tabManager.renameTab(`_components/${sourcePath}`, `_components/${newPath}`);

      if (currentFile === `_components/${sourcePath}`) {
        currentFile = `_components/${newPath}`;
        updateUrlWithFile(currentFile);
      }
    } else {
      // Move folder = move all components inside
      const componentsToMove = componentTreeRenderer?.getFilesUnderFolder(sourcePath) || [];
      if (componentsToMove.length === 0) return;

      const folderName = sourcePath.split('/').pop()!;
      const newFolderPath = targetFolder ? `${targetFolder}/${folderName}` : folderName;

      for (const componentPath of componentsToMove) {
        const relativePath = componentPath.substring(sourcePath.length + 1);
        const newComponentPath = `${newFolderPath}/${relativePath}`;
        await storage.renameComponent(componentPath, newComponentPath);

        // Update tab manager with new path
        tabManager.renameTab(`_components/${componentPath}`, `_components/${newComponentPath}`);

        if (currentFile === `_components/${componentPath}`) {
          currentFile = `_components/${newComponentPath}`;
          updateUrlWithFile(currentFile);
        }
      }

      componentTreeRenderer?.expanded.delete(sourcePath);
      componentTreeRenderer?.expanded.add(newFolderPath);
    }

    await loadComponents();
  } catch (err) {
    console.error('Failed to move component:', err);
    alert(`Failed to move component: ${(err as Error).message}`);
  }
}

// Component context menu action handler
function handleComponentContextMenuAction(action: string, targetPath: string | null, targetType: 'file' | 'folder' | 'empty'): void {
  switch (action) {
    case 'new-file':
      componentTreeRenderer?.startInlineCreate('file', targetType === 'folder' ? targetPath || '' : '');
      break;
    case 'new-folder':
      componentTreeRenderer?.startInlineCreate('folder', targetType === 'folder' ? targetPath || '' : '');
      break;
    case 'rename':
      if (targetPath) {
        componentTreeRenderer?.startInlineRename(targetPath, targetType as 'file' | 'folder');
      }
      break;
    case 'duplicate':
      if (targetPath && targetType === 'file') {
        void duplicateComponentFile(targetPath);
      }
      break;
    case 'delete':
      if (targetPath) {
        if (targetType === 'file') {
          showDeleteModal('component', targetPath);
        } else if (targetType === 'folder') {
          deleteComponentFolderWithConfirm(targetPath);
        }
      }
      break;
  }
}

async function deleteComponentFolderWithConfirm(folderPath: string): Promise<void> {
  // Check if it's a pending folder
  if (componentTreeRenderer?.pending.has(folderPath)) {
    componentTreeRenderer.pending.delete(folderPath);
    componentTreeRenderer.render(currentComponentsData.map(c => ({ path: c.name })));
    return;
  }

  const componentsInFolder = componentTreeRenderer?.getFilesUnderFolder(folderPath) || [];
  if (componentsInFolder.length === 0) {
    alert('Folder is empty');
    return;
  }

  const confirmed = confirm(`Delete folder "${folderPath}" and all ${componentsInFolder.length} component(s) inside?`);
  if (!confirmed) return;

  try {
    for (const componentPath of componentsInFolder) {
      await storage.deleteComponent(componentPath);
    }
    componentTreeRenderer?.expanded.delete(folderPath);
    await loadComponents();
  } catch (err) {
    console.error('Failed to delete component folder:', err);
    alert(`Failed to delete folder: ${(err as Error).message}`);
  }
}

// Wire up the in-modal tree controls: view-mode toggle and the
// "New File/Folder" / "New Component/Folder" footer buttons. Context menu,
// drag-and-drop and inline create/rename are wired by TreeRenderer itself.
function setupProjectSwitcherTreeListeners(): void {
  psViewButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const mode = (btn.dataset.view as PsViewMode) || 'tree';
      if (psActiveTab !== 'files' && psActiveTab !== 'components') return;
      setViewMode(psActiveTab, mode);
      updateProjectSwitcherTabUI();
      renderProjectSwitcherList(psSearchInput?.value ?? '');
      psSearchInput?.focus();
    });
  });

  btnPsNewFile?.addEventListener('click', () => {
    if (getViewMode('files') !== 'tree') {
      setViewMode('files', 'tree');
      updateProjectSwitcherTabUI();
      renderProjectSwitcherList('');
    }
    fileTreeRenderer?.startInlineCreate('file', '');
  });
  btnPsNewFolder?.addEventListener('click', () => {
    if (getViewMode('files') !== 'tree') {
      setViewMode('files', 'tree');
      updateProjectSwitcherTabUI();
      renderProjectSwitcherList('');
    }
    fileTreeRenderer?.startInlineCreate('folder', '');
  });
  btnPsNewComponent?.addEventListener('click', () => {
    if (getViewMode('components') !== 'tree') {
      setViewMode('components', 'tree');
      updateProjectSwitcherTabUI();
      renderProjectSwitcherList('');
    }
    componentTreeRenderer?.startInlineCreate('file', '');
  });
  btnPsNewComponentFolder?.addEventListener('click', () => {
    if (getViewMode('components') !== 'tree') {
      setViewMode('components', 'tree');
      updateProjectSwitcherTabUI();
      renderProjectSwitcherList('');
    }
    componentTreeRenderer?.startInlineCreate('folder', '');
  });
}

// ========================================
// EDIT MODE AND PANEL FUNCTIONS
// ========================================

// Reset panel widths to equal distribution (clears inline styles set by user resizing)
function resetPanelWidths(): void {
  editorPanel.style.width = '';
  markdownPanel.style.width = '';
  scriptsPanel.style.width = '';
}

// Toggle edit mode
function toggleEditMode(): void {
  editMode = !editMode;

  if (editMode) {
    editorPanel.classList.remove('hidden');
    editorResize.classList.remove('hidden');
    btnToggleEdit.classList.add('active');
    btnToggleEdit.textContent = 'Editing';
    initCodeMirror(sourceContent);
  } else {
    editorPanel.classList.add('hidden');
    editorResize.classList.add('hidden');
    btnToggleEdit.classList.remove('active');
    btnToggleEdit.textContent = 'Edit';
    if (editorView) {
      editorView.destroy();
      editorView = null;
    }
  }
  resetPanelWidths();
}

/** Which panels the workspace currently shows (windowed mode stores this per window). */
function getPanelState(): PanelState {
  return {
    edit: editMode,
    docs: toggleDocs.checked,
    scripts: toggleScripts.checked,
    vars: toggleVariables.checked,
    editorWidth: editorPanel.style.width || undefined,
    scriptsWidth: scriptsPanel.style.width || undefined,
    varsHeight: variablesPanel.style.height || undefined,
  };
}

/** Restore a window's remembered panel layout (windowed mode, on activation). */
function applyPanelState(state: PanelState): void {
  toggleDocs.checked = state.docs;
  toggleScripts.checked = state.scripts;
  toggleVariables.checked = state.vars;
  if (editMode !== state.edit) toggleEditMode();
  updatePanelVisibility();
  // The toggles above reset the dragged sizes; put this window's back
  editorPanel.style.width = state.editorWidth ?? '';
  scriptsPanel.style.width = state.scriptsWidth ?? '';
  variablesPanel.style.height = state.varsHeight ?? '';
}

// Update panel visibility based on checkbox states
function updatePanelVisibility(): void {
  // Variables panel - only show if checked AND has variables
  const hasVars = Object.keys(staticVariables).length > 0 || Object.keys(dynamicVariables).length > 0;
  if (toggleVariables.checked && hasVars) {
    variablesPanel.classList.remove('hidden');
  } else {
    variablesPanel.classList.add('hidden');
  }

  // Documentation panel
  if (toggleDocs.checked) {
    markdownPanel.classList.remove('hidden');
  } else {
    markdownPanel.classList.add('hidden');
  }

  // Scripts panel
  if (toggleScripts.checked) {
    scriptsPanel.classList.remove('hidden');
    panelsResizeHandle.classList.remove('panel-hidden');
  } else {
    scriptsPanel.classList.add('hidden');
    panelsResizeHandle.classList.add('panel-hidden');
  }

  // If docs is hidden, also hide the resize handle
  if (!toggleDocs.checked) {
    panelsResizeHandle.classList.add('panel-hidden');
  }

  resetPanelWidths();
}

// Save file. Returns false when the save failed (the user is warned).
async function saveFile(force = false): Promise<boolean> {
  if (!currentFile) return true;
  if (!force && !hasUnsavedChanges) return true;

  const path = currentFile;
  const content = sourceContent;
  setSaveStatus('saving');

  try {
    const data = await storage.saveFile(path, content) as SaveResponse;

    if (!data.success) {
      throw new Error('The server did not confirm the save');
    }

    clearUnsavedBackup(unsavedBackupKeyFor(path));
    // The user may have switched files or kept typing while the request ran
    if (currentFile !== path) {
      if (tabManager.getTab(path)?.sourceContent === content) {
        tabManager.updateTabState(path, { hasUnsavedChanges: false, lastSaveTime: Date.now() });
        tabBar?.render();
      }
      return true;
    }
    if (sourceContent === content) {
      hasUnsavedChanges = false;
    }
    lastSaveTime = Date.now();
    lastSavedHash = data.hash || await computeHash(content);
    setSaveStatus(hasUnsavedChanges ? 'modified' : 'synced');

    // Update tab state after save
    tabManager.updateActiveState({ hasUnsavedChanges, lastSaveTime, lastSavedHash });
    tabBar?.render();
    return true;
  } catch (err) {
    console.error('Save failed:', err);
    if (currentFile === path) setSaveStatus('error');
    reportSaveFailure(path, content, err);
    return false;
  }
}

// --- Project-wide search & component usages ---

const projectSearch = new ProjectSearch({
  getContents: getProjectContentsForSearch,
  onOpen: (path, match, query, caseSensitive) => {
    void openProjectSearchResult(path, match, query, caseSensitive);
  },
});

/** Stored project contents, overlaid with unsaved text from open tabs */
async function getProjectContentsForSearch(): Promise<{ path: string; content: string }[]> {
  const byPath = new Map<string, string>();
  for (const f of await storage.getAllContents()) byPath.set(f.path, f.content);
  for (const tab of tabManager.tabs) {
    if (tab.sourceContent && byPath.has(tab.path)) byPath.set(tab.path, tab.sourceContent);
  }
  if (currentFile && byPath.has(currentFile)) byPath.set(currentFile, sourceContent);
  return [...byPath].map(([path, content]) => ({ path, content }));
}

function openProjectSearch(initialQuery?: string): void {
  // Prefill from the editor selection, like Ctrl+F does
  let query = initialQuery;
  if (query === undefined && editorView) {
    const sel = editorView.state.selection.main;
    const text = editorView.state.sliceDoc(sel.from, sel.to);
    if (text && !text.includes('\n')) query = text;
  }
  modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);
  modalManager.show(MODAL_IDS.PROJECT_SEARCH);
  void projectSearch.open(query);
}

function openComponentUsages(componentPath: string): void {
  modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);
  modalManager.show(MODAL_IDS.PROJECT_SEARCH);
  void projectSearch.showUsages(componentPath);
}

/**
 * Load the file containing a search hit and jump to it: in the source editor
 * when it's open (exact match selected), otherwise via the preview find bar.
 */
async function openProjectSearchResult(
  path: string,
  match: ProjectSearchMatch,
  query: string,
  caseSensitive: boolean
): Promise<void> {
  modalManager.hide(MODAL_IDS.PROJECT_SEARCH);
  if (path !== currentFile) {
    if (path.startsWith('_components/')) await loadComponent(path);
    else await loadFile(path);
    refreshTreeSelection();
  }
  if (currentFile !== path) return; // load failed

  // Without a visible preview the editor is the only place to show the hit
  if (!editMode && !toggleDocs.checked) toggleEditMode();

  if (editMode && editorView) {
    const view = editorView;
    const pos = Math.min(match.offset, view.state.doc.length);
    // Expand any folded (collapsed) block hiding the match
    const unfold: ReturnType<typeof unfoldEffect.of>[] = [];
    foldedRanges(view.state).between(pos, pos, (from, to) => {
      unfold.push(unfoldEffect.of({ from, to }));
    });
    view.dispatch({ selection: { anchor: pos }, effects: unfold });
    // The find bar picks the first match at/after the cursor, i.e. this one
    paneSearch.open('source', { query, caseSensitive });
  } else {
    paneSearch.open('preview', { query, caseSensitive });
  }
}

// --- Save failure handling ---

let saveErrorDismissedAt = 0;
/** File/content shown in the save-error modal (for copy/download/retry) */
let saveErrorTarget: { path: string; content: string } | null = null;
/** Set right before an intentional reload so beforeunload doesn't block it */
let bypassUnloadWarning = false;
/** Don't re-open the warning more often than this after it was dismissed */
const SAVE_ERROR_REMIND_MS = 30_000;

function currentProjectIdFromUrl(): string | null {
  return new URL(window.location.href).searchParams.get(URL_PROJECT_PARAM);
}

function unsavedBackupKeyFor(path: string): string {
  return unsavedBackupKey(storage.currentBackendName, currentProjectIdFromUrl(), path);
}

function isAuthError(err: unknown): boolean {
  const status = (err as { status?: number } | null)?.status;
  if (status === 401 || status === 403) return true;
  const msg = err instanceof Error ? err.message : String(err);
  return /not authenticated|invalid token|session expired|no token|unauthori[sz]ed|\b401\b/i.test(msg);
}

/**
 * Warn loudly that a save failed. Keeps a local backup of the unsaved text
 * so it survives the reload + re-login the modal recommends.
 */
function reportSaveFailure(path: string, content: string, err: unknown): void {
  const backedUp = writeUnsavedBackup(unsavedBackupKeyFor(path), content);
  saveErrorTarget = { path, content };

  const detail = err instanceof Error ? err.message : String(err);
  setStatus('Save failed — your changes are NOT stored', 'error');

  saveErrorMessage.textContent = isAuthError(err)
    ? 'Your session has expired or you were logged out, so your latest changes could not be saved to the server.'
    : 'Your latest changes could not be saved. The server may be unreachable, or your session may have expired.';
  saveErrorFile.textContent = path;
  saveErrorDetail.textContent = detail;
  saveErrorBackupNote.classList.toggle('hidden', !backedUp);

  if (modalManager.isOpen(MODAL_IDS.SAVE_ERROR)) return;
  // Auto-save retries on every edit; don't nag on each keystroke after the
  // user explicitly dismissed the warning (the status icon stays red).
  if (Date.now() - saveErrorDismissedAt < SAVE_ERROR_REMIND_MS) return;
  modalManager.show(MODAL_IDS.SAVE_ERROR);
}

function setupSaveErrorModal(): void {
  btnSaveErrorCopy.addEventListener('click', async () => {
    if (!saveErrorTarget) return;
    try {
      await navigator.clipboard.writeText(saveErrorTarget.content);
      btnSaveErrorCopy.textContent = 'Copied!';
      setTimeout(() => { btnSaveErrorCopy.textContent = 'Copy text'; }, 2000);
    } catch {
      btnSaveErrorCopy.textContent = 'Copy failed — use Download';
    }
  });

  btnSaveErrorDownload.addEventListener('click', () => {
    if (!saveErrorTarget) return;
    const blob = new Blob([saveErrorTarget.content], { type: 'text/markdown' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = saveErrorTarget.path.split('/').pop() || 'unsaved.md';
    a.click();
    setTimeout(() => URL.revokeObjectURL(a.href), 1000);
  });

  btnSaveErrorRetry.addEventListener('click', async () => {
    const target = saveErrorTarget;
    if (!target) return;
    modalManager.hide(MODAL_IDS.SAVE_ERROR);
    // Allow the modal to re-open immediately if the retry fails too
    saveErrorDismissedAt = 0;
    if (target.path === currentFile) {
      if (await saveFile(true)) setStatus('Saved', 'success');
      return;
    }
    try {
      await storage.saveFile(target.path, target.content);
      clearUnsavedBackup(unsavedBackupKeyFor(target.path));
      if (tabManager.getTab(target.path)?.sourceContent === target.content) {
        tabManager.updateTabState(target.path, { hasUnsavedChanges: false });
      }
      tabBar?.render();
      setStatus('Saved', 'success');
    } catch (err) {
      reportSaveFailure(target.path, target.content, err);
    }
  });

  btnSaveErrorReload.addEventListener('click', () => {
    // Unsaved text is in the local backup (and offered on next open)
    bypassUnloadWarning = true;
    window.location.reload();
  });

  // Last line of defence: the browser's own "leave page?" prompt
  window.addEventListener('beforeunload', (e) => {
    if (bypassUnloadWarning) return;
    const anyUnsaved = hasUnsavedChanges || tabManager.tabs.some(t => t.hasUnsavedChanges);
    if (!anyUnsaved) return;
    e.preventDefault();
    e.returnValue = '';
  });
}

/**
 * After a fresh load of `path`, offer to restore text kept by
 * reportSaveFailure (e.g. from before an expired session + reload).
 */
function offerUnsavedBackupRestore(path: string, loadedContent: string): void {
  const key = unsavedBackupKeyFor(path);
  const backup = readUnsavedBackup(key);
  if (!backup) return;
  if (backup.content === loadedContent) {
    clearUnsavedBackup(key);
    return;
  }
  // Defer so the freshly loaded file renders before the prompt blocks
  setTimeout(() => {
    if (currentFile !== path) return;
    const when = new Date(backup.savedAt).toLocaleString();
    const restore = confirm(
      `Leedoc found unsaved changes to "${path}" from ${when} that failed to save.\n\n` +
      'Restore them now? (They will be saved to the server.)\n\n' +
      'Cancel discards the backup and keeps the version from the server.'
    );
    if (!restore) {
      clearUnsavedBackup(key);
      return;
    }
    applyRestoredContent(backup.content);
  }, 50);
}

/** Replace the current file's content with `content` as a user edit (saves) */
function applyRestoredContent(content: string): void {
  if (editMode && editorView) {
    // Goes through the editor update listener: marks modified, re-renders, saves
    editorView.dispatch({ changes: { from: 0, to: editorView.state.doc.length, insert: content } });
    return;
  }
  sourceContent = content;
  hasUnsavedChanges = true;
  setSaveStatus('modified');
  tabManager.updateActiveState({ sourceContent, hasUnsavedChanges: true });
  tabBar?.render();
  void processFile();
  void saveFile();
}

/**
 * Save the current file right away if it still has unsaved changes. Called
 * before switching files: the debounced autosave only ever saves whatever
 * file is current when it fires, so a quick edit-then-switch would otherwise
 * leave the previous file unsaved until it became active again.
 */
function flushPendingSave(): void {
  if (!currentFile || !hasUnsavedChanges) return;
  clearTimeout(saveDebounceTimer!);
  void saveFile();
}

// Debounced save
function debouncedSave(): void {
  clearTimeout(saveDebounceTimer!);
  saveDebounceTimer = window.setTimeout(() => {
    saveFile();
  }, 1000);
}

// Set save status - state can be: hidden, synced, modified, saving, error, out-of-sync
function setSaveStatus(state: SaveState, tooltip?: string): void {
  saveState = state;

  if (state === 'hidden' || !currentFile) {
    saveStatusBtn.classList.add('hidden');
    return;
  }

  saveStatusBtn.classList.remove('hidden');
  saveStatusBtn.className = 'btn-save-status ' + state;

  // Build tooltip with last save time if available
  let tooltipText = tooltip || '';
  if (!tooltipText) {
    switch (state) {
      case 'synced':
        tooltipText = lastSaveTime ? `Saved ${formatRelativeTime(lastSaveTime)}` : 'In sync';
        break;
      case 'modified':
        tooltipText = 'Unsaved changes - click to save';
        break;
      case 'saving':
        tooltipText = 'Saving...';
        break;
      case 'error':
        tooltipText = 'Save failed - click to retry';
        break;
      case 'out-of-sync':
        tooltipText = 'Out of sync with server - click to save';
        break;
    }
  }
  saveStatusBtn.title = tooltipText;
}

// Format relative time (e.g., "2 min ago", "just now")
function formatRelativeTime(timestamp: number): string {
  const seconds = Math.floor((Date.now() - timestamp) / 1000);

  if (seconds < 10) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;

  // Fall back to absolute time
  return new Date(timestamp).toLocaleTimeString();
}

// --- Tab system helpers ---

/**
 * Lightweight preview of a non-active tab from its cached source (weave +
 * markdown only, no enrichers). Used by windowed mode for windows whose file
 * hasn't been rendered live since the mode was switched on.
 */
async function renderDetachedPreview(path: string): Promise<string | null> {
  const tab = tabManager.getTab(path);
  if (!tab?.sourceContent) return null;
  try {
    const components = await loadAllComponents();
    const allPagePaths = currentFilesData
      .map(f => f.path)
      .filter(p => p.endsWith('.src.md') && !p.startsWith('_components/'));
    const result = weave(tab.sourceContent, {
      variables: { ...tab.staticVariables, ...tab.dynamicVariables, ...tab.userVariables },
      components,
      sourcePath: path,
      allPaths: allPagePaths,
    });
    if (!result.success) return null;
    return marked.parse(rewriteMediaUrls(result.output)) as string;
  } catch {
    return null;
  }
}

function snapshotCurrentFileState(): TabFileState | null {
  if (!currentFile) return null;
  // Windowed mode: the preview still shows this file — keep a copy for its window
  desktop?.captureWorkspace(currentFile);
  return {
    path: currentFile,
    isComponent: currentFile.startsWith('_components/'),
    sourceContent,
    hasUnsavedChanges,
    lastSavedHash,
    lastSaveTime,
    userVariables: { ...userVariables },
    staticVariables: { ...staticVariables },
    dynamicVariables: { ...dynamicVariables },
    editorScrollTop: editorView?.scrollDOM?.scrollTop ?? 0,
    editorCursorPos: editorView?.state?.selection?.main?.head ?? 0,
    activeScriptTab,
    checklistEnabled,
    checklistSlugs: [...checklistSlugs],
    // Capture the full CodeMirror state (including its undo/redo stack) so
    // that switching back to this tab restores history scoped to this file.
    editorState: editorView?.state ?? null,
  };
}

/**
 * Drop the Documentation panel back to rendered-preview mode. Called on every
 * file/tab switch so the Markdown-source view is scoped to the page that
 * actually has it toggled on.
 */
function resetMarkdownSourceView(): void {
  if (!markdownSourceView) return;
  markdownSourceView = false;
  btnViewMarkdown.classList.remove('active');
}

function restoreFileState(state: TabFileState): void {
  resetMarkdownSourceView();
  // Anchor map belongs to the outgoing file; drop it until the next render
  scrollSync.invalidate();
  currentFile = state.path;
  sourceContent = state.sourceContent;
  hasUnsavedChanges = state.hasUnsavedChanges;
  lastSavedHash = state.lastSavedHash;
  lastSaveTime = state.lastSaveTime;
  userVariables = { ...state.userVariables };
  staticVariables = { ...state.staticVariables };
  dynamicVariables = { ...state.dynamicVariables };
  activeScriptTab = state.activeScriptTab;

  // Restore checklist state
  checklistEnabled = state.checklistEnabled;
  checklistSlugs = [...(state.checklistSlugs ?? [])];
  checklistEnricher.setEnabled(checklistEnabled);
  btnChecklist.classList.toggle('active', checklistEnabled);
  markdownOutput.classList.toggle('checklist-mode', checklistEnabled);

  // Sync variable system, replaying the tab's user-edited values on top of
  // the frontmatter defaults so scripts keep reflecting the user's overrides.
  variableSystem.restoreVariables(staticVariables, dynamicVariables, userVariables);
  variableSystem.clearAllBlockOverrides();

  // Update editor content if in edit mode (guard to prevent save trigger).
  // Prefer restoring the cached EditorState so the file's own undo/redo
  // history comes back with it; fall back to a fresh state for placeholders.
  if (editMode && editorView) {
    isRestoringTabState = true;
    const cachedState = state.editorState as EditorState | null | undefined;
    if (cachedState) {
      editorView.setState(cachedState);
    } else {
      editorView.setState(createFreshEditorState(sourceContent));
      editorView.dispatch({
        selection: { anchor: Math.min(state.editorCursorPos, sourceContent.length) }
      });
      autoFoldCollapsedBlocks(editorView);
    }
    editorView.scrollDOM.scrollTop = state.editorScrollTop;
    isRestoringTabState = false;
  }

  // Update UI
  setSaveStatus(hasUnsavedChanges ? 'modified' : 'synced');
  updateCurrentFileDisplay(state.path);

  // Handle variables panel visibility
  if (state.isComponent) {
    variablesPanel.classList.add('hidden');
  } else {
    buildVariablesForm();
  }

  // Re-process (weave/tangle/render) from cached content
  processFile();
}

function updateCurrentFileDisplay(path: string): void {
  updatePageTitle();

  // Update URL
  const url = new URL(window.location.href);
  url.searchParams.set('file', path);
  window.history.pushState({}, '', url.toString());
}

async function handleTabClose(path: string): Promise<void> {
  const tab = tabManager.getTab(path);
  if (!tab) return;

  // Auto-save if unsaved changes. If that fails, keep the tab open so the
  // unsaved text isn't thrown away (the save-error modal explains why).
  if (tab.hasUnsavedChanges) {
    if (path === currentFile) {
      if (!await saveFile()) return;
    } else {
      // Save non-active tab directly
      try {
        await storage.saveFile(path, tab.sourceContent);
        clearUnsavedBackup(unsavedBackupKeyFor(path));
      } catch (err) {
        console.error('Failed to save tab before closing:', err);
        reportSaveFailure(path, tab.sourceContent, err);
        return;
      }
    }
  }

  const nextPath = tabManager.closeTab(path);
  tabBar?.render();

  if (path === currentFile) {
    // Clear currentFile so loadFile/loadComponent won't snapshot the closed file
    currentFile = null;
    if (nextPath) {
      // Peek at the next tab's state without changing _activeTab
      const nextState = tabManager.getTab(nextPath);
      if (nextState && nextState.sourceContent) {
        // Has real content - activate and restore from cache
        tabManager.activateTab(nextPath);
        restoreFileState(nextState);
      } else {
        // Placeholder - load from server (loadFile/loadComponent handles activation)
        if (nextPath.startsWith('_components/')) {
          await loadComponent(nextPath);
        } else {
          await loadFile(nextPath);
        }
      }
      tabBar?.render();
      refreshTreeSelection();
    } else {
      clearEditor();
      void openProjectSwitcher('files');
    }
  }

  persistTabs();
}

async function handleTabCloseAllExcept(keepPath: string): Promise<void> {
  // Save any unsaved tabs before closing; abort if any save fails so no
  // unsaved text is discarded
  for (const tab of tabManager.tabs) {
    if (tab.path !== keepPath && tab.hasUnsavedChanges) {
      try {
        await storage.saveFile(tab.path, tab.sourceContent);
        clearUnsavedBackup(unsavedBackupKeyFor(tab.path));
      } catch (err) {
        console.error('Failed to save tab:', err);
        reportSaveFailure(tab.path, tab.sourceContent, err);
        return;
      }
    }
  }

  tabManager.closeAllExcept(keepPath);
  tabBar?.render();
  persistTabs();
}

function persistTabs(): void {
  try {
    const data = tabManager.serialize();
    localStorage.setItem('leedoc_tabs', JSON.stringify(data));
  } catch {
    // localStorage might be full or unavailable
  }
}

// --- End tab system helpers ---

/**
 * Navigate to a resolved inter-page link from the rendered markdown.
 * `newTab` is reserved for future per-tab loading behaviour; today both
 * single- and multi-file modes route through loadFile() which will open or
 * activate the appropriate tab.
 */
async function handlePageLinkNavigation(
  path: string,
  opts: { newTab: boolean; anchor: string | null }
): Promise<void> {
  void opts.newTab;
  await loadFile(path);
  if (opts.anchor) {
    requestAnimationFrame(() => {
      document.getElementById(opts.anchor!)?.scrollIntoView({ block: 'start' });
    });
  }
}

/**
 * Click handler for unresolved (broken) inter-page links. Opens the
 * Create File modal with the category + filename pre-filled from the link
 * target so the user can create the missing page in one step.
 */
function openCreatePageForBrokenLink(href: string): void {
  // Strip anchor + leading ./
  const cleaned = href.split('#')[0].replace(/^\.\//, '');
  if (!cleaned) return;

  // Resolve against the current file's directory if the href is relative.
  let resolved = cleaned;
  if (currentFile && !cleaned.startsWith('/')) {
    const fromDir = currentFile.includes('/')
      ? currentFile.slice(0, currentFile.lastIndexOf('/'))
      : '';
    const parts = (fromDir ? fromDir.split('/') : []);
    for (const seg of cleaned.split('/')) {
      if (seg === '..') parts.pop();
      else if (seg !== '.' && seg !== '') parts.push(seg);
    }
    resolved = parts.join('/');
  } else if (cleaned.startsWith('/')) {
    resolved = cleaned.slice(1);
  }

  // Strip .md / .src.md from the basename
  const lastSlash = resolved.lastIndexOf('/');
  const dir = lastSlash >= 0 ? resolved.slice(0, lastSlash) : '';
  let baseName = lastSlash >= 0 ? resolved.slice(lastSlash + 1) : resolved;
  baseName = baseName.replace(/\.src\.md$/i, '').replace(/\.md$/i, '');

  if (!baseName) return;

  if (!dir) {
    showCreateFileModal({ root: true, name: baseName });
  } else {
    // Use the topmost folder as the category; any deeper folders are flattened
    // because the current modal only supports a single category level.
    const category = dir.split('/')[0];
    showCreateFileModal({ category, name: baseName });
  }
}

// Load a file
async function loadFile(path: string): Promise<void> {
  // Snapshot current file state ONCE before changing anything
  if (currentFile && currentFile !== path) {
    flushPendingSave();
    const snapshot = snapshotCurrentFileState();
    if (snapshot) tabManager.updateActiveState(snapshot);
  }

  // Markdown-source view is per-page — reset when navigating to a different file.
  if (currentFile !== path) {
    resetMarkdownSourceView();
    // Anchor map belongs to the outgoing file; drop it until the next render
    scrollSync.invalidate();
  }

  // Tab-aware: if already open in multi-mode, try to restore from cache
  if (tabManager.isOpen(path) && path !== currentFile) {
    const existingState = tabManager.activateTab(path);
    if (existingState && existingState.sourceContent) {
      restoreFileState(existingState);
      tabBar?.render();
      refreshTreeSelection();
      return;
    }
    // Fall through to load from server if placeholder
  }

  try {
    setStatus('Loading...', 'loading');

    const data = await storage.readFile(path) as FileResponse;

    currentFile = path;
    hasUnsavedChanges = false;
    lastSavedHash = await computeHash(data.content);
    lastSaveTime = null; // Unknown - file was loaded, not saved
    setSaveStatus('synced');
    trackRecentPath(path);

    // Auto-expand folders to show the current file and re-render the file tree
    if (!path.startsWith('_components/')) {
      fileTreeRenderer?.expandToPath(path);
      if (currentFilesData.length > 0) {
        fileTreeRenderer?.render(currentFilesData);
      }
    }

    // Update URL with file path
    const url = new URL(window.location.href);
    url.searchParams.set('file', path);
    window.history.pushState({}, '', url.toString());

    if (data.variables.static !== undefined) {
      staticVariables = data.variables.static;
      dynamicVariables = data.variables.dynamic || {};
    } else {
      staticVariables = (data.variables as Record<string, string>) || {};
      dynamicVariables = {};
    }
    userVariables = { ...staticVariables, ...dynamicVariables };
    // Sync with variableSystem for enrichers
    variableSystem.setVariables(staticVariables, dynamicVariables);
    sourceContent = data.content;

    // Update editor if in edit mode. Use a fresh EditorState so this new file
    // starts with an empty undo history (Ctrl+Z must not cross file boundaries).
    if (editMode && editorView) {
      resetEditorContent(sourceContent);
    }

    // Update page title
    updatePageTitle();

    // Clear per-block variable overrides when loading a new file
    variableSystem.clearAllBlockOverrides();

    // Build variables form
    buildVariablesForm();

    // Load checklist state for this file
    try {
      const savedChecklist = await storage.backend.getChecklist(path);
      checklistEnabled = savedChecklist !== null;
      checklistSlugs = savedChecklist ?? [];
      checklistEnricher.setEnabled(checklistEnabled);
      btnChecklist.classList.toggle('active', checklistEnabled);
      markdownOutput.classList.toggle('checklist-mode', checklistEnabled);
    } catch (err) {
      checklistSlugs = [];
      checklistEnabled = false;
      checklistEnricher.setEnabled(false);
      btnChecklist.classList.remove('active');
      markdownOutput.classList.remove('checklist-mode');
    }

    // Auto weave and tangle
    await processFile();

    // Register with tab manager
    const tabState = snapshotCurrentFileState();
    if (tabState) {
      tabManager.openTab(path, tabState);
      tabBar?.render();
      persistTabs();
    }

    offerUnsavedBackupRestore(path, data.content);

  } catch (err) {
    const error = err as Error;
    setStatus(`Error: ${error.message}`, 'error');
  }
}

// Load a component (for full editing, same as source files)
async function loadComponent(name: string): Promise<void> {
  const componentPath = name.startsWith('_components/') ? name : `_components/${name}`;

  // Snapshot current file state ONCE before changing anything
  if (currentFile && currentFile !== componentPath) {
    flushPendingSave();
    const snapshot = snapshotCurrentFileState();
    if (snapshot) tabManager.updateActiveState(snapshot);
  }

  // Markdown-source view is per-page — reset when navigating to a different file.
  if (currentFile !== componentPath) {
    resetMarkdownSourceView();
    scrollSync.invalidate();
  }

  // Tab-aware: if already open in multi-mode, try to restore from cache
  if (tabManager.isOpen(componentPath) && componentPath !== currentFile) {
    const existingState = tabManager.activateTab(componentPath);
    if (existingState && existingState.sourceContent) {
      restoreFileState(existingState);
      tabBar?.render();
      refreshTreeSelection();
      return;
    }
    // Fall through to load from server if placeholder
  }

  // Strip prefix for API call
  const apiName = name.startsWith('_components/') ? name.replace('_components/', '') : name;

  try {
    setStatus('Loading...', 'loading');

    const data = await storage.readComponent(apiName) as ComponentResponse;

    // Use the path returned by the API for saving
    currentFile = data.path;
    hasUnsavedChanges = false;
    lastSavedHash = await computeHash(data.content);
    lastSaveTime = null; // Unknown - file was loaded, not saved
    setSaveStatus('synced');
    trackRecentPath(data.path);

    // Update URL with component path
    const url = new URL(window.location.href);
    url.searchParams.set('file', data.path);
    window.history.pushState({}, '', url.toString());

    // Components don't have frontmatter variables
    staticVariables = {};
    dynamicVariables = {};
    userVariables = {};
    // Sync with variableSystem for enrichers
    variableSystem.setVariables({}, {});
    sourceContent = data.content;

    // Update editor if in edit mode. Use a fresh EditorState so this component
    // starts with an empty undo history (Ctrl+Z must not cross file boundaries).
    if (editMode && editorView) {
      resetEditorContent(sourceContent);
    }

    // Update page title
    updatePageTitle();

    // Clear per-block variable overrides when loading a component
    variableSystem.clearAllBlockOverrides();

    // Hide variables panel for components
    variablesPanel.classList.add('hidden');

    // Load checklist state for this component
    try {
      const savedChecklist = await storage.backend.getChecklist(data.path);
      checklistEnabled = savedChecklist !== null;
      checklistSlugs = savedChecklist ?? [];
      checklistEnricher.setEnabled(checklistEnabled);
      btnChecklist.classList.toggle('active', checklistEnabled);
      markdownOutput.classList.toggle('checklist-mode', checklistEnabled);
    } catch (err) {
      checklistSlugs = [];
      checklistEnabled = false;
      checklistEnricher.setEnabled(false);
      btnChecklist.classList.remove('active');
      markdownOutput.classList.remove('checklist-mode');
    }

    // Auto weave and tangle the component directly
    await processFile();

    // Register with tab manager
    const tabState = snapshotCurrentFileState();
    if (tabState) {
      tabManager.openTab(data.path, tabState);
      tabBar?.render();
      persistTabs();
    }

    offerUnsavedBackupRestore(data.path, data.content);

  } catch (err) {
    const error = err as Error;
    setStatus(`Error: ${error.message}`, 'error');
  }
}

// Build variables form - all inputs are plain text (no password masking)
function buildVariablesForm(): void {
  const hasDynamic = Object.keys(dynamicVariables).length > 0;
  const hasStatic = Object.keys(staticVariables).length > 0;

  if (!hasDynamic && !hasStatic) {
    variablesPanel.classList.add('hidden');
    return;
  }

  // Only show if toggle is checked
  if (toggleVariables.checked) {
    variablesPanel.classList.remove('hidden');
  }

  let html = '';

  // Dynamic variables section (always visible, prominent)
  if (hasDynamic) {
    html += `
      <div class="var-section dynamic-section">
        <h4>Runtime Variables</h4>
        <p class="section-hint">Configure these for your deployment. Changes auto-refresh.</p>
        <div class="var-grid">
          ${Object.entries(dynamicVariables).map(([key, value]) => {
            // Use userVariables if available (preserves user edits), fallback to default
            const displayValue = userVariables[key] !== undefined ? userVariables[key] : value;
            return `
              <div class="var-row">
                <label>${key}</label>
                <input
                  type="text"
                  data-var="${key}"
                  value="${escapeHtml(displayValue)}"
                  placeholder="(enter value)"
                >
              </div>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  // Static variables section (collapsed by default)
  if (hasStatic) {
    html += `
      <details class="var-section static-section">
        <summary>Static Variables <span class="static-count">(${Object.keys(staticVariables).length})</span></summary>
        <p class="section-hint">Internal variables for script organization. Usually no need to change.</p>
        <div class="var-grid">
          ${Object.entries(staticVariables).map(([key, value]) => {
            // Use userVariables if available (preserves user edits), fallback to default
            const displayValue = userVariables[key] !== undefined ? userVariables[key] : value;
            return `
              <div class="var-row static">
                <label>${key}</label>
                <input
                  type="text"
                  data-var="${key}"
                  value="${escapeHtml(displayValue)}"
                >
              </div>
            `;
          }).join('')}
        </div>
      </details>
    `;
  }

  variablesForm.innerHTML = html;

  // Listen for changes with debounce
  variablesForm.querySelectorAll('input').forEach(input => {
    input.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const varName = target.dataset.var!;
      const value = target.value;
      userVariables[varName] = value;
      // Sync with variableSystem for enrichers
      variableSystem.setUserVariable(varName, value);
      debouncedProcess();
    });
  });
}

// Debounced process
function debouncedProcess(): void {
  clearTimeout(debounceTimer!);
  debounceTimer = window.setTimeout(() => {
    processFile();
  }, 10);
}

// Client-side variable extraction (mirrors server logic)
function extractVariablesFromContent(content: string): { static: Record<string, string>; dynamic: Record<string, string> } {
  const staticVars: Record<string, string> = {};
  const dynamicVars: Record<string, string> = {};
  const varBlockMatch = content.match(/<!--\s*vars\s*([\s\S]*?)-->/);

  if (varBlockMatch) {
    const varBlock = varBlockMatch[1];
    const lines = varBlock.split('\n');

    for (const line of lines) {
      // Match @VAR_NAME=value (dynamic) or VAR_NAME=value (static)
      const match = line.match(/^\s*(@?)([A-Za-z_][A-Za-z0-9_]*)\s*=\s*(.+?)\s*$/);
      if (match) {
        const isDynamic = match[1] === '@';
        const varName = match[2];
        const varValue = match[3];

        if (isDynamic) {
          dynamicVars[varName] = varValue;
        } else {
          staticVars[varName] = varValue;
        }
      }
    }
  }

  return { static: staticVars, dynamic: dynamicVars };
}

// Debounced variable detection - only rebuild form if variables actually changed
function debouncedVariableDetect(): void {
  clearTimeout(variableDetectTimer!);
  variableDetectTimer = window.setTimeout(() => {
    detectVariableChanges();
  }, 800); // Slightly longer debounce for variable detection
}

// Detect if variables changed and update form
function detectVariableChanges(): void {
  const extracted = extractVariablesFromContent(sourceContent);

  // Quick comparison using JSON (efficient for small objects)
  const staticChanged = JSON.stringify(extracted.static) !== JSON.stringify(staticVariables);
  const dynamicChanged = JSON.stringify(extracted.dynamic) !== JSON.stringify(dynamicVariables);

  if (staticChanged || dynamicChanged) {
    const previousDefaults = { ...staticVariables, ...dynamicVariables };
    // Preserve user-entered values for variables that still exist
    const oldUserVariables = { ...userVariables };

    staticVariables = extracted.static;
    dynamicVariables = extracted.dynamic;

    // Rebuild userVariables: start with new defaults, overlay preserved values
    userVariables = { ...staticVariables, ...dynamicVariables };

    // Restore only explicit user overrides (not stale previous defaults).
    for (const key of Object.keys(oldUserVariables)) {
      if (key in userVariables && oldUserVariables[key] !== previousDefaults[key]) {
        userVariables[key] = oldUserVariables[key];
      }
    }

    // Sync with variableSystem for enrichers (including user-edited values)
    variableSystem.restoreVariables(staticVariables, dynamicVariables, userVariables);

    // Rebuild the form
    buildVariablesForm();
  }
}

// Script variable editor state
let activeScriptEditor: HTMLElement | null = null;
let scriptEditorEscapeHandler: ((e: KeyboardEvent) => void) | null = null;

/**
 * Sync script mode dropdown options from the active script implementation.
 */
function syncScriptModeOptions(options: ScriptModeOption[], outputs?: Record<string, string>): void {
  const availableOptions = options.length > 0 ? options : DEFAULT_SCRIPT_MODE_OPTIONS;
  const existing = Array.from(scriptModeSelect.options)
    .map(option => `${option.value}:${option.text}:${option.disabled ? '1' : '0'}`)
    .join('|');
  const next = availableOptions
    .map(option => {
      const disabled = option.value !== 'standard' && outputs ? !outputs[option.value] : false;
      return `${option.value}:${option.label}:${disabled ? '1' : '0'}`;
    })
    .join('|');

  if (existing !== next) {
    scriptModeSelect.innerHTML = availableOptions
      .map(option => {
        const disabled = option.value !== 'standard' && outputs ? !outputs[option.value] : false;
        return `<option value="${option.value}"${disabled ? ' disabled' : ''}>${escapeHtml(option.label)}</option>`;
      })
      .join('');
  }

  const hasCurrentMode = availableOptions.some(option => option.value === scriptMode);
  const currentModeHasOutput = scriptMode === 'standard' || !outputs || !!outputs[scriptMode];

  if (!hasCurrentMode || !currentModeHasOutput) {
    scriptMode = availableOptions[0].value;
  }

  scriptModeSelect.value = scriptMode;
  scriptModeSelect.disabled = availableOptions.length <= 1;
}

/**
 * Update the script display with variable substitution (including overrides)
 */
function updateScriptDisplay(scriptName: string): void {
  const scriptInfo = scriptInfoMap.get(scriptName);

  if (scriptInfo) {
    syncScriptModeOptions(scriptInfo.availableModes, scriptInfo.outputs);
  }

  if (scriptMode !== 'standard') {
    const modeOutput = scriptInfo?.outputs[scriptMode];
    if (modeOutput) {
      scriptsOutput.value = modeOutput;
      // Update the variable UI for this script
      updateScriptVarsUI(scriptName);
      return;
    }

    scriptsOutput.value = `Mode "${scriptMode}" is not available for this script.`;
    // Update the variable UI for this script
    updateScriptVarsUI(scriptName);
    return;
  }

  // Standard mode - apply variable substitution
  const meta = variableSystem.getScriptMeta(scriptName);

  if (meta && meta.variables.length > 0) {
    // Apply script-level variable overrides to the template
    const content = variableSystem.applyScriptVariables(meta.template, scriptName);
    scriptsOutput.value = `${content.trimEnd()}\n`;
  } else {
    // No variables, use the pre-computed tangled script
    scriptsOutput.value = tangledScripts[scriptName] || '';
  }

  // Update the variable UI for this script
  updateScriptVarsUI(scriptName);
}

/**
 * Update the script variables UI (button visibility and override indicator)
 */
function updateScriptVarsUI(scriptName: string | null): void {
  const varsBtn = document.getElementById('script-vars-btn') as HTMLButtonElement;
  const varsIndicator = document.getElementById('script-vars-indicator') as HTMLSpanElement;

  if (!scriptName) {
    varsBtn?.classList.add('hidden');
    return;
  }

  const meta = variableSystem.getScriptMeta(scriptName);

  if (!meta || meta.variables.length === 0) {
    varsBtn?.classList.add('hidden');
  } else {
    varsBtn?.classList.remove('hidden');

    // Show indicator if there are overrides
    if (variableSystem.hasScriptOverrides(scriptName)) {
      varsIndicator.classList.remove('hidden');
    } else {
      varsIndicator.classList.add('hidden');
    }
  }
}

/**
 * Update script tab checkboxes to reflect current heading checklist state
 * Called when heading checkboxes change to sync script checkbox visual state
 */
function updateScriptTabsUI(): void {
  if (!checklistEnabled || !checklistSync.hasMapping()) return;

  scriptsTabs.querySelectorAll('.script-tab').forEach(tab => {
    const tabEl = tab as HTMLElement;
    const scriptName = tabEl.dataset.script;
    if (!scriptName) return;

    const state = checklistSync.getScriptCheckState(scriptName);
    const checkbox = tab.querySelector('.script-checkbox') as HTMLInputElement | null;

    if (checkbox) {
      checkbox.checked = state === 'checked';
      checkbox.indeterminate = state === 'partial';
    }

    // Update visual state classes
    tabEl.classList.remove('script-checked', 'script-partial');
    if (state === 'checked') {
      tabEl.classList.add('script-checked');
    } else if (state === 'partial') {
      tabEl.classList.add('script-partial');
    }
  });
}

/**
 * Show the script variable editor popup
 */
function showScriptVarsEditor(scriptName: string): void {
  hideScriptVarsEditor();

  const meta = variableSystem.getScriptMeta(scriptName);
  if (!meta || meta.variables.length === 0) return;

  const overrides = variableSystem.getScriptOverrides(scriptName);

  const editor = document.createElement('div');
  editor.className = 'script-var-editor';
  editor.id = 'script-var-editor';

  // Build variable rows
  const varsHtml = meta.variables.map(varName => {
    const globalValue = variableSystem.userVariables[varName] || '';
    const currentValue = overrides[varName] !== undefined ? overrides[varName] : globalValue;
    const isOverridden = overrides[varName] !== undefined;
    return `
      <div class="script-var-row${isOverridden ? ' overridden' : ''}">
        <label>${escapeHtml(varName)}</label>
        <input type="text"
               data-var="${escapeHtml(varName)}"
               value="${escapeHtml(currentValue)}"
               placeholder="${escapeHtml(globalValue)}">
      </div>
    `;
  }).join('');

  editor.innerHTML = `
    <div class="script-var-editor-header">
      <span>Script Variables</span>
      <button class="script-var-editor-close" title="Close">&times;</button>
    </div>
    <div class="script-var-editor-body">${varsHtml}</div>
    <div class="script-var-editor-footer">
      <button class="script-var-reset">Reset to Global</button>
    </div>
  `;

  // Append to scripts header
  const scriptsHeader = scriptsPanel.querySelector('.scripts-header') as HTMLElement;
  scriptsHeader.appendChild(editor);
  activeScriptEditor = editor;

  // Setup event listeners
  setupScriptEditorListeners(editor, scriptName);

  // Focus first input
  const firstInput = editor.querySelector('input') as HTMLInputElement | null;
  if (firstInput) {
    firstInput.focus();
    firstInput.select();
  }
}

/**
 * Hide the script variable editor
 */
function hideScriptVarsEditor(): void {
  if (activeScriptEditor) {
    activeScriptEditor.remove();
    activeScriptEditor = null;
  }
  if (scriptEditorEscapeHandler) {
    document.removeEventListener('keydown', scriptEditorEscapeHandler);
    scriptEditorEscapeHandler = null;
  }
}

/**
 * Setup event listeners for the script variable editor
 */
function setupScriptEditorListeners(editor: HTMLElement, scriptName: string): void {
  // Close button
  const closeBtn = editor.querySelector('.script-var-editor-close');
  closeBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    hideScriptVarsEditor();
  });

  // Reset button
  const resetBtn = editor.querySelector('.script-var-reset');
  resetBtn?.addEventListener('click', (e) => {
    e.preventDefault();
    e.stopPropagation();
    variableSystem.clearScriptOverrides(scriptName);
    updateScriptDisplay(scriptName);
    hideScriptVarsEditor();
  });

  // Variable inputs - real-time update
  editor.querySelectorAll('input[data-var]').forEach(input => {
    const inputEl = input as HTMLInputElement;

    inputEl.addEventListener('input', () => {
      const varName = inputEl.dataset.var!;
      const newValue = inputEl.value;
      const globalValue = variableSystem.userVariables[varName] || '';

      variableSystem.setScriptOverride(scriptName, varName, newValue);

      // Update row styling
      const row = inputEl.closest('.script-var-row');
      if (newValue === globalValue) {
        row?.classList.remove('overridden');
      } else {
        row?.classList.add('overridden');
      }

      // Update script display
      updateScriptDisplay(scriptName);
    });

    // Prevent form submission on Enter
    inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }
    });
  });

  // Escape to close
  scriptEditorEscapeHandler = (e: KeyboardEvent) => {
    if (e.key === 'Escape') {
      hideScriptVarsEditor();
    }
  };
  document.addEventListener('keydown', scriptEditorEscapeHandler);

  // Close when a press STARTS outside the editor (mousedown, not click):
  // a text-selection drag that starts inside an input and releases outside
  // must not dismiss the popup.
  setTimeout(() => {
    const closeOnOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!editor.contains(target) && !target.closest('#script-vars-btn')) {
        hideScriptVarsEditor();
        document.removeEventListener('mousedown', closeOnOutside);
      }
    };
    document.addEventListener('mousedown', closeOnOutside);
  }, 100);
}

// Process file (weave + tangle)
async function processFile(): Promise<void> {
  if (!currentFile) return;

  // Consume the cursor-align request up front so error paths don't leave it
  // armed for an unrelated later render.
  const alignAfterRender = pendingCursorAlign;
  pendingCursorAlign = false;

  // Keep variable UI in sync even when sourceContent changes outside direct typing events.
  detectVariableChanges();

  setStatus('Processing...', 'loading');

  try {
    // Load components for embed resolution
    const components = await loadAllComponents();

    // Collect page paths for wikilink resolution (exclude components)
    const allPagePaths = currentFilesData
      .map(f => f.path)
      .filter(p => p.endsWith('.src.md') && !p.startsWith('_components/'));

    // Weave - process embeds with variable substitution SKIPPED to preserve {{VAR}} templates
    // This allows tangleWithSubstitution to handle both template preservation and final output
    const templateWeaveResult = weave(sourceContent, {
      variables: userVariables,
      components,
      skipVariableSubstitution: true,
      sourcePath: currentFile ?? undefined,
      allPaths: allPagePaths,
    });

    if (!templateWeaveResult.success) {
      setStatus('Weave error', 'error');
      markdownOutput.innerHTML = `<pre style="color: #f44;">${escapeHtml(templateWeaveResult.error || 'Unknown error')}</pre>`;
      scrollSync.invalidate();
      desktop?.onPreviewRendered();
      return;
    }

    // Parse code block variables from the template-woven output (embeds
    // expanded, {{VAR}} placeholders intact) so blocks that arrive via
    // <!-- embed: ... --> get override metadata too, not just blocks written
    // directly in the source file.
    variableSystem.parseCodeBlockVariables(templateWeaveResult.output);

    // Apply variable substitution to get display content
    const { result: displayContent } = substituteVariables(templateWeaveResult.output, userVariables);
    wovenContent = displayContent;

    if (markdownSourceView) {
      // Show the exported markdown form: embeds expanded, wikilinks expanded,
      // variables substituted, and all leedoc-only markers stripped
      // (`[ld:toc]`, `<!-- tangle: ... -->`, `<!-- collapse -->`, media:// rewrite).
      // This matches exactly what lands in `_generated/docs/*.md` on export.
      const portable = cleanForExport(wovenContent);
      markdownOutput.innerHTML = `<pre class="markdown-source"><code>${escapeHtml(portable)}</code></pre>`;
      scrollSync.invalidate();
      tocSidebar.update();
      tocSidebar.syncChecklist(checklistSlugs);
      updatePageTitle();
    } else {
      // Rewrite media:// URLs to actual URLs for rendering
      const contentWithMediaUrls = rewriteMediaUrls(wovenContent);

      // Render markdown
      markdownOutput.innerHTML = marked.parse(contentWithMediaUrls) as string;

      // Run enricher pipeline (syntax highlighting, copy buttons, variable editing, etc.)
      await previewEnricher.enrich(markdownOutput, {
        sourceContent,
        userVariables
      });

      // Update TOC sidebar with new headings and checklist state
      tocSidebar.update();
      tocSidebar.syncChecklist(checklistSlugs);

      // Update title (needs rendered headings for checklist count)
      updatePageTitle();

      // Rebuild the scroll-sync anchor map against the fresh preview DOM,
      // then (after typing) snap the preview to the line being edited.
      scrollSync.rebuild(sourceContent);
      if (alignAfterRender) scrollSync.alignToCursor();
    }
    desktop?.onPreviewRendered();

    // Tangle - single pass extracts scripts AND preserves templates
    // Uses the template-woven content (embeds expanded, vars NOT substituted)
    // tangleWithSubstitution handles variable substitution internally
    const tangleResult = tangleWithSubstitution(templateWeaveResult.output, {
      variables: userVariables,
    });

    // Script metadata is now directly available from the single tangle pass
    // No fragile position-based matching needed!
    if (tangleResult.success) {
      scriptTemplateMeta = tangleResult.scriptMeta;
      variableSystem.setScriptMeta(scriptTemplateMeta);

      // Build script-heading mapping for checklist sync
      const resolvedScriptNames = tangleResult.scriptMeta.map(m => m.name);
      scriptHeadingMapping = buildScriptHeadingMapping(templateWeaveResult.output, resolvedScriptNames);
      checklistSync.setMapping(scriptHeadingMapping);
    }

    if (tangleResult.success) {
      tangledScripts = tangleResult.scripts;

      // Generate interactive script variants
      // Use wovenContent (with variables substituted) instead of templateWeaveResult.output
      const scriptTitle = currentFile ? currentFile.replace(/\.src\.md$/, '') : 'Installation Script';
      scriptInfoMap = generateAllScriptModes(wovenContent, tangledScripts, {
        title: scriptTitle,
      });

      // Use scriptMeta order (document order of first appearance) instead of alphabetical
      const scriptNames = tangleResult.scriptMeta.map(m => m.name);

      if (scriptNames.length === 0) {
        scriptsTabs.innerHTML = '';
        scriptsOutput.value = 'No scripts found.\n\nAdd <!-- tangle: script.sh --> directives to generate scripts.';
        activeScriptTab = null;
        updateScriptVarsUI(null);
      } else {
        // Determine which tab to show - preserve selection if still exists
        let selectedScript = scriptNames[0];
        if (activeScriptTab && scriptNames.includes(activeScriptTab)) {
          selectedScript = activeScriptTab;
        } else {
          activeScriptTab = selectedScript;
        }

        // Build script tabs (with checkboxes when checklist mode is enabled)
        scriptsTabs.innerHTML = scriptNames.map((name) => {
          const checkState = checklistEnabled ? checklistSync.getScriptCheckState(name) : 'unchecked';
          const checkboxHtml = checklistEnabled
            ? `<input type="checkbox" class="script-checkbox" data-script="${name}" ${checkState === 'checked' ? 'checked' : ''}>`
            : '';
          const stateClass = checklistEnabled && checkState !== 'unchecked' ? ` script-${checkState}` : '';
          return `
            <div class="script-tab${name === selectedScript ? ' active' : ''}${stateClass}" data-script="${name}">
              ${checkboxHtml}${name}
            </div>
          `;
        }).join('');

        // Handle indeterminate state for partial checkboxes (can't be set via HTML attribute)
        if (checklistEnabled) {
          scriptsTabs.querySelectorAll('.script-checkbox').forEach(cb => {
            const checkbox = cb as HTMLInputElement;
            const scriptName = checkbox.dataset.script!;
            if (checklistSync.getScriptCheckState(scriptName) === 'partial') {
              checkbox.indeterminate = true;
            }
          });
        }

        // Show selected script (apply any script-level overrides)
        updateScriptDisplay(selectedScript);

        // Tab click handlers
        scriptsTabs.querySelectorAll('.script-tab').forEach(tab => {
          tab.addEventListener('click', (e) => {
            // Don't switch tabs if clicking the checkbox
            if ((e.target as HTMLElement).classList.contains('script-checkbox')) return;
            scriptsTabs.querySelectorAll('.script-tab').forEach(t => t.classList.remove('active'));
            tab.classList.add('active');
            activeScriptTab = (tab as HTMLElement).dataset.script!;
            updateScriptDisplay(activeScriptTab);
          });
        });

        // Script checkbox handlers (for checklist sync)
        if (checklistEnabled) {
          scriptsTabs.querySelectorAll('.script-checkbox').forEach(cb => {
            cb.addEventListener('change', (e) => {
              e.stopPropagation(); // Don't trigger tab click
              const checkbox = e.target as HTMLInputElement;
              const scriptName = checkbox.dataset.script!;
              checklistSync.onScriptToggle(scriptName, checkbox.checked);
              // Re-render preview to update heading checkboxes
              processFile();
            });
          });
        }
      }

      setStatus('Ready', '');
    } else {
      setStatus('Tangle error', 'error');
      scriptsOutput.value = tangleResult.error || 'Unknown error';
    }

  } catch (err) {
    const error = err as Error;
    setStatus(`Error: ${error.message}`, 'error');
  }
}

// Export handler
async function handleExport(format: ExportFormat): Promise<void> {
  if (!currentFile || !wovenContent) {
    alert('No document to export. Please open a file first.');
    return;
  }

  if (!wovenContent.trim()) {
    alert('No content to export. Please ensure the document is loaded.');
    return;
  }

  // Get filename from current file
  const filename = currentFile.replace(/\.src\.md$/, '').replace(/^.*\//, '');

  setStatus('Exporting...', 'loading');

  try {
    // Clean woven markdown by stripping leedoc-specific markers ([ld:toc], tangle, etc.)
    const exportContent = cleanForExport(wovenContent);
    const success = await exportService.exportAndDownload(exportContent, format, {
      title: filename,
      filename: filename,
    });

    if (success) {
      setStatus('Exported', '');
      setTimeout(() => setStatus('Ready', ''), 2000);
    } else {
      setStatus('Export failed', 'error');
    }
  } catch (error) {
    console.error('Export error:', error);
    setStatus('Export failed', 'error');
    alert(`Export failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// Debounced save for checklist state changes
function debounceSaveChecklist(): void {
  if (checklistSaveTimer) clearTimeout(checklistSaveTimer);
  checklistSaveTimer = window.setTimeout(async () => {
    if (!currentFile) return;
    try {
      await storage.backend.saveChecklist(currentFile, checklistSlugs);
    } catch (err) {
      console.error('Failed to save checklist:', err);
    }
  }, 500);
}

// Update page title: "docname | Leedoc" or "docname [checked/total] | Leedoc"
function updatePageTitle(): void {
  // Usages button only makes sense for components
  btnComponentUsages.classList.toggle('hidden', !currentFile?.startsWith('_components/'));

  if (!currentFile) {
    document.title = 'Leedoc';
    return;
  }

  let docName: string;
  if (currentFile.startsWith('_components/')) {
    docName = currentFile.replace('_components/', '').replace('.md', '');
  } else {
    docName = currentFile.split('/').pop()!.replace('.src.md', '');
  }

  if (checklistEnabled) {
    const checklistIds = new Set<string>();

    markdownOutput.querySelectorAll('.checklist-section[data-heading-id]').forEach((el) => {
      const id = (el as HTMLElement).dataset.headingId;
      if (id) checklistIds.add(id);
    });

    markdownOutput.querySelectorAll('.checklist-task-item[data-checklist-task-id]').forEach((el) => {
      const id = (el as HTMLElement).dataset.checklistTaskId;
      if (id) checklistIds.add(id);
    });

    const totalItems = checklistIds.size;
    const checkedCount = checklistSlugs.filter(slug => checklistIds.has(slug)).length;
    document.title = `[${checkedCount}/${totalItems}] ${docName} | Leedoc`;
  } else {
    document.title = `${docName} | Leedoc`;
  }
}

// Set status
function setStatus(text: string, type: string): void {
  statusEl.textContent = text;
  statusEl.className = 'status' + (type ? ' ' + type : '');
}

// Loading modal (reusable loading indicator with semi-transparent backdrop)
function showLoading(message: string = 'Loading...'): void {
  if (loadingModalMessage) {
    loadingModalMessage.textContent = message;
  }
  modalManager.show(MODAL_IDS.LOADING);
}

function hideLoading(): void {
  modalManager.hide(MODAL_IDS.LOADING);
}

// Utility: escape HTML
function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

// Resize functionality
function initResizeHandles(): void {
  const handles = document.querySelectorAll('.resize-handle');

  handles.forEach(handle => {
    handle.addEventListener('mousedown', (e: Event) => {
      const mouseEvent = e as MouseEvent;
      mouseEvent.preventDefault();
      const resizeType = (handle as HTMLElement).dataset.resize;
      const isVertical = handle.classList.contains('resize-handle-v');

      document.body.classList.add(isVertical ? 'resizing-v' : 'resizing');

      const startX = mouseEvent.clientX;
      const startY = mouseEvent.clientY;

      let target: HTMLElement | null;
      let startSize: number;

      switch (resizeType) {
        case 'variables':
          target = document.getElementById('variables-panel');
          startSize = target!.offsetHeight;
          break;
        case 'panels':
          // Not a document-wide lookup: windowed-mode snapshots clone the workspace
          target = scriptsPanel;
          startSize = target.offsetWidth;
          break;
        case 'editor':
          target = document.getElementById('editor-panel');
          startSize = target!.offsetWidth;
          break;
        default:
          return;
      }

      function onMouseMove(e: MouseEvent) {
        if (isVertical) {
          const delta = e.clientY - startY;
          const newSize = Math.max(80, Math.min(workspaceEl.clientHeight * 0.5, startSize + delta));
          target!.style.height = newSize + 'px';
        } else {
          const delta = e.clientX - startX;
          if (resizeType === 'panels') {
            const newSize = Math.max(200, Math.min(contentContainerEl.clientWidth * 0.6, startSize - delta));
            target!.style.width = newSize + 'px';
          } else if (resizeType === 'editor') {
            const newSize = Math.max(300, Math.min(contentContainerEl.clientWidth * 0.7, startSize + delta));
            target!.style.width = newSize + 'px';
          }
        }
      }

      function onMouseUp() {
        document.body.classList.remove('resizing', 'resizing-v');
        document.removeEventListener('mousemove', onMouseMove);
        document.removeEventListener('mouseup', onMouseUp);
      }

      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    });
  });
}

// Sentinel value in the category dropdown that means "create at project root"
const ROOT_CATEGORY_SENTINEL = '__root__';

// CRUD: Show create file modal. Optional prefill comes from broken-link clicks.
function showCreateFileModal(prefill?: { category?: string; name?: string; root?: boolean }): void {
  // Get existing categories from the file list
  const categories = [...new Set(currentFilesData
    .map(f => f.path.includes('/') ? f.path.split('/')[0] : '')
    .filter(Boolean))].sort();

  // Populate category dropdown
  newFileCategory.innerHTML =
    '<option value="">-- Create new category --</option>' +
    `<option value="${ROOT_CATEGORY_SENTINEL}">(project root)</option>`;
  for (const cat of categories) {
    newFileCategory.innerHTML += `<option value="${cat}">${cat}</option>`;
  }

  // Reset / prefill form
  newCategoryName.value = '';
  newFileName.value = prefill?.name ?? '';

  if (prefill?.root) {
    newFileCategory.value = ROOT_CATEGORY_SENTINEL;
    newCategoryGroup.style.display = 'none';
  } else if (prefill?.category && categories.includes(prefill.category)) {
    newFileCategory.value = prefill.category;
    newCategoryGroup.style.display = 'none';
  } else if (prefill?.category) {
    newFileCategory.value = '';
    newCategoryName.value = prefill.category;
    newCategoryGroup.style.display = 'block';
  } else {
    newFileCategory.value = '';
    newCategoryGroup.style.display = 'block';
  }

  updateFilePreview();
  modalManager.show(MODAL_IDS.CREATE_FILE);

  if (prefill?.name) {
    // Focus filename so user can just press Enter
    newFileName.focus();
    newFileName.select();
  } else if (prefill?.root || (prefill?.category && categories.includes(prefill.category))) {
    newFileName.focus();
  } else {
    newCategoryName.focus();
  }
}

// CRUD: Handle category selection change
function handleCategoryChange(): void {
  if (newFileCategory.value === '') {
    // Creating new category
    newCategoryGroup.style.display = 'block';
    newCategoryName.focus();
  } else {
    // Using existing category, or project root
    newCategoryGroup.style.display = 'none';
    newCategoryName.value = '';
    newFileName.focus();
  }
  updateFilePreview();
}

// CRUD: Show create component modal
function showCreateComponentModal(): void {
  // Reset form
  newComponentName.value = '';
  updateComponentPreview();
  modalManager.show(MODAL_IDS.CREATE_COMPONENT);
  newComponentName.focus();
}

// CRUD: Show delete confirmation modal
function showDeleteModal(type: 'file' | 'component', pathOrName: string): void {
  deleteTarget = { type, path: pathOrName };
  deleteTargetPath.textContent = type === 'file' ? pathOrName : `_components/${pathOrName}`;
  modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);
  modalManager.show(MODAL_IDS.DELETE);
}

// CRUD: Close all modals
function closeAllModals(): void {
  modalManager.hideAll();
  userMenuDropdown?.classList.add('hidden');
  deleteTarget = null;
  renameTarget = null;
  selectedSnapshotId = null;
  selectedMediaFilename = null;
}

// CRUD: Update file path preview
function updateFilePreview(): void {
  const isRoot = newFileCategory.value === ROOT_CATEGORY_SENTINEL;
  const name = sanitizeFilename(newFileName.value) || 'name';

  if (isRoot) {
    newFilePreview.innerHTML = `Path: <span class="preview-filename">${name}</span>.src.md`;
  } else {
    const category = newFileCategory.value || sanitizeFilename(newCategoryName.value) || 'category';
    newFilePreview.innerHTML = `Path: <span class="preview-filename">${category}</span>/<span class="preview-filename">${name}</span>.src.md`;
  }

  // Enable/disable create button - need either category, root, or new-category name
  const hasCategory = isRoot || newFileCategory.value || newCategoryName.value.trim();
  btnCreateFile.disabled = !hasCategory || !newFileName.value.trim();
}

// CRUD: Update component path preview
function updateComponentPreview(): void {
  const name = sanitizeFilename(newComponentName.value) || 'component';
  newComponentPreview.innerHTML = `Path: _components/<span class="preview-filename">${name}</span>.md`;

  // Enable/disable create button
  btnCreateComponent.disabled = !newComponentName.value.trim();
}

// CRUD: Create new file
async function createNewFile(): Promise<void> {
  const isRoot = newFileCategory.value === ROOT_CATEGORY_SENTINEL;
  const name = sanitizeFilename(newFileName.value);
  if (!name) return;

  let path: string;
  if (isRoot) {
    path = `${name}.src.md`;
  } else {
    const category = newFileCategory.value || sanitizeFilename(newCategoryName.value);
    if (!category) return;
    path = `${category}/${name}.src.md`;
  }

  try {
    btnCreateFile.disabled = true;
    btnCreateFile.textContent = 'Creating...';

    const data = await storage.createFile(path) as { path: string };

    // Refresh file list and open new file
    await loadFiles();
    closeAllModals();
    await loadFile(data.path);

  } catch (err) {
    const error = err as Error;
    alert(`Error: ${error.message}`);
  } finally {
    btnCreateFile.disabled = false;
    btnCreateFile.textContent = 'Create File';
  }
}

// CRUD: Create new component
async function createNewComponent(): Promise<void> {
  const name = sanitizeFilename(newComponentName.value);
  if (!name) return;

  const fullName = `${name}.md`;

  try {
    btnCreateComponent.disabled = true;
    btnCreateComponent.textContent = 'Creating...';

    const data = await storage.createComponent(fullName) as { name: string };

    // Refresh component list and open new component
    await loadComponents();
    closeAllModals();
    await loadComponent(data.name);

  } catch (err) {
    const error = err as Error;
    alert(`Error: ${error.message}`);
  } finally {
    btnCreateComponent.disabled = false;
    btnCreateComponent.textContent = 'Create Component';
  }
}

// CRUD: Confirm and execute delete
async function confirmDelete(): Promise<void> {
  if (!deleteTarget) return;

  const { type, path } = deleteTarget;

  try {
    btnConfirmDelete.disabled = true;
    btnConfirmDelete.textContent = 'Deleting...';

    if (type === 'file') {
      await storage.deleteFile(path);
      await loadFiles();
      // Remove tab and clear editor if deleted file was open
      tabManager.removeTab(path);
      if (currentFile === path) {
        const nextPath = tabManager.activeTab;
        if (nextPath) {
          const nextState = tabManager.getTab(nextPath);
          if (nextState?.sourceContent) {
            restoreFileState(nextState);
          } else {
            await loadFile(nextPath);
          }
        } else {
          clearEditor();
        }
      }
      tabBar?.render();
    } else {
      await storage.deleteComponent(path);
      await loadComponents();
      // Remove tab and clear editor if deleted component was open
      const componentPath = `_components/${path}`;
      tabManager.removeTab(componentPath);
      if (currentFile === componentPath) {
        const nextPath = tabManager.activeTab;
        if (nextPath) {
          const nextState = tabManager.getTab(nextPath);
          if (nextState?.sourceContent) {
            restoreFileState(nextState);
          } else {
            await loadFile(nextPath);
          }
        } else {
          clearEditor();
        }
      }
      tabBar?.render();
    }

    closeAllModals();

  } catch (err) {
    const error = err as Error;
    alert(`Error: ${error.message}`);
  } finally {
    btnConfirmDelete.disabled = false;
    btnConfirmDelete.textContent = 'Delete';
  }
}

// CRUD: Show rename modal
function showRenameModal(type: 'file' | 'component', pathOrName: string): void {
  renameTarget = { type, path: pathOrName };
  modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);

  if (type === 'file') {
    // Extract category and filename from path like "category/name.src.md"
    const parts = pathOrName.split('/');
    const category = parts.slice(0, -1).join('/');
    const filename = parts[parts.length - 1].replace('.src.md', '');

    renameModalTitle.textContent = 'Rename Manual';
    renameHint.textContent = '.src.md will be preserved';
    renameInput.value = filename;
    renameTarget.category = category;
    renameTarget.originalName = filename;
  } else {
    // Component: just the name like "component.md"
    const componentName = pathOrName.replace('.md', '');

    renameModalTitle.textContent = 'Rename Component';
    renameHint.textContent = '.md will be preserved';
    renameInput.value = componentName;
    renameTarget.originalName = componentName;
  }

  updateRenamePreview();
  modalManager.show(MODAL_IDS.RENAME);
  renameInput.focus();
  renameInput.select();
}

// CRUD: Update rename preview
function updateRenamePreview(): void {
  const newName = sanitizeFilename(renameInput.value) || renameTarget?.originalName || 'name';

  if (renameTarget?.type === 'file') {
    renamePreview.innerHTML = `Path: <span class="preview-filename">${renameTarget.category}</span>/<span class="preview-filename">${newName}</span>.src.md`;
  } else {
    renamePreview.innerHTML = `Path: _components/<span class="preview-filename">${newName}</span>.md`;
  }

  // Enable/disable rename button
  const isValid = renameInput.value.trim() && sanitizeFilename(renameInput.value) !== renameTarget?.originalName;
  btnConfirmRename.disabled = !isValid;
}

// CRUD: Confirm and execute rename
async function confirmRename(): Promise<void> {
  if (!renameTarget) return;

  const { type, path, category } = renameTarget;
  const newName = sanitizeFilename(renameInput.value);

  if (!newName) return;

  try {
    btnConfirmRename.disabled = true;
    btnConfirmRename.textContent = 'Renaming...';

    if (type === 'file') {
      const newPath = `${category}/${newName}.src.md`;
      await storage.renameFile(path, newPath);

      // Update tab manager with new path
      tabManager.renameTab(path, newPath);

      await loadFiles();

      // Update current file reference if renamed file was open
      if (currentFile === path) {
        currentFile = newPath;
        // Update URL
        const url = new URL(window.location.href);
        url.searchParams.set('file', newPath);
        window.history.pushState({}, '', url.toString());
        updatePageTitle();
      }
    } else {
      const newComponentName = `${newName}.md`;
      await storage.renameComponent(path, newComponentName);

      // Update tab manager with new path
      tabManager.renameTab(`_components/${path}`, `_components/${newComponentName}`);

      await loadComponents();

      // Update current file reference if renamed component was open
      if (currentFile === `_components/${path}`) {
        currentFile = `_components/${newComponentName}`;
        // Update URL
        const url = new URL(window.location.href);
        url.searchParams.set('file', currentFile);
        window.history.pushState({}, '', url.toString());
        updatePageTitle();
      }
    }

    closeAllModals();

  } catch (err) {
    const error = err as Error;
    alert(`Error: ${error.message}`);
  } finally {
    btnConfirmRename.disabled = false;
    btnConfirmRename.textContent = 'Rename';
  }
}

// CRUD: Clear editor state
function clearEditor(): void {
  currentFile = null;
  sourceContent = '';
  wovenContent = '';
  staticVariables = {};
  dynamicVariables = {};
  userVariables = {};
  tangledScripts = {};
  scriptInfoMap = new Map();
  hasUnsavedChanges = false;

  // Reset UI
  updatePageTitle();
  markdownOutput.innerHTML = '<p class="placeholder">Select or create a file...</p>';
  scrollSync.invalidate();
  scriptsOutput.value = '';
  scriptsTabs.innerHTML = '';
  scriptMode = 'standard';
  syncScriptModeOptions(DEFAULT_SCRIPT_MODE_OPTIONS, { standard: '' });
  variablesForm.innerHTML = '';
  variablesPanel.classList.add('hidden');
  lastSaveTime = null;
  lastSavedHash = null;
  setSaveStatus('hidden');
  setStatus('', '');

  // Clear URL parameter and hash
  const url = new URL(window.location.href);
  url.searchParams.delete('file');
  url.hash = '';
  window.history.pushState({}, '', url.toString());

  // Reset editor if in edit mode (fresh state — no carry-over undo history)
  if (editMode && editorView) {
    resetEditorContent('');
  }

  // Clear active state in the modal's trees
  psFileTree?.querySelectorAll('.tree-file').forEach(el => el.classList.remove('active'));
  psComponentTree?.querySelectorAll('.tree-file').forEach(el => el.classList.remove('active'));

  // Update tab bar
  tabBar?.render();
}

// ==================== Storage Mode Management ====================

// Setup storage-related event listeners
function setupStorageEventListeners(): void {
  // Clicking the brand opens the switcher on the Files tab (replaces the
  // legacy "click brand to reveal the sidebar" behavior).
  appBrandTrigger?.addEventListener('click', () => {
    void openProjectSwitcher('files');
  });
  appBrandTrigger?.addEventListener('keydown', (e: KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void openProjectSwitcher('files');
    }
  });

  // Topbar project pill opens the switcher on the Projects tab
  // (clicking the project name implies switching projects, not browsing files).
  topbarProjectPill?.addEventListener('click', (e: MouseEvent) => {
    e.stopPropagation();
    void openProjectSwitcher('projects');
  });

  // Project switcher: New project
  btnPsNewProject?.addEventListener('click', async () => {
    const name = prompt('Enter project name:');
    if (!name) return;
    await createAndOpenProject(name);
  });

  // Project switcher: Create project from typed search query (empty-state button)
  btnPsCreateFromQuery?.addEventListener('click', async () => {
    const name = psSearchInput?.value.trim();
    if (!name) return;
    await createAndOpenProject(name);
  });

  // Project switcher: Import project
  btnPsImportProject?.addEventListener('click', () => {
    importFileInput?.click();
  });

  importFileInput?.addEventListener('change', async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    const mode = storage.currentBackendName;
    const backend = mode === 'cloud' ? cloudBackend! : browserBackend!;

    try {
      setStatus('Importing...', 'loading');
      let result = await backend.importProject(file) as any;

      // Handle existing project - ask for confirmation to overwrite
      if (result.needsConfirmation && result.existingProject) {
        const overwrite = confirm(
          `A project named "${result.projectName}" already exists.\n\nDo you want to replace it with the imported version?`
        );
        if (overwrite) {
          result = await backend.importProject(file, {
            existingProjectId: result.existingProject.id
          });
        } else {
          setStatus('Import cancelled', '');
          importFileInput.value = '';
          return;
        }
      }

      if (mode === 'cloud') {
        await cloudBackend!.loadProject(result.projectId);
        saveLastProject('cloud', result.projectId);
      } else if (result.projectId) {
        saveLastProject('browser', result.projectId);
      }
      setUrlProjectId(result.projectId ?? null);

      await updateProjectList();
      clearEditor();
      await loadFilesAndComponents();
      setStatus('Imported', '');
    } catch (err) {
      const error = err as Error;
      alert(`Error importing project: ${error.message}`);
      setStatus('Import failed', 'error');
    }

    // Clear the input so the same file can be imported again
    importFileInput.value = '';
  });

  // Project switcher: Export project
  btnPsExportProject?.addEventListener('click', async () => {
    const mode = storage.currentBackendName;
    const backend = mode === 'cloud' ? cloudBackend! : browserBackend!;

    try {
      setStatus('Exporting...', 'loading');
      await backend.downloadProject();
      setStatus('Exported', '');
    } catch (err) {
      const error = err as Error;
      alert(`Error exporting project: ${error.message}`);
      setStatus('Export failed', 'error');
    }
  });

  // Project switcher: tab click
  psTabs.forEach(btn => {
    btn.addEventListener('click', () => {
      const tab = btn.dataset.psTab as PsTab | undefined;
      if (tab && (PS_TAB_ORDER as string[]).includes(tab)) {
        setProjectSwitcherTab(tab);
      }
    });
  });

  // Project switcher: search input — filter + keyboard nav
  psSearchInput?.addEventListener('input', () => {
    renderProjectSwitcherList(psSearchInput!.value);
  });
  psSearchInput?.addEventListener('keydown', async (e: KeyboardEvent) => {
    // Space as the first character toggles between Projects and Files tabs.
    // (Leading spaces make no sense for search, so we repurpose them.)
    if (e.key === ' ' && psSearchInput!.value === '') {
      e.preventDefault();
      toggleProjectSwitcherTab();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      // While the key is held down (OS key-repeat), throttle so the list scrolls
      // at a pace the user can actually follow. The first (non-repeat) press
      // always navigates immediately.
      if (e.repeat) {
        const now = performance.now();
        if (now - psLastArrowNavAt < PS_ARROW_REPEAT_INTERVAL_MS) return;
        psLastArrowNavAt = now;
      } else {
        psLastArrowNavAt = performance.now();
      }
      moveProjectSwitcherHighlight(e.key === 'ArrowDown' ? 1 : -1);
    } else if (e.key === 'ArrowRight') {
      // Tree view: expand folder, or step into expanded folder. List view ignores.
      if (expandHighlightedTreeItem()) {
        e.preventDefault();
      }
    } else if (e.key === 'ArrowLeft') {
      // Tree view: collapse folder, or jump to parent folder. List view ignores.
      if (collapseHighlightedTreeItem()) {
        e.preventDefault();
      }
    } else if (e.key === 'Tab') {
      // Tab also toggles between tabs — convenient for right-handers
      e.preventDefault();
      toggleProjectSwitcherTab();
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const items = projectSwitcherItems();
      if (items && items.length > 0) {
        const idx = Math.max(0, Math.min(psHighlightIndex, items.length - 1));
        const el = items[idx];
        // For folder headers, click() toggles expansion and re-renders the tree —
        // restore highlight to the same folder afterwards.
        const folderPath = el.classList.contains('tree-folder-header')
          ? (el.parentElement as HTMLElement | null)?.dataset.path
          : undefined;
        el.click();
        if (folderPath) refocusTreeItemByPath(folderPath);
      } else if (psActiveTab === 'projects') {
        const name = psSearchInput!.value.trim();
        if (name) await createAndOpenProject(name);
      }
    }
  });

  // Project switcher: file item click
  psFileList?.addEventListener('click', async (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const item = target.closest('.ps-file-item') as HTMLElement | null;
    if (!item) return;
    const path = item.dataset.filePath;
    if (!path) return;
    modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);
    await loadFile(path);
  });

  // Project switcher: component item click
  psComponentList?.addEventListener('click', async (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const item = target.closest('.ps-file-item') as HTMLElement | null;
    if (!item) return;
    const name = item.dataset.componentName;
    if (!name) return;
    modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);
    await loadComponent(name);
  });

  // Project selection and inline actions inside the project switcher modal
  psProjectList?.addEventListener('click', async (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    const mode = storage.currentBackendName;

    // Handle rename button
    const renameBtn = target.closest('.btn-project-rename') as HTMLElement | null;
    if (renameBtn) {
      e.stopPropagation();
      const projectId = renameBtn.dataset.renameProjectId!;

      if (mode === 'cloud') {
        const projects = await cloudBackend!.listProjects();
        const project = projects.find(p => String(p.id) === projectId);
        if (!project) return;

        const newName = prompt('Enter new project name:', project.name);
        if (!newName || newName === project.name) return;

        try {
          await cloudBackend!.renameProject(projectId, newName);
          await updateProjectList();
        } catch (err) {
          const error = err as Error;
          alert(`Error renaming project: ${error.message}`);
        }
      } else {
        const projects = await browserBackend!.listProjects();
        const project = projects.find(p => p.id === projectId);
        if (!project) return;

        const newName = prompt('Enter new project name:', project.name);
        if (!newName || newName === project.name) return;

        try {
          await browserBackend!.renameProject(projectId, newName);
          await updateProjectList();
        } catch (err) {
          const error = err as Error;
          alert(`Error renaming project: ${error.message}`);
        }
      }
      return;
    }

    // Handle delete button
    const deleteBtn = target.closest('.btn-project-delete') as HTMLElement | null;
    if (deleteBtn) {
      e.stopPropagation();
      const projectId = deleteBtn.dataset.deleteProjectId!;

      if (mode === 'cloud') {
        const projects = await cloudBackend!.listProjects();
        const project = projects.find(p => String(p.id) === projectId);
        if (!project) return;

        const confirmed = confirm(`Are you sure you want to delete "${project.name}"?\n\nThis action cannot be undone.`);
        if (!confirmed) return;

        try {
          await cloudBackend!.deleteProject(projectId);

          const currentProject = cloudBackend!.getCurrentProject();
          if (!currentProject || String(currentProject.id) === projectId) {
            const remainingProjects = await cloudBackend!.listProjects();
            if (remainingProjects.length > 0) {
              await cloudBackend!.loadProject(remainingProjects[0].id);
              saveLastProject('cloud', remainingProjects[0].id);
            } else {
              const projectId = await cloudBackend!.createProject('My Project');
              saveLastProject('cloud', projectId);
            }
            await syncUrlProjectId('cloud');
            clearEditor();
            await loadFilesAndComponents();
          }

          await updateProjectList();
        } catch (err) {
          const error = err as Error;
          alert(`Error deleting project: ${error.message}`);
        }
      } else {
        const projects = await browserBackend!.listProjects();
        const project = projects.find(p => p.id === projectId);
        if (!project) return;

        const confirmed = confirm(`Are you sure you want to delete "${project.name}"?\n\nThis action cannot be undone.`);
        if (!confirmed) return;

        try {
          await browserBackend!.deleteProject(projectId);

          const currentProject = await browserBackend!.getCurrentProject();
          if (!currentProject || currentProject.id === projectId) {
            const remainingProjects = await browserBackend!.listProjects();
            if (remainingProjects.length > 0) {
              await browserBackend!.loadProject(remainingProjects[0].id);
              saveLastProject('browser', remainingProjects[0].id);
            } else {
              const newProjectId = await browserBackend!.createProject('My Project');
              saveLastProject('browser', newProjectId);
            }
            await syncUrlProjectId('browser');
            clearEditor();
            await loadFilesAndComponents();
          }

          await updateProjectList();
        } catch (err) {
          const error = err as Error;
          alert(`Error deleting project: ${error.message}`);
        }
      }
      return;
    }

    // Handle project selection
    const item = target.closest('.sidebar-project-item') as HTMLElement | null;
    if (!item) return;

    const projectId = item.dataset.projectId;
    if (!projectId) return;

    // Close modal immediately for responsiveness
    modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);

    showLoading('Loading project...');
    try {
      if (mode === 'cloud') {
        await cloudBackend!.loadProject(projectId);
        saveLastProject('cloud', projectId);
      } else {
        await browserBackend!.loadProject(projectId);
        saveLastProject('browser', projectId);
      }
      setUrlProjectId(projectId);
      await updateProjectList();
      tabManager.closeAll();
      clearEditor();
      await loadFilesAndComponents();
      if (!tabManager.activeTab && currentFilesData.some(f => f.path === README_PATH)) {
        await loadFile(README_PATH);
      }
    } catch (err) {
      const error = err as Error;
      alert(`Error loading project: ${error.message}`);
    } finally {
      hideLoading();
    }
  });

  // User menu: Toggle dropdown
  btnUserAvatar?.addEventListener('click', (e: MouseEvent) => {
    e.stopPropagation();
    userMenuDropdown?.classList.toggle('hidden');
  });

  // User menu: Close when a press starts outside (mousedown, not click)
  document.addEventListener('mousedown', (e: MouseEvent) => {
    const target = e.target as HTMLElement;
    if (!userMenu?.contains(target)) {
      userMenuDropdown?.classList.add('hidden');
    }
  });

  // User menu: History button
  btnMenuHistory?.addEventListener('click', () => {
    userMenuDropdown?.classList.add('hidden');
    openHistoryModal();
  });

  // User menu: Media Library button
  btnMenuMedia?.addEventListener('click', () => {
    userMenuDropdown?.classList.add('hidden');
    openMediaLibrary();
  });

  // User menu: Templates button
  btnMenuTemplates?.addEventListener('click', () => {
    userMenuDropdown?.classList.add('hidden');
    openTemplateLibrary();
  });

  // User menu: experimental windowed mode toggle
  btnMenuWindowed?.addEventListener('click', () => {
    userMenuDropdown?.classList.add('hidden');
    desktop?.toggle();
  });

  // User menu: Help button
  btnMenuHelp?.addEventListener('click', () => {
    userMenuDropdown?.classList.add('hidden');
    modalManager.show(MODAL_IDS.HELP);
  });

  // User menu: Logout button (direct logout, no confirmation)
  btnMenuLogout?.addEventListener('click', () => {
    userMenuDropdown?.classList.add('hidden');
    cloudAuth.logout();
  });

  // User menu: Login button (show auth modal)
  btnMenuLogin?.addEventListener('click', () => {
    userMenuDropdown?.classList.add('hidden');
    showAuthModal();
  });

  // Auth modal: Form submission
  authForm?.addEventListener('submit', async (e: Event) => {
    e.preventDefault();
    await handleAuthSubmit();
  });

  // Auth modal: Switch between login/register
  authSwitchBtn?.addEventListener('click', (e: MouseEvent) => {
    e.preventDefault();
    toggleAuthMode();
  });

  // Auth modal: Close on backdrop press (mousedown — see CRUD modals above)
  authModal?.addEventListener('mousedown', (e: MouseEvent) => {
    if (e.target === authModal) {
      closeAuthModal();
    }
  });

  // Auth modal: Skip to browser storage
  authSkipBtn?.addEventListener('click', () => {
    closeAuthModal();
  });

}

// Switch storage mode
async function switchStorageMode(mode: string): Promise<void> {
  try {
    const urlProjectId = getUrlProjectId();

    // For cloud mode, show auth modal if not authenticated
    if (mode === 'cloud') {
      if (!cloudAuth.isAuthenticated()) {
        // Show auth modal
        showAuthModal();
        // Keep showing the selector as 'cloud' but don't switch backend
        return;
      }

      // Make sure we have a project loaded
      const projects = await cloudBackend!.listProjects();
      if (projects.length === 0) {
        // Create a default project
        const createdProjectId = await cloudBackend!.createProject('My Project');
        saveLastProject('cloud', createdProjectId);
      } else {
        // URL project is tab-specific and takes precedence over shared localStorage project.
        const currentProjectId = cloudBackend!.getCurrentProject()?.id ?? null;
        const targetFromUrl = urlProjectId
          ? projects.find(p => String(p.id) === urlProjectId)
          : null;
        const lastProjectId = getLastProject('cloud');
        const targetFromStorage = lastProjectId
          ? projects.find(p => String(p.id) === lastProjectId)
          : null;
        const projectToLoad = targetFromUrl?.id ?? targetFromStorage?.id ?? projects[0].id;

        if (!currentProjectId || String(currentProjectId) !== String(projectToLoad)) {
          await cloudBackend!.loadProject(projectToLoad);
        }
        saveLastProject('cloud', projectToLoad);
      }
    }

    if (mode === 'browser') {
      // Make sure we have a project
      const projects = await browserBackend!.listProjects();
      if (projects.length === 0) {
        // Create a default project
        const createdProjectId = await browserBackend!.createProject('My Project');
        saveLastProject('browser', createdProjectId);
      } else {
        const currentProjectId = (await browserBackend!.getCurrentProject())?.id ?? null;
        const targetFromUrl = urlProjectId
          ? projects.find(p => String(p.id) === urlProjectId)
          : null;
        const lastProjectId = getLastProject('browser');
        const targetFromStorage = lastProjectId
          ? projects.find(p => p.id === lastProjectId)
          : null;
        const projectToLoad = targetFromUrl?.id ?? targetFromStorage?.id ?? projects[0].id;

        if (!currentProjectId || String(currentProjectId) !== String(projectToLoad)) {
          await browserBackend!.loadProject(projectToLoad);
        }
        saveLastProject('browser', projectToLoad);
      }
    }

    await storage.switchBackend(mode as 'cloud' | 'browser');
    await syncUrlProjectId(mode as 'cloud' | 'browser');
    updateStorageModeUI();
    tabManager.closeAll();
    clearEditor();
    await loadFilesAndComponents();

    // Default to the README home page if nothing else is active.
    if (!tabManager.activeTab && currentFilesData.some(f => f.path === README_PATH)) {
      await loadFile(README_PATH);
    }

  } catch (err) {
    const error = err as Error;
    alert(`Error switching storage: ${error.message}`);
    updateStorageModeUI();
  }
}

// Update UI based on current storage mode
function updateStorageModeUI(): void {
  const mode = storage.currentBackendName;

  // Update storage mode indicator in sidebar
  if (topbarStorageMode) {
    topbarStorageMode.textContent = mode === 'cloud' ? 'Cloud' : 'Local';
    topbarStorageMode.className = `topbar-storage-mode ${mode === 'cloud' ? 'cloud' : 'browser'}`;
  }

  // Update cloud UI (handles auth state, avatar, email, logout visibility)
  updateCloudUI();

  // Always update project list to match current storage backend
  updateProjectList();
}

// ==================== Project / File Switcher ====================

type PsTab = 'projects' | 'files' | 'components';
// Display + cycle order. Files first (primary use case), then components, then projects.
const PS_TAB_ORDER: PsTab[] = ['files', 'components', 'projects'];

// Cached lists for filtering in the switcher modal
let psProjects: { id: string | number; name: string; isActive: boolean }[] = [];
let psHighlightIndex = 0;
let psActiveTab: PsTab = 'files';
// Timestamp of the last arrow-key navigation, used to throttle OS key-repeat so
// holding an arrow scrolls the switcher at a readable, controlled pace.
let psLastArrowNavAt = 0;
// Minimum ms between navigation steps while an arrow key is held down.
const PS_ARROW_REPEAT_INTERVAL_MS = 110;

// Update project list (unified for both storage modes) — updates sidebar label
// and the cached project list used by the project switcher modal.
async function updateProjectList(): Promise<void> {
  const mode = storage.currentBackendName;

  if (mode === 'cloud') {
    await updateCloudProjectList();
  } else {
    await updateBrowserProjectList();
  }

  // If the switcher modal is currently visible, refresh its list too
  const modal = document.getElementById(MODAL_IDS.PROJECT_SWITCHER);
  if (modal && !modal.classList.contains('hidden')) {
    renderProjectSwitcherList(psSearchInput?.value ?? '');
  }
}

async function updateBrowserProjectList(): Promise<void> {
  if (!browserBackend) return;

  const projects = await browserBackend.listProjects();
  const currentProjectId = (await browserBackend.getCurrentProject())?.id ?? null;
  // Normalize to strings so numeric/string id drift (see updateCloudProjectList)
  // doesn't break the active-project highlight.
  const currentKey = currentProjectId == null ? null : String(currentProjectId);
  const currentProject = projects.find(p => String(p.id) === currentKey);

  if (topbarProjectName) {
    topbarProjectName.textContent = currentProject ? currentProject.name : 'Select project…';
  }
  tabBar?.render();

  psProjects = projects.map(p => ({
    id: p.id,
    name: p.name,
    isActive: String(p.id) === currentKey,
  }));
}

// Update cloud UI
async function updateCloudUI(): Promise<void> {
  const isAuthenticated = cloudAuth.isAuthenticated();

  // Show/hide login/logout buttons based on auth state
  if (btnMenuLogout) {
    btnMenuLogout.classList.toggle('hidden', !isAuthenticated);
  }
  if (btnMenuLogin) {
    btnMenuLogin.classList.toggle('hidden', isAuthenticated);
  }

  if (!isAuthenticated) {
    // Show grey "?" avatar when not logged in
    if (userAvatarInitials) {
      userAvatarInitials.textContent = '?';
    }
    if (btnUserAvatar) {
      btnUserAvatar.classList.add('not-logged-in');
    }
    if (userMenuEmail) {
      userMenuEmail.textContent = 'Not logged in';
    }
    return;
  }

  // Show user email and initials in avatar
  const user = cloudAuth.getUser();
  if (user) {
    if (userMenuEmail) {
      userMenuEmail.textContent = user.email;
    }
    if (userAvatarInitials) {
      // Show first letter of email as initials
      userAvatarInitials.textContent = user.email.charAt(0).toUpperCase();
    }
    if (btnUserAvatar) {
      btnUserAvatar.classList.remove('not-logged-in');
    }
  }

  // Update project list only if we're in cloud mode
  // (otherwise updateProjectList will show browser projects)
  if (storage.currentBackendName === 'cloud') {
    await updateCloudProjectList();
  }
}

async function updateCloudProjectList(): Promise<void> {
  if (!cloudBackend || !cloudAuth.isAuthenticated()) return;

  try {
    const projects = await cloudBackend.listProjects();
    const currentProject = cloudBackend.getCurrentProject();

    if (topbarProjectName) {
      topbarProjectName.textContent = currentProject ? currentProject.name : 'Select project…';
    }
    tabBar?.render();

    // Cloud project IDs drift between number (server response / created projects)
    // and string (from dataset attributes / URL params). Compare as strings so
    // the active-project highlight survives every switch path.
    const currentKey = currentProject?.id == null ? null : String(currentProject.id);

    psProjects = projects.map(p => ({
      id: p.id,
      name: p.name,
      isActive: String(p.id) === currentKey,
    }));
  } catch (err) {
    console.error('Error loading cloud projects:', err);
    psProjects = [];
    if (psProjectList) {
      psProjectList.innerHTML = '<div class="sidebar-project-item" style="color: var(--text-muted)">Error loading projects</div>';
    }
  }
}

/**
 * Open the spotlight-style switcher modal, refresh list, focus search.
 * No-op while the auth modal is showing — we don't want a second modal
 * stacked behind the login flow.
 */
async function openProjectSwitcher(tab: PsTab = 'files'): Promise<void> {
  if (modalManager.isOpen(MODAL_IDS.AUTH)) return;
  await updateProjectList();
  if (psSearchInput) psSearchInput.value = '';
  psActiveTab = tab;
  updateProjectSwitcherTabUI();
  renderProjectSwitcherList('');
  modalManager.show(MODAL_IDS.PROJECT_SWITCHER);
  // Focus on next tick so the modal is visible first
  requestAnimationFrame(() => psSearchInput?.focus());
}

function setProjectSwitcherTab(tab: PsTab): void {
  if (psActiveTab === tab) return;
  psActiveTab = tab;
  if (psSearchInput) psSearchInput.value = '';
  updateProjectSwitcherTabUI();
  renderProjectSwitcherList('');
  psSearchInput?.focus();
}

function toggleProjectSwitcherTab(direction: 1 | -1 = 1): void {
  const idx = PS_TAB_ORDER.indexOf(psActiveTab);
  const next = PS_TAB_ORDER[(idx + direction + PS_TAB_ORDER.length) % PS_TAB_ORDER.length];
  setProjectSwitcherTab(next);
}

function updateProjectSwitcherTabUI(): void {
  psTabs.forEach(btn => btn.classList.toggle('active', btn.dataset.psTab === psActiveTab));

  const isFiles = psActiveTab === 'files';
  const isComponents = psActiveTab === 'components';
  const isProjects = psActiveTab === 'projects';

  // Per-tab view mode (only applies to Files / Components)
  const filesView = getViewMode('files');
  const componentsView = getViewMode('components');

  psProjectList?.classList.toggle('hidden', !isProjects);
  psFileTree?.classList.toggle('hidden', !(isFiles && filesView === 'tree'));
  psFileList?.classList.toggle('hidden', !(isFiles && filesView === 'list'));
  psComponentTree?.classList.toggle('hidden', !(isComponents && componentsView === 'tree'));
  psComponentList?.classList.toggle('hidden', !(isComponents && componentsView === 'list'));

  psFooterProjects?.classList.toggle('hidden', !isProjects);
  psFooterFiles?.classList.toggle('hidden', !isFiles);
  psFooterComponents?.classList.toggle('hidden', !isComponents);

  // View-mode toggle is only meaningful for the file/component browsers
  psViewToggle?.classList.toggle('hidden', isProjects);
  const activeView = isFiles ? filesView : isComponents ? componentsView : 'tree';
  psViewButtons.forEach(btn => btn.classList.toggle('active', btn.dataset.view === activeView));

  if (psSearchInput) {
    psSearchInput.placeholder =
      isProjects     ? 'Search projects or type a new name…' :
      isFiles        ? (filesView === 'tree' ? 'Filter file tree…' : 'Search files by name or path…') :
      /* components */ (componentsView === 'tree' ? 'Filter component tree…' : 'Search components by name or path…');
  }
}

/**
 * Render the filtered list for the currently active tab.
 */
function renderProjectSwitcherList(query: string): void {
  if (psActiveTab === 'projects') renderProjectSwitcherProjects(query);
  else if (psActiveTab === 'files') renderProjectSwitcherFiles(query);
  else renderProjectSwitcherComponents(query);
}

function hideAllPsEmptyStates(): void {
  psEmpty?.classList.add('hidden');
  psEmptyFiles?.classList.add('hidden');
  psEmptyComponents?.classList.add('hidden');
}

function renderProjectSwitcherProjects(query: string): void {
  if (!psProjectList) return;

  const q = query.trim().toLowerCase();
  const filtered = q
    ? psProjects.filter(p => p.name.toLowerCase().includes(q))
    : psProjects;

  renderProjectList(psProjectList, filtered, { emptyMessage: 'No projects yet — create one below' });

  hideAllPsEmptyStates();

  if (filtered.length === 0 && q) {
    psEmpty?.classList.remove('hidden');
    if (psEmptyQuery) psEmptyQuery.textContent = `"${query.trim()}"`;
    psHighlightIndex = 0;
  } else {
    const activeIdx = filtered.findIndex(p => p.isActive);
    psHighlightIndex = activeIdx >= 0 ? activeIdx : 0;
  }
  applyProjectSwitcherHighlight();
}

/**
 * Render a folder breadcrumb like `folder1 › folder2`, each crumb highlighted
 * if it contains the current query. Empty/root dirs render as "(root)".
 */
function renderBreadcrumb(dir: string, query: string): string {
  if (!dir) return '<em>(root)</em>';
  const parts = dir.split('/').filter(Boolean);
  if (parts.length === 0) return '<em>(root)</em>';
  return parts
    .map(p => `<span class="ps-crumb">${highlightMatch(p, query)}</span>`)
    .join('<span class="ps-crumb-sep">›</span>');
}

/**
 * Highlight occurrences of `query` (case-insensitive, contains match) inside `text`.
 * Reuses the pre-existing escapeHtml helper defined earlier in this file.
 */
function highlightMatch(text: string, query: string): string {
  if (!query) return escapeHtml(text);
  const lowerText = text.toLowerCase();
  const lowerQ = query.toLowerCase();
  const idx = lowerText.indexOf(lowerQ);
  if (idx < 0) return escapeHtml(text);
  return (
    escapeHtml(text.slice(0, idx)) +
    '<mark>' + escapeHtml(text.slice(idx, idx + query.length)) + '</mark>' +
    escapeHtml(text.slice(idx + query.length))
  );
}

type PsListItem = { name: string; path: string; dir: string };

/**
 * Shared ranking: exact-name > name-startsWith > name-contains > path-contains.
 */
function rankPsItems(items: PsListItem[], q: string): PsListItem[] {
  if (!q) return [...items].sort((a, b) => a.path.localeCompare(b.path));
  return [...items].sort((a, b) => {
    const score = (f: PsListItem) => {
      const n = f.name.toLowerCase();
      if (n === q) return 0;
      if (n.startsWith(q)) return 1;
      if (n.includes(q)) return 2;
      return 3;
    };
    const sa = score(a), sb = score(b);
    if (sa !== sb) return sa - sb;
    return a.path.localeCompare(b.path);
  });
}

const PS_FILE_ICON = '<svg class="ps-file-icon" xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 -960 960 960" width="16" fill="currentColor"><path d="M320-240h320v-80H320v80Zm0-160h320v-80H320v80ZM240-80q-33 0-56.5-23.5T160-160v-640q0-33 23.5-56.5T240-880h320l240 240v480q0 33-23.5 56.5T720-80H240Z"/></svg>';
const PS_COMPONENT_ICON = '<svg class="ps-file-icon" xmlns="http://www.w3.org/2000/svg" height="16" viewBox="0 -960 960 960" width="16" fill="currentColor"><path d="M352-120H200q-33 0-56.5-23.5T120-200v-152q48 0 84-30.5t36-77.5q0-47-36-77.5T120-568v-152q0-33 23.5-56.5T200-800h160q0-42 29-71t71-29q42 0 71 29t29 71h160q33 0 56.5 23.5T800-720v160q42 0 71 29t29 71q0 42-29 71t-71 29v160q0 33-23.5 56.5T720-120H568q0-50-31.5-85T460-240q-45 0-76.5 35T352-120Z"/></svg>';

function renderPsFileList(
  container: HTMLElement,
  items: PsListItem[],
  query: string,
  dataAttr: 'data-file-path' | 'data-component-name',
  icon: string
): void {
  container.innerHTML = items.map(f => {
    const nameHtml = highlightMatch(f.name, query);
    const breadcrumb = renderBreadcrumb(f.dir, query);
    const key = dataAttr === 'data-file-path' ? f.path : f.path;
    return `
      <div class="ps-file-item" ${dataAttr}="${escapeHtml(key)}">
        ${icon}
        <div class="ps-file-meta">
          <div class="ps-file-name">${nameHtml}</div>
          <div class="ps-file-path">${breadcrumb}</div>
        </div>
      </div>
    `;
  }).join('');
}

function renderProjectSwitcherFiles(query: string): void {
  hideAllPsEmptyStates();
  const mode = getViewMode('files');

  if (mode === 'tree') {
    // Tree view: re-render so any new items appear, then apply the search
    // filter (TreeRenderer.filter() handles auto-expanding matched folders).
    fileTreeRenderer?.render(currentFilesData);
    fileTreeRenderer?.filter(query);
    resetHighlightToActiveOrTop();
    return;
  }

  if (!psFileList) return;
  const q = query.trim().toLowerCase();

  // Derive the full relative directory from `path` (e.g. "folder1/folder2/file.src.md"
  // → dir: "folder1/folder2"). FileData.dir only holds the immediate parent — we
  // can't reuse it because `renderBreadcrumb` needs every ancestor segment.
  const items: PsListItem[] = currentFilesData.map(f => {
    const parts = f.path.split('/');
    const leaf = parts.pop() ?? f.name;
    const dir = parts.join('/');
    return { name: leaf, path: f.path, dir };
  });

  const matches = q
    ? items.filter(f => f.name.toLowerCase().includes(q) || f.path.toLowerCase().includes(q))
    : items;

  if (matches.length === 0) {
    psFileList.innerHTML = '';
    psEmptyFiles?.classList.remove('hidden');
    if (psEmptyFilesQuery) {
      psEmptyFilesQuery.textContent = q ? `"${query.trim()}"` : '(no files in this project)';
    }
    psHighlightIndex = 0;
    return;
  }

  // Empty query → recent first, then alpha. Non-empty query → relevance rank.
  const sorted = q ? rankPsItems(matches, q) : sortByRecent(matches);
  renderPsFileList(psFileList, sorted, query.trim(), 'data-file-path', PS_FILE_ICON);

  resetHighlightToActiveOrTop();
}

function renderProjectSwitcherComponents(query: string): void {
  hideAllPsEmptyStates();
  const mode = getViewMode('components');

  if (mode === 'tree') {
    componentTreeRenderer?.render(currentComponentsData.map(c => ({ path: c.name })));
    componentTreeRenderer?.filter(query);
    resetHighlightToActiveOrTop();
    return;
  }

  if (!psComponentList) return;
  const q = query.trim().toLowerCase();

  // Components use `name` as both name and path (full path relative to _components/).
  // Normalize into PsListItem with leaf name + parent dir so we can show breadcrumbs.
  // Recent-files tracking stores components with the `_components/` prefix
  // (matching `loadComponent` / `currentFile`), so we synthesize a matching key.
  const items: PsListItem[] = currentComponentsData.map(c => {
    const parts = c.name.split('/');
    const leaf = parts.pop() ?? c.name;
    const dir = parts.join('/');
    return { name: leaf, path: `_components/${c.name}`, dir };
  });

  const matches = q
    ? items.filter(f => f.name.toLowerCase().includes(q) || f.path.toLowerCase().includes(q))
    : items;

  if (matches.length === 0) {
    psComponentList.innerHTML = '';
    psEmptyComponents?.classList.remove('hidden');
    if (psEmptyComponentsQuery) {
      psEmptyComponentsQuery.textContent = q ? `"${query.trim()}"` : '(no components in this project)';
    }
    psHighlightIndex = 0;
    return;
  }

  const sorted = q ? rankPsItems(matches, q) : sortByRecent(matches);
  // The flat-list click handler reads data-component-name and treats it as
  // the path within _components/, so strip our prefix back off for the attr.
  const forDataAttr: PsListItem[] = sorted.map(item => ({
    ...item,
    path: item.path.replace(/^_components\//, ''),
  }));
  renderPsFileList(psComponentList, forDataAttr, query.trim(), 'data-component-name', PS_COMPONENT_ICON);

  resetHighlightToActiveOrTop();
}

/**
 * Find the navigable item that corresponds to the currently-loaded file/component,
 * so arrow-key navigation can start from "where the user already is" rather than
 * jumping to the top. Returns -1 if no item matches.
 */
function findActiveSwitcherItemIndex(items: HTMLElement[]): number {
  for (let i = 0; i < items.length; i++) {
    const el = items[i];
    // Tree files, tree-folder-headers' parent .tree-folder, and project rows all
    // carry an .active class when they represent the current selection. Folder
    // headers never get .active themselves, but we still want their containing
    // .tree-folder to count? — for now match leaves only; tree-folder.active is
    // not set by the renderer.
    if (el.classList.contains('active')) return i;
    if (el.dataset.filePath && el.dataset.filePath === currentFile) return i;
    if (el.dataset.componentName && `_components/${el.dataset.componentName}` === currentFile) return i;
  }
  return -1;
}

function resetHighlightToActiveOrTop(): void {
  const items = projectSwitcherItems();
  if (!items || items.length === 0) {
    psHighlightIndex = 0;
    return;
  }
  const idx = findActiveSwitcherItemIndex(items);
  psHighlightIndex = idx >= 0 ? idx : 0;
  applyProjectSwitcherHighlight();
}

function getTreeNavItems(container: HTMLElement): HTMLElement[] {
  // Tree-view keyboard nav walks both folder headers (so users can expand/collapse
  // and step into folders) and file leaves, in DOM/visual order. Items inside a
  // collapsed folder or hidden by search are skipped.
  const all = container.querySelectorAll<HTMLElement>('.tree-folder-header, .tree-file');
  const result: HTMLElement[] = [];
  for (const el of Array.from(all)) {
    if (el.classList.contains('tree-folder-header')) {
      if (el.parentElement?.classList.contains('search-hidden')) continue;
    } else if (el.classList.contains('search-hidden')) {
      continue;
    }
    if (el.offsetParent === null) continue;
    result.push(el);
  }
  return result;
}

function treeItemPath(el: HTMLElement): string | undefined {
  if (el.classList.contains('tree-folder-header')) {
    return (el.parentElement as HTMLElement | null)?.dataset.path;
  }
  return el.dataset.path;
}

function projectSwitcherItems(): HTMLElement[] | null {
  if (psActiveTab === 'projects') {
    const list = psProjectList?.querySelectorAll<HTMLElement>('.sidebar-project-item[data-project-id]');
    return list ? Array.from(list) : null;
  }
  if (psActiveTab === 'files') {
    if (getViewMode('files') === 'tree') {
      return psFileTree ? getTreeNavItems(psFileTree) : null;
    }
    const list = psFileList?.querySelectorAll<HTMLElement>('.ps-file-item[data-file-path]');
    return list ? Array.from(list) : null;
  }
  // components
  if (getViewMode('components') === 'tree') {
    return psComponentTree ? getTreeNavItems(psComponentTree) : null;
  }
  const list = psComponentList?.querySelectorAll<HTMLElement>('.ps-file-item[data-component-name]');
  return list ? Array.from(list) : null;
}

function applyProjectSwitcherHighlight(): void {
  const items = projectSwitcherItems();
  if (!items) return;
  items.forEach((el, i) => el.classList.toggle('ps-highlight', i === psHighlightIndex));
  items[psHighlightIndex]?.scrollIntoView({ block: 'nearest' });
}

function moveProjectSwitcherHighlight(delta: number): void {
  const items = projectSwitcherItems();
  if (!items || items.length === 0) return;
  psHighlightIndex = (psHighlightIndex + delta + items.length) % items.length;
  applyProjectSwitcherHighlight();
}

function refocusTreeItemByPath(path: string): void {
  const items = projectSwitcherItems();
  if (!items) return;
  for (let i = 0; i < items.length; i++) {
    if (treeItemPath(items[i]) === path) {
      psHighlightIndex = i;
      applyProjectSwitcherHighlight();
      return;
    }
  }
}

function expandHighlightedTreeItem(): boolean {
  const items = projectSwitcherItems();
  if (!items || items.length === 0) return false;
  const el = items[psHighlightIndex];
  if (!el?.classList.contains('tree-folder-header')) return false;
  const folder = el.parentElement as HTMLElement | null;
  if (!folder) return false;
  const path = folder.dataset.path;
  if (folder.classList.contains('collapsed')) {
    el.click(); // toggleFolder -> re-render
    if (path) refocusTreeItemByPath(path);
  }
  // Already expanded: stay put (no step-into).
  return true;
}

function collapseHighlightedTreeItem(): boolean {
  const items = projectSwitcherItems();
  if (!items || items.length === 0) return false;
  const el = items[psHighlightIndex];
  if (!el) return false;
  if (el.classList.contains('tree-folder-header')) {
    const folder = el.parentElement as HTMLElement | null;
    if (folder && !folder.classList.contains('collapsed')) {
      const path = folder.dataset.path;
      el.click(); // toggleFolder -> re-render
      if (path) refocusTreeItemByPath(path);
      return true;
    }
  }
  // File or already-collapsed folder: jump to parent folder header
  const startFolder = el.classList.contains('tree-folder-header')
    ? (el.parentElement?.parentElement?.closest('.tree-folder') as HTMLElement | null)
    : (el.parentElement?.closest('.tree-folder') as HTMLElement | null);
  const parentPath = startFolder?.dataset.path;
  if (parentPath) {
    refocusTreeItemByPath(parentPath);
    return true;
  }
  return false;
}

/**
 * Create a new project (respecting the current storage mode) and open it.
 * Shared by the "New" button, the empty-state button, and Enter-on-no-matches.
 */
async function createAndOpenProject(name: string): Promise<void> {
  const trimmed = name.trim();
  if (!trimmed) return;

  const mode = storage.currentBackendName;
  try {
    let projectId: string | number;
    if (mode === 'cloud') {
      projectId = await cloudBackend!.createProject(trimmed);
      saveLastProject('cloud', projectId);
    } else {
      projectId = await browserBackend!.createProject(trimmed);
      saveLastProject('browser', projectId);
    }
    setUrlProjectId(projectId);
    await updateProjectList();
    clearEditor();
    await loadFilesAndComponents();
    modalManager.hide(MODAL_IDS.PROJECT_SWITCHER);
  } catch (err) {
    const error = err as Error;
    alert(`Error creating project: ${error.message}`);
  }
}

// ==================== Auth Modal ====================

function showAuthModal(): void {
  authMode = 'login';
  updateAuthModalUI();
  authEmail!.value = '';
  authPassword!.value = '';
  authError!.textContent = '';
  authError!.classList.add('hidden');
  modalManager.show(MODAL_IDS.AUTH);
  authEmail?.focus();
}

function applyAuthConfig(): void {
  // When registration is disabled, hide the switch button and its divider,
  // and force the modal into login mode so there's no way to toggle to register.
  const divider = authSwitchBtn?.previousElementSibling as HTMLElement | null;
  if (authSwitchBtn) authSwitchBtn.classList.toggle('hidden', !allowRegistration);
  if (divider?.classList.contains('auth-divider')) {
    divider.classList.toggle('hidden', !allowRegistration);
  }
  if (!allowRegistration && authMode === 'register') {
    authMode = 'login';
    updateAuthModalUI();
  }
}

function closeAuthModal(): void {
  modalManager.hide(MODAL_IDS.AUTH);
  // If user closes modal without logging in, switch to browser mode
  if (!cloudAuth.isAuthenticated() && storage.currentBackendName !== 'browser') {
    switchStorageMode('browser');
  }
}

function toggleAuthMode(): void {
  if (!allowRegistration) return;
  authMode = authMode === 'login' ? 'register' : 'login';
  updateAuthModalUI();
  authError!.textContent = '';
  authError!.classList.add('hidden');
}

function updateAuthModalUI(): void {
  if (authMode === 'login') {
    if (authModalTitle) authModalTitle.textContent = 'Welcome to Leedoc';
    if (authModalSubtitle) authModalSubtitle.textContent = 'Sign in to sync your projects across devices';
    if (authBtnText) authBtnText.textContent = 'Sign In';
    if (authSwitchText) authSwitchText.textContent = 'Create an account';
  } else {
    if (authModalTitle) authModalTitle.textContent = 'Create Account';
    if (authModalSubtitle) authModalSubtitle.textContent = 'Sign up to save your projects in the cloud';
    if (authBtnText) authBtnText.textContent = 'Create Account';
    if (authSwitchText) authSwitchText.textContent = 'Sign in instead';
  }
}

async function handleAuthSubmit(): Promise<void> {
  const email = authEmail!.value.trim();
  const password = authPassword!.value;

  if (!email || !password) {
    showAuthError('Please enter email and password');
    return;
  }

  authSubmitBtn!.disabled = true;
  if (authBtnText) authBtnText.textContent = authMode === 'login' ? 'Signing in...' : 'Creating account...';
  authError!.classList.add('hidden');

  try {
    if (authMode === 'login') {
      await cloudAuth.login(email, password);
    } else {
      await cloudAuth.register(email, password);
    }
    // Success - the onAuthChange listener will handle the rest
  } catch (err) {
    const error = err as Error;
    showAuthError(error.message);
  } finally {
    authSubmitBtn!.disabled = false;
    updateAuthModalUI();
  }
}

function showAuthError(message: string): void {
  authError!.textContent = message;
  authError!.classList.remove('hidden');
}

// Start
init();
initResizeHandles();
